import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Building2,
  Bookmark,
  Download,
  Star,
  Search,
  SlidersHorizontal,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Info,
  ArrowUpDown,
  Flag,
  Check,
  Lock,
  Users,
  X,
  GripVertical,
  Pin,
} from 'lucide-react';
import { subDays, startOfMonth, startOfYear, format as formatDate } from 'date-fns';
import ReportService from '../../../services/reports/Report.service';
import TipContext from '../../../providers/GlobalTip';
import AuthProvider from '../../../providers/AuthProvider';
import EpochMonthService from '../../../services/EpochMonth.service';
import { SAVED_VIEW_NAME_MAX_LENGTH, SavedViewRecord, SavedViewVisibility } from '../../../services/reports/SavedViews.service';
import { LocalSavedView, useSavedViews } from '../../../hooks/useSavedViews';
import PerformanceDashboardService from '../../../services/dashboards/PerformanceDashboard.service';
import { DashboardFilterOption } from '../../../services/dashboards/dashboardFilters';
import { COLUMN_RESIZE_HANDLE_CLASS, useResizableColumns } from '../../../hooks/useResizableColumns';
import { pinColumn, unpinColumn, usePinnedColumns } from '../../../hooks/columnPinning';
import { STICKY_LAYER, useStickyTableRows } from '../../../hooks/useStickyTableRows';
import { usePendingFilterSelection } from '../../../hooks/usePendingFilterSelection';
import { ColumnHeaderMenu, ColumnSortDirection } from '../ColumnHeaderMenu.component';
import { OtbSummaryPills } from '../OtbSummaryPills.component';
import { downloadCsv } from '../downloadCsv';

export interface VendorPerformanceReportProps {
  clientId?: string | number;
  epochMonth?: number;
  onClose?: () => void;
}

export type VendorView =
  | 'total_by_vendor'
  | 'category_by_vendor'
  | 'vendor_by_category'
  | 'vendor_by_class'
  | 'class_by_vendor'
  | 'location_class_vendor';

export interface LocationClassVendorGroup {
  id: string;
  location_code: string;
  location_name: string;
  otb_dollars?: number | null;
  plan_sales?: number | null;
  plan_inflow?: number | null;
  // Plan on-order (from plan data, like OTB) -- the On Order pill.
  plan_on_order?: number | null;
  classes: ClassGroupData[];
}

export type MetricColumnKey =
  | 'sales_dollars' | 'sales_pct_of_total' | 'sales_units'
  | 'inventory_oh_dollars' | 'inventory_pct_of_total' | 'inventory_oh_units'
  | 'inflow_dollars' | 'inflow_pct_of_total' | 'cost_of_purchase_pct'
  | 'on_order_dollars' | 'on_order_pct_of_total' | 'on_order_units'
  | 'past_due_on_order_dollars' | 'past_due_on_order_units'
  | 'gross_margin_pct' | 'maintained_margin_pct' | 'markdown_dollars' | 'markdown_pct'
  | 'sell_through_pct' | 'sell_through_start_inv_pct' | 'annual_turn_rate' | 'rtv_dollars'
  | 'avg_selling_price' | 'avg_purchase_price' | 'leaf_class_names';

export interface ColumnToggleConfig {
  key: MetricColumnKey;
  label: string;
  headerLabel?: string;
  description: string;
  defaultVisible: boolean;
  sortKey?: SortableMetricKey;
  // A text column (not a metric): its CSV cells get csvCell's formula guard, like the dimension columns.
  isText?: boolean;
}

// Single source for every metric column; list order is the default column order.
// How the On-Order Span treats a PO without a delivery date (VendorPerformanceService::ON_ORDER_DUE_DATE_SQL).
export const ON_ORDER_NO_DATE_NOTE = 'Open POs with no delivery date are counted as due now.';

// How one span drives both halves of the report (OtbPlanService::SPAN_MONTHS on the server).
export const ON_ORDER_OTB_SPAN_NOTE =
  "OTB reads whole plan months, from this month through the span's last month. On-order counts POs due within the span from today.";

// Past-due on-order is a subset of On-Order, never taken out of it.
export const PAST_DUE_ON_ORDER_DESCRIPTION = 'Open POs whose delivery start is before today; included in On-Order; no cutoff.';

// On-order dollars follow the Retail / Cost price basis (toPriceBasisRow), so their labels name it.
const priceBasisLabel = (key: MetricColumnKey, priceBasis: 'retail' | 'cost'): string | null => {
  const basis = priceBasis === 'cost' ? 'Cost' : 'Retail';
  if (key === 'on_order_dollars') return `On-Order (${basis})`;
  if (key === 'past_due_on_order_dollars') return `Past-Due On-Order (${basis})`;
  return null;
};

// Per-unit price columns (SPS-5667 #21), shared with Top Sellers. Each is summed dollars / summed
// units at every level -- row, group subtotal and report total -- never an average of averages.
export const AVG_SELLING_PRICE_LABEL = 'Avg Selling Price';
export const AVG_SELLING_PRICE_DESCRIPTION = 'Net sales $ / units sold. Blank when nothing sold';
export const AVG_PURCHASE_PRICE_LABEL = 'Avg Purchase Price';
export const AVG_PURCHASE_PRICE_DESCRIPTION = 'Purchases at cost $ / units received. Blank when nothing was received';

// Sub-class column (SPS-5667 #6), shared with Top Sellers. Class views report the plan (parent)
// class, the one OTB is planned at; this column keeps the store-level class(es) under it.
export const SUB_CLASS_LABEL = 'Sub-class';
export const SUB_CLASS_DESCRIPTION = 'Store-level class under the Class, which is the plan class OTB is planned at';
// How the server joins a row's sub-classes (VendorPerformanceService::PLAN_CLASS_SELECT_COLS).
export const LEAF_CLASS_SEPARATOR = ', ';
export const splitLeafClassNames = (names: string | null | undefined): string[] =>
  String(names ?? '').split(LEAF_CLASS_SEPARATOR).map((name) => name.trim()).filter((name) => name !== '');
export const joinLeafClassNames = (names: string[]): string | null =>
  names.length > 0 ? [...names].sort((a, b) => a.localeCompare(b)).join(LEAF_CLASS_SEPARATOR) : null;

// Dollars per unit, or null when there are no units -- so a cell shows "-", never NaN or Infinity.
export const avgPerUnit = (dollars: number | null | undefined, units: number | null | undefined): number | null => {
  const u = Number(units);
  if (units == null || !Number.isFinite(u) || u <= 0) return null;
  const d = Number(dollars);
  return Number.isFinite(d) ? d / u : null;
};

// Annual Turn as GetVendorPerformanceTurnRateData.sql computes it: inventory has only today's
// snapshot, so the denominator is current on-hand inventory, not a month-end average.
export const ANNUAL_TURN_DESCRIPTION =
  'Trailing 12-month net sales / current on-hand inventory (retail). Fixed annual figure, not affected by the date range';
export const INFLOW_DESCRIPTION = 'Dollar value of vendor receipts; excludes transfers and RTV';

export const COLUMN_TOGGLE_LIST: ColumnToggleConfig[] = [
  { key: 'sales_dollars', label: 'Sales $', description: 'Net sales for the period', defaultVisible: true },
  { key: 'sales_pct_of_total', label: '% of Total Sales', headerLabel: '% of Sales', description: 'Share of sales: of the parent group when grouped, otherwise of the report total', defaultVisible: true, sortKey: 'sales_dollars' },
  { key: 'sales_units', label: 'Sales Units', description: 'Units sold in the period', defaultVisible: false },
  { key: 'avg_selling_price', label: AVG_SELLING_PRICE_LABEL, description: AVG_SELLING_PRICE_DESCRIPTION, defaultVisible: false },
  { key: 'inventory_oh_dollars', label: 'Inventory OH $', description: 'Current on-hand inventory value', defaultVisible: true },
  { key: 'inventory_pct_of_total', label: '% of Total Inv', headerLabel: '% of Inv', description: 'Share of inventory value: of the parent group when grouped, otherwise of the report total', defaultVisible: true, sortKey: 'inventory_oh_dollars' },
  { key: 'inventory_oh_units', label: 'Inventory OH Units', description: 'Current on-hand inventory in units', defaultVisible: false },
  { key: 'inflow_dollars', label: 'Inflow $', description: INFLOW_DESCRIPTION, defaultVisible: true },
  { key: 'inflow_pct_of_total', label: '% of Total Inflow', headerLabel: '% of Inflow', description: 'Share of inflow: of the parent group when grouped, otherwise of the report total', defaultVisible: true, sortKey: 'inflow_dollars' },
  { key: 'cost_of_purchase_pct', label: 'Cost of Purchase %', description: 'Purchases at cost / net sales. Purchases at cost = units received in the period x the actual unit cost of each SKU', defaultVisible: false },
  { key: 'avg_purchase_price', label: AVG_PURCHASE_PRICE_LABEL, description: AVG_PURCHASE_PRICE_DESCRIPTION, defaultVisible: false },
  { key: 'on_order_dollars', label: 'On-Order $', description: `Open purchase order dollars within the selected on-order span. ${ON_ORDER_NO_DATE_NOTE}`, defaultVisible: true },
  { key: 'on_order_pct_of_total', label: '% of Total On-Order', headerLabel: '% of On-Order', description: 'Share of on-order dollars: of the parent group when grouped, otherwise of the report total', defaultVisible: true, sortKey: 'on_order_dollars' },
  { key: 'on_order_units', label: 'On-Order Units', description: `Open PO units within the selected on-order span. ${ON_ORDER_NO_DATE_NOTE}`, defaultVisible: false },
  { key: 'past_due_on_order_dollars', label: 'Past-Due On-Order $', headerLabel: 'Past-Due On-Order', description: PAST_DUE_ON_ORDER_DESCRIPTION, defaultVisible: false },
  { key: 'past_due_on_order_units', label: 'Past-Due On-Order Units', description: PAST_DUE_ON_ORDER_DESCRIPTION, defaultVisible: false },
  { key: 'gross_margin_pct', label: 'Initial Margin %', description: 'Margin before markdowns, on gross (ticketed) sales: (Gross sales − COGS) / Gross sales', defaultVisible: true },
  { key: 'maintained_margin_pct', label: 'Gross Margin %', description: 'Margin after markdowns, on net sales, the primary vendor health signal: (Net sales − COGS) / Net sales', defaultVisible: false },
  { key: 'markdown_dollars', label: 'Markdown $', description: 'Total discount dollars taken in the period', defaultVisible: false },
  { key: 'markdown_pct', label: 'Markdown %', description: 'Total markdowns as a percentage of gross/net sales (Markdowns / Sales)', defaultVisible: true },
  { key: 'sell_through_pct', label: 'Sell-Through %', description: 'Units sold / inflow units. Displays "-" when there is no inflow', defaultVisible: true },
  { key: 'sell_through_start_inv_pct', label: 'Sell-Through (with Starting Inv) %', headerLabel: 'Sell-Through (w/ Start Inv) %', description: 'Units sold / (units sold + on-hand units)', defaultVisible: true, sortKey: 'sell_through_start_inv_pct' },
  { key: 'annual_turn_rate', label: 'Annual Turn', description: ANNUAL_TURN_DESCRIPTION, defaultVisible: true },
  { key: 'rtv_dollars', label: 'RTV $', description: 'Return to vendor, dollar value of merchandise sent back', defaultVisible: false },
  { key: 'leaf_class_names', label: SUB_CLASS_LABEL, description: SUB_CLASS_DESCRIPTION, defaultVisible: false, isText: true },
];

export const DEFAULT_COLUMN_ORDER: MetricColumnKey[] = COLUMN_TOGGLE_LIST.map((col) => col.key);

export const METRIC_COLUMN_WIDTH = 140;
export const LABEL_COLUMN_KEY = 'label';
const DEFAULT_COLUMN_WIDTHS: Readonly<Record<string, number>> = { [LABEL_COLUMN_KEY]: 240 };
// Location > Class > Vendor stacks two pills per line under each group name, which needs a wider
// first column; a width the user drags still overrides this.
const LOCATION_CLASS_VENDOR_COLUMN_WIDTHS: Readonly<Record<string, number>> = { [LABEL_COLUMN_KEY]: 340 };
export const getDefaultColumnWidths = (view: VendorView): Readonly<Record<string, number>> =>
  view === 'location_class_vendor' ? LOCATION_CLASS_VENDOR_COLUMN_WIDTHS : DEFAULT_COLUMN_WIDTHS;

const COLUMN_CONFIG_BY_KEY = new Map<MetricColumnKey, ColumnToggleConfig>(
  COLUMN_TOGGLE_LIST.map((col) => [col.key, col])
);

export interface CategoryVendorItem extends MetricFields {
  id: string;
  vendor: string;
}

export interface CategoryGroupData {
  id: string;
  category: string;
  vendors: CategoryVendorItem[];
  otb_dollars?: number | null;
  plan_sales?: number | null;
  plan_inflow?: number | null;
  // Plan on-order (from plan data, like OTB) -- the On Order pill.
  plan_on_order?: number | null;
}

export interface CategoryBreakdownItem extends MetricFields {
  id: string;
  category: string;
  otb_dollars?: number | null;
  plan_sales?: number | null;
  plan_inflow?: number | null;
  // Plan on-order (from plan data, like OTB) -- the On Order pill.
  plan_on_order?: number | null;
}

export interface ClassVendorItem extends MetricFields {
  id: string;
  vendor: string;
}

export interface ClassGroupData {
  id: string;
  m1_class_name: string;
  m1_class_code: string;
  vendors: ClassVendorItem[];
  otb_dollars?: number | null;
  plan_sales?: number | null;
  plan_inflow?: number | null;
  // Plan on-order (from plan data, like OTB) -- the On Order pill.
  plan_on_order?: number | null;
}

export interface ClassBreakdownItem extends MetricFields {
  id: string;
  m1_class_name: string;
  m1_class_code: string;
}

export interface VendorRowData extends MetricFields {
  id: string;
  vendor: string;
  category?: string;
  m1_class_name?: string;
  has_alert?: boolean;
  otb_dollars?: number | null;
  plan_sales?: number | null;
  plan_inflow?: number | null;
  // Plan on-order (from plan data, like OTB) -- the On Order pill.
  plan_on_order?: number | null;

  categories: CategoryBreakdownItem[];
  classes: ClassBreakdownItem[];
}

// Raw API row shape returned by GET /api/reports/vendor-performance.
// Depending on group_by, a row carries `category` (vendor,category / category,vendor)
// or `m1_class_name`/`m1_class_code` (vendor,class / class,vendor) in addition to the
// always-present vendor + metric fields. When include_ly is true, every *_ly field and
// sales_change_pct are also present.
export interface VendorApiRow {
  vendor: string;
  category?: string;
  m1_class_name?: string;
  m1_class_code?: string;
  location_code?: string;
  location_name?: string;
  location_otb_retail?: number;
  location_otb_cost?: number;
  location_plan_sales_retail?: number;
  location_plan_sales_cost?: number;
  location_plan_inflow_retail?: number;
  location_plan_inflow_cost?: number;
  location_on_order_retail?: number;
  location_on_order_cost?: number;
  class_otb_retail?: number;
  class_otb_cost?: number;
  class_plan_sales_retail?: number;
  class_plan_sales_cost?: number;
  class_plan_inflow_retail?: number;
  class_plan_inflow_cost?: number;
  class_on_order_retail?: number;
  class_on_order_cost?: number;
  category_otb_retail?: number;
  category_otb_cost?: number;
  category_plan_sales_retail?: number;
  category_plan_sales_cost?: number;
  category_plan_inflow_retail?: number;
  category_plan_inflow_cost?: number;
  category_on_order_retail?: number;
  category_on_order_cost?: number;
  total_otb_retail?: number;
  total_otb_cost?: number;
  total_plan_sales_retail?: number;
  total_plan_sales_cost?: number;
  total_plan_inflow_retail?: number;
  total_plan_inflow_cost?: number;
  total_on_order_retail?: number;
  total_on_order_cost?: number;
  sales_dollars: number;
  sales_units: number;
  inventory_oh_dollars: number;
  inventory_oh_units: number;
  on_order_dollars: number;
  on_order_units: number;
  past_due_on_order_dollars?: number;
  past_due_on_order_units?: number;
  inflow_dollars: number;
  inflow_units?: number;
  purchases_cost_dollars?: number;
  cost_of_purchase_pct?: number | null;
  gross_margin_pct: number | null;
  maintained_margin_pct: number | null;
  markdown_dollars: number;
  markdown_pct: number | null;
  sell_through_pct: number | null;
  sell_through_start_inv_pct?: number | null;
  annual_turn_rate?: number | null;
  // Class views only: the store-level classes rolled into this row's plan class (m1_class_*),
  // joined with LEAF_CLASS_SEPARATOR.
  leaf_class_names?: string | null;

  sales_dollars_ly?: number;
  sales_units_ly?: number;
  inventory_oh_dollars_ly?: number;
  inventory_oh_units_ly?: number;
  on_order_dollars_ly?: number;
  on_order_units_ly?: number;
  past_due_on_order_dollars_ly?: number;
  past_due_on_order_units_ly?: number;
  inflow_dollars_ly?: number;
  inflow_units_ly?: number;
  purchases_cost_dollars_ly?: number;
  cost_of_purchase_pct_ly?: number | null;
  gross_margin_pct_ly?: number | null;
  maintained_margin_pct_ly?: number | null;
  markdown_dollars_ly?: number;
  markdown_pct_ly?: number | null;
  sell_through_pct_ly?: number | null;
  sell_through_start_inv_pct_ly?: number | null;
  sales_change_pct?: number | null;
}

export interface VendorApiResponse {
  rows: VendorApiRow[];
  totals: Partial<VendorApiRow>;
  generated_at: string;
  currency: string;
  turn_rate_window?: {
    from: string;
    to: string;
    inventory_months: number;
    inventory_basis?: 'month_end_average' | 'latest_snapshot' | 'none';
    turn_rows_returned?: number;
    rows_with_turn?: number;
    error?: string | null;
  };
}

export interface SpanDateRangeInfo {
  value: string;
  label: string;
  dateRange: string;
  fullLabel: string;
}

export const ON_ORDER_SPAN_OPTIONS = [
  '1_month',
  '2_months',
  '3_months',
  '6_months',
  'all',
] as const;

export type OnOrderSpanKey = (typeof ON_ORDER_SPAN_OPTIONS)[number] | 'custom';

export function getSpanDateRangeInfo(
  span: string,
  baseDate = new Date(),
  customStart?: string,
  customEnd?: string
): SpanDateRangeInfo {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  if (span === 'custom') {
    let dateRange = 'Custom Range';
    if (customStart && customEnd) {
      try {
        const s = new Date(customStart + 'T00:00:00');
        const e = new Date(customEnd + 'T00:00:00');
        const sYear = s.getFullYear();
        const eYear = e.getFullYear();
        const sMonth = monthNames[s.getMonth()];
        const sDay = s.getDate();
        const eMonth = monthNames[e.getMonth()];
        const eDay = e.getDate();
        dateRange = sYear === eYear
          ? `${sMonth} ${sDay} – ${eMonth} ${eDay}, ${eYear}`
          : `${sMonth} ${sDay}, ${sYear} – ${eMonth} ${eDay}, ${eYear}`;
      } catch (err) {
        dateRange = `${customStart} – ${customEnd}`;
      }
    } else if (customStart) {
      dateRange = `From ${customStart}`;
    } else if (customEnd) {
      dateRange = `Until ${customEnd}`;
    }
    return {
      value: 'custom',
      label: 'Custom',
      dateRange,
      fullLabel: `Custom (${dateRange})`,
    };
  }

  const start = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
  const startMonth = monthNames[start.getMonth()];
  const startDay = start.getDate();
  const startYear = start.getFullYear();

  let label = '3 Months Out';
  let monthsToAdd: number | null = 3;

  if (span === '1_month') {
    label = '1 Month Out';
    monthsToAdd = 1;
  } else if (span === '2_months') {
    label = '2 Months Out';
    monthsToAdd = 2;
  } else if (span === '3_months') {
    label = '3 Months Out';
    monthsToAdd = 3;
  } else if (span === '6_months') {
    label = '6 Months Out';
    monthsToAdd = 6;
  } else if (span === 'all') {
    label = 'All Future On-Order';
    monthsToAdd = null;
  }

  if (monthsToAdd === null) {
    const dateRange = `${startMonth} ${startDay}, ${startYear} – All Future`;
    return {
      value: span,
      label,
      dateRange,
      fullLabel: `${label} (${dateRange})`,
    };
  }

  // End date is last day of the span month (start + monthsToAdd - 1)
  const end = new Date(start.getFullYear(), start.getMonth() + monthsToAdd, 0);
  const endMonth = monthNames[end.getMonth()];
  const endDay = end.getDate();
  const endYear = end.getFullYear();

  const sameYear = startYear === endYear;
  const dateRange = sameYear
    ? `${startMonth} ${startDay} – ${endMonth} ${endDay}, ${endYear}`
    : `${startMonth} ${startDay}, ${startYear} – ${endMonth} ${endDay}, ${endYear}`;

  return {
    value: span,
    label,
    dateRange,
    fullLabel: `${label} (${dateRange})`,
  };
}

// A saved view as this browser kept it before saved views were stored on the server; read only to
// import those views once (useSavedViews). Column order, widths and pins stay in localStorage.
export interface SavedVendorPerformanceView {
  id: string;
  name: string;
  visibility: 'only_me' | 'shared';
  createdAt: string;
  view: VendorView;
  filters: {
    selectedDateRange: string;
    selectedLocations: string[];
    selectedClasses: string[];
    selectedCategories: string[];
    selectedVendors?: string[];
    excludeVendor?: boolean;
    minSales: string;
    showYOY: boolean;
  };
  visibleColumns: Record<string, boolean>;
  sortConfig?: SortConfig | null;
  onOrderSpan?: string;
  onOrderStartDate?: string;
  onOrderEndDate?: string;
  priceBasis?: 'retail' | 'cost';
  isFavorite?: boolean;
  showPctChangeDiff?: boolean;
  columnOrder?: MetricColumnKey[];
}

type StorageUserId = string | number | null | undefined;

const storageScope = (userId: StorageUserId, clientId: string | number): string =>
  `${userId ?? 'anonymous'}-${clientId}`;

export const getSavedViewsStorageKey = (userId: StorageUserId, clientId: string | number): string =>
  `vendor-performance-saved-views-${storageScope(userId, clientId)}`;

// Pre-per-user key, read only as a fallback so older views aren't lost.
const getLegacySavedViewsStorageKey = (clientId: string | number): string =>
  `vendor-performance-saved-views-${clientId}`;

export const getColumnOrderStorageKey = (userId: StorageUserId, clientId: string | number): string =>
  `vendor-performance-column-order-${storageScope(userId, clientId)}`;

export const getColumnWidthsStorageKey = (userId: StorageUserId, clientId: string | number): string =>
  `vendor-performance-column-widths-${storageScope(userId, clientId)}`;

export const getPinnedColumnsStorageKey = (userId: StorageUserId, clientId: string | number): string =>
  `vendor-performance-pinned-columns-${storageScope(userId, clientId)}`;

export const SAVED_VIEWS_STORAGE_PREFIX = 'vendor-performance';

// What the server stores for a view: everything that restores the report, without the identity
// and star, which live on the saved view record and per user respectively.
export type VendorPerformanceViewPayload = Omit<SavedVendorPerformanceView, 'id' | 'name' | 'visibility' | 'createdAt' | 'isFavorite'>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * This browser's views for the user + client, ready to import. Entries that couldn't be applied
 * (no id, no view, no filters) are skipped rather than imported broken.
 */
export const loadLocalSavedViews = (userId: StorageUserId, clientId: string | number): LocalSavedView<VendorPerformanceViewPayload>[] => {
  let parsed: unknown;
  try {
    const raw = window.localStorage.getItem(getSavedViewsStorageKey(userId, clientId))
      ?? window.localStorage.getItem(getLegacySavedViewsStorageKey(clientId));
    parsed = raw ? JSON.parse(raw) : [];
  } catch (error) {
    console.error('Failed to read saved vendor performance views from localStorage', error);
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  return parsed
    .filter((view): view is SavedVendorPerformanceView =>
      isRecord(view) && (typeof view.id === 'string' || typeof view.id === 'number') && typeof view.view === 'string' && isRecord(view.filters))
    .map(({ id, name, visibility, createdAt, isFavorite, ...payload }) => ({
      legacyId: String(id),
      name: (typeof name === 'string' && name.trim() ? name.trim() : 'Untitled view').slice(0, SAVED_VIEW_NAME_MAX_LENGTH),
      payload,
      isFavorite: Boolean(isFavorite),
    }));
};

const loadColumnOrder = (userId: StorageUserId, clientId: string | number): MetricColumnKey[] => {
  try {
    const raw = window.localStorage.getItem(getColumnOrderStorageKey(userId, clientId));
    return normalizeColumnOrder(raw ? JSON.parse(raw) : null);
  } catch (error) {
    console.error('Failed to read vendor performance column order from localStorage', error);
    return DEFAULT_COLUMN_ORDER;
  }
};

const persistColumnOrder = (userId: StorageUserId, clientId: string | number, order: MetricColumnKey[] | null): void => {
  try {
    const key = getColumnOrderStorageKey(userId, clientId);
    if (order === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, JSON.stringify(order));
    }
  } catch (error) {
    console.error('Failed to persist vendor performance column order to localStorage', error);
  }
};

const VIEW_TO_GROUP_BY: Record<VendorView, string> = {
  total_by_vendor: 'vendor',
  category_by_vendor: 'vendor,category',
  vendor_by_category: 'category,vendor',
  vendor_by_class: 'vendor,class',
  class_by_vendor: 'class,vendor',
  location_class_vendor: 'location,class,vendor',
};

export interface ViewOptionConfig {
  key: VendorView;
  label: string;
  hasOtb: boolean;
}

export const WITH_OTB_VIEW_OPTIONS: ViewOptionConfig[] = [
  { key: 'total_by_vendor', label: 'Total by Vendor', hasOtb: true },
  { key: 'vendor_by_category', label: 'Vendor by Category', hasOtb: true },
  { key: 'class_by_vendor', label: 'Class by Vendor', hasOtb: true },
  { key: 'location_class_vendor', label: 'Location > Class > Vendor', hasOtb: true },
];

export const WITHOUT_OTB_VIEW_OPTIONS: ViewOptionConfig[] = [
  { key: 'category_by_vendor', label: 'Category by Vendor', hasOtb: false },
  { key: 'vendor_by_class', label: 'Vendor by Class', hasOtb: false },
];

// Info-icon text for the sticky first column, describing what each row level is in the active view.
export const FIRST_COLUMN_DESCRIPTIONS: Record<VendorView, string> = {
  total_by_vendor: 'One row per vendor, totaled across every location, class and category in the filters',
  vendor_by_category: 'Grouped by category; each category row subtotals the vendors listed under it',
  class_by_vendor: 'Grouped by class; each class row subtotals the vendors listed under it',
  location_class_vendor: 'Grouped by location, then class; each group row subtotals the vendors under it',
  category_by_vendor: 'Grouped by vendor; each vendor row subtotals the categories listed under it',
  vendor_by_class: 'Grouped by vendor; each vendor row subtotals the classes listed under it',
};

// Which rows divide by which for the % columns, per grouped view -- shown in the note under the table.
export const GROUP_PARENT_RELATIONS: Record<Exclude<VendorView, 'total_by_vendor'>, string> = {
  location_class_vendor: 'vendor ÷ class, class ÷ location',
  class_by_vendor: 'vendor ÷ class',
  vendor_by_category: 'vendor ÷ category',
  category_by_vendor: 'category ÷ vendor',
  vendor_by_class: 'class ÷ vendor',
};

export const VIEW_OPTIONS: ViewOptionConfig[] = [
  ...WITH_OTB_VIEW_OPTIONS,
  ...WITHOUT_OTB_VIEW_OPTIONS,
];

// Resolves the Date Range popover's selection to a concrete ISO from/to ending today.
// `customRange`, when provided (the user picked actual dates via the calendar), always wins
// regardless of what `selectedDateRange`'s display label says -- the label is free to show a
// formatted date string instead of a literal preset name once a custom range is applied. Any
// unrecognized preset string (with no customRange) falls back to Last 3 Months, matching the
// component's default selectedDateRange.
//
// Presets are named in months (RetailORBIT's backend actuals aggregate monthly, via epochMonth),
// but resolved as simple trailing-day windows rather than calendar-month-aligned boundaries -- the
// vendor-performance API takes a literal `sales_date BETWEEN from AND to`, not an epochMonth
// bucket, so there's no correctness reason to prefer calendar-month edges over a plain N-day
// trailing window, and it keeps this resolver consistent with Month/Year to date below.
// Trailing presets => days covered, ending with the last complete day.
export const TRAILING_PRESET_DAYS: Record<string, number> = {
  'Last 1 Month': 30,
  'Last 2 Months': 60,
  'Last 3 Months': 90,
  'Last 6 Months': 180,
  'Last 12 Months': 365,
};
export const DEFAULT_TRAILING_PRESET = 'Last 3 Months';

// Every preset ends yesterday -- today is still a partial day of sales. Month / Year to date start
// from yesterday's month / year, so on the 1st they cover the last complete month rather than an
// empty range.
export const resolveDateRange = (
  selectedDateRange: string,
  customRange?: { from: Date; to: Date },
  today: Date = new Date(),
): { from: string; to: string } => {
  if (customRange) {
    return { from: formatDate(customRange.from, 'yyyy-MM-dd'), to: formatDate(customRange.to, 'yyyy-MM-dd') };
  }
  const lastCompleteDay = subDays(today, 1);
  const to = formatDate(lastCompleteDay, 'yyyy-MM-dd');
  switch (selectedDateRange) {
    case 'Month to date':
      return { from: formatDate(startOfMonth(lastCompleteDay), 'yyyy-MM-dd'), to };
    case 'Year to date':
      return { from: formatDate(startOfYear(lastCompleteDay), 'yyyy-MM-dd'), to };
    default: {
      const days = TRAILING_PRESET_DAYS[selectedDateRange] ?? TRAILING_PRESET_DAYS[DEFAULT_TRAILING_PRESET];
      return { from: formatDate(subDays(lastCompleteDay, days - 1), 'yyyy-MM-dd'), to };
    }
  }
};

const n = (val: number | null | undefined): number => (val == null || isNaN(val as number) ? 0 : val);

// Shared metric fields present on every API row, coerced to numbers with LY fields defaulted
// to 0 when include_ly is off. rtv_dollars has no backend data source yet — not part of the
// confirmed report spec, always 0 until added.
// Units sold / (units sold + on-hand units); a sold-out item (nothing on hand) is 100%.
export function computeSellThroughWithStartingInv(
  salesUnits: number,
  inventoryOhUnits: number,
  rawPct?: number | null
): number | null {
  if (rawPct != null && !isNaN(rawPct)) return rawPct;
  const baseUnits = inventoryOhUnits + salesUnits;
  if (baseUnits <= 0) return null;
  return salesUnits / baseUnits;
}

const buildMetricFields = (row: VendorApiRow) => ({
  sales_dollars: n(row.sales_dollars),
  sales_units: n(row.sales_units),
  inventory_oh_dollars: n(row.inventory_oh_dollars),
  inventory_oh_units: n(row.inventory_oh_units),
  on_order_dollars: n(row.on_order_dollars),
  on_order_units: n(row.on_order_units),
  past_due_on_order_dollars: n(row.past_due_on_order_dollars),
  past_due_on_order_units: n(row.past_due_on_order_units),
  inflow_dollars: n(row.inflow_dollars),
  cost_of_purchase_pct: row.cost_of_purchase_pct ?? null,
  gross_margin_pct: n(row.gross_margin_pct),
  maintained_margin_pct: n(row.maintained_margin_pct),
  markdown_dollars: n(row.markdown_dollars),
  markdown_pct: n(row.markdown_pct),
  sell_through_pct: row.sell_through_pct ?? null,
  sell_through_start_inv_pct: computeSellThroughWithStartingInv(
    n(row.sales_units),
    n(row.inventory_oh_units),
    row.sell_through_start_inv_pct
  ),
  rtv_dollars: 0,
  annual_turn_rate: row.annual_turn_rate ?? null,
  leaf_class_names: row.leaf_class_names ?? null,

  sales_dollars_ly: n(row.sales_dollars_ly),
  sales_units_ly: n(row.sales_units_ly),
  inventory_oh_dollars_ly: n(row.inventory_oh_dollars_ly),
  inventory_oh_units_ly: n(row.inventory_oh_units_ly),
  on_order_dollars_ly: n(row.on_order_dollars_ly),
  on_order_units_ly: n(row.on_order_units_ly),
  past_due_on_order_dollars_ly: n(row.past_due_on_order_dollars_ly),
  past_due_on_order_units_ly: n(row.past_due_on_order_units_ly),
  inflow_dollars_ly: n(row.inflow_dollars_ly),
  cost_of_purchase_pct_ly: row.cost_of_purchase_pct_ly ?? null,
  gross_margin_pct_ly: n(row.gross_margin_pct_ly),
  maintained_margin_pct_ly: n(row.maintained_margin_pct_ly),
  markdown_dollars_ly: n(row.markdown_dollars_ly),
  markdown_pct_ly: n(row.markdown_pct_ly),
  sell_through_pct_ly: row.sell_through_pct_ly ?? null,
  // Needs prior-year on-hand, which the current-only inventory snapshot can't provide.
  sell_through_start_inv_pct_ly: null as number | null,
  rtv_dollars_ly: 0,
  inflow_units: n(row.inflow_units),
  inflow_units_ly: n(row.inflow_units_ly),
  purchases_cost_dollars: n(row.purchases_cost_dollars),
  purchases_cost_dollars_ly: n(row.purchases_cost_dollars_ly),
  avg_selling_price: avgPerUnit(row.sales_dollars, row.sales_units),
  avg_selling_price_ly: avgPerUnit(row.sales_dollars_ly, row.sales_units_ly),
  avg_purchase_price: avgPerUnit(row.purchases_cost_dollars, row.inflow_units),
  avg_purchase_price_ly: avgPerUnit(row.purchases_cost_dollars_ly, row.inflow_units_ly),
});

export type MetricFields = ReturnType<typeof buildMetricFields>;

// Cost / retail ratio implied by a gross margin: cost = retail x (1 - GM). The backend sends GM as a
// 0-1 fraction (0.55); a value outside -1..1 is treated as already being a percent (55). A row with
// no margin (null) has no cost/retail spread to apply, so it converts 1:1.
export const onOrderCostMultiplier = (grossMarginPct: number | null | undefined): number => {
  if (grossMarginPct == null || isNaN(grossMarginPct)) return 1;
  const gm = grossMarginPct > 1 || grossMarginPct < -1 ? grossMarginPct / 100 : grossMarginPct;
  return Math.max(0, 1 - gm);
};

// Applied once to the fetched rows, before any grouping, so the On-Order column, every group
// subtotal, % of On-Order, the footer and the On Order pills all read the same price basis.
export const toPriceBasisRow = (row: VendorApiRow, priceBasis: 'retail' | 'cost'): VendorApiRow => {
  if (priceBasis !== 'cost') return row;
  return {
    ...row,
    on_order_dollars: n(row.on_order_dollars) * onOrderCostMultiplier(row.gross_margin_pct),
    on_order_dollars_ly: n(row.on_order_dollars_ly) * onOrderCostMultiplier(row.gross_margin_pct_ly),
    past_due_on_order_dollars: n(row.past_due_on_order_dollars) * onOrderCostMultiplier(row.gross_margin_pct),
    past_due_on_order_dollars_ly: n(row.past_due_on_order_dollars_ly) * onOrderCostMultiplier(row.gross_margin_pct_ly),
  };
};

// Sums additive metrics and weights percentages across flat API rows or group items (vendor roll-ups, Subtotal row, Group Subtotals).
export const sumVendorMetrics = (rows: Array<VendorApiRow | MetricFields | any>): MetricFields => {
  const sums = {
    sales_dollars: 0, sales_units: 0, inventory_oh_dollars: 0, inventory_oh_units: 0,
    on_order_dollars: 0, on_order_units: 0, inflow_dollars: 0, markdown_dollars: 0,
    past_due_on_order_dollars: 0, past_due_on_order_units: 0,
    sales_dollars_ly: 0, sales_units_ly: 0, inventory_oh_dollars_ly: 0, inventory_oh_units_ly: 0,
    on_order_dollars_ly: 0, on_order_units_ly: 0, inflow_dollars_ly: 0, markdown_dollars_ly: 0,
    past_due_on_order_dollars_ly: 0, past_due_on_order_units_ly: 0,
    inflow_units: 0, inflow_units_ly: 0,
    purchases_cost_dollars: 0, purchases_cost_dollars_ly: 0,
  };
  let grossProfitDollars = 0;
  let grossProfitDollarsLy = 0;
  let maintainedProfitDollars = 0;
  let maintainedProfitDollarsLy = 0;
  let purchasesCostDollars = 0;
  let purchasesCostDollarsLy = 0;
  let hasPurchasesCost = false;
  let hasPurchasesCostLy = false;
  let turnWeightedSum = 0;
  let turnSalesSum = 0;
  const leafClassNames = new Set<string>();

  rows.forEach((r) => {
    const s = n(r.sales_dollars);
    sums.sales_dollars += s;
    sums.sales_units += n(r.sales_units);
    sums.inventory_oh_dollars += n(r.inventory_oh_dollars);
    sums.inventory_oh_units += n(r.inventory_oh_units);
    sums.on_order_dollars += n(r.on_order_dollars);
    sums.on_order_units += n(r.on_order_units);
    sums.past_due_on_order_dollars += n(r.past_due_on_order_dollars);
    sums.past_due_on_order_units += n(r.past_due_on_order_units);
    sums.inflow_dollars += n(r.inflow_dollars);
    sums.markdown_dollars += n(r.markdown_dollars);
    sums.inflow_units += n(r.inflow_units);
    sums.inflow_units_ly += n(r.inflow_units_ly);
    sums.purchases_cost_dollars += n(r.purchases_cost_dollars);
    sums.purchases_cost_dollars_ly += n(r.purchases_cost_dollars_ly);

    const sLy = n(r.sales_dollars_ly);
    sums.sales_dollars_ly += sLy;
    sums.sales_units_ly += n(r.sales_units_ly);
    sums.inventory_oh_dollars_ly += n(r.inventory_oh_dollars_ly);
    sums.inventory_oh_units_ly += n(r.inventory_oh_units_ly);
    sums.on_order_dollars_ly += n(r.on_order_dollars_ly);
    sums.on_order_units_ly += n(r.on_order_units_ly);
    sums.past_due_on_order_dollars_ly += n(r.past_due_on_order_dollars_ly);
    sums.past_due_on_order_units_ly += n(r.past_due_on_order_units_ly);
    sums.inflow_dollars_ly += n(r.inflow_dollars_ly);
    sums.markdown_dollars_ly += n(r.markdown_dollars_ly);

    // Margins may arrive as a fraction (0.55) or a percent (55).
    const gm = n(r.gross_margin_pct);
    const gmFrac = (gm > 1 || gm < -1) ? gm / 100 : gm;
    grossProfitDollars += s * gmFrac;

    const gmLy = n(r.gross_margin_pct_ly);
    const gmLyFrac = (gmLy > 1 || gmLy < -1) ? gmLy / 100 : gmLy;
    grossProfitDollarsLy += sLy * gmLyFrac;

    const mm = n(r.maintained_margin_pct);
    const mmFrac = (mm > 1 || mm < -1) ? mm / 100 : mm;
    maintainedProfitDollars += s * mmFrac;

    const mmLy = n(r.maintained_margin_pct_ly);
    const mmLyFrac = (mmLy > 1 || mmLy < -1) ? mmLy / 100 : mmLy;
    maintainedProfitDollarsLy += sLy * mmLyFrac;

    if (r.cost_of_purchase_pct != null) {
      hasPurchasesCost = true;
      const cop = n(r.cost_of_purchase_pct);
      const copFrac = (cop > 1 || cop < -1) ? cop / 100 : cop;
      purchasesCostDollars += s * copFrac;
    }
    if (r.cost_of_purchase_pct_ly != null) {
      hasPurchasesCostLy = true;
      const copLy = n(r.cost_of_purchase_pct_ly);
      const copLyFrac = (copLy > 1 || copLy < -1) ? copLy / 100 : copLy;
      purchasesCostDollarsLy += sLy * copLyFrac;
    }

    if (r.annual_turn_rate != null) {
      turnWeightedSum += n(r.annual_turn_rate) * s;
      turnSalesSum += s;
    }
    splitLeafClassNames(r.leaf_class_names).forEach((name) => leafClassNames.add(name));
  });

  const gross_margin_pct = sums.sales_dollars > 0 ? grossProfitDollars / sums.sales_dollars : 0;
  const gross_margin_pct_ly = sums.sales_dollars_ly > 0 ? grossProfitDollarsLy / sums.sales_dollars_ly : 0;
  const maintained_margin_pct = sums.sales_dollars > 0 ? maintainedProfitDollars / sums.sales_dollars : 0;
  const maintained_margin_pct_ly = sums.sales_dollars_ly > 0 ? maintainedProfitDollarsLy / sums.sales_dollars_ly : 0;

  const sell_through_pct = sums.inflow_units > 0 ? sums.sales_units / sums.inflow_units : null;
  const sell_through_pct_ly = sums.inflow_units_ly > 0 ? sums.sales_units_ly / sums.inflow_units_ly : null;

  const sell_through_start_inv_pct = computeSellThroughWithStartingInv(sums.sales_units, sums.inventory_oh_units);
  const sell_through_start_inv_pct_ly: number | null = null;

  return {
    ...sums,
    markdown_pct: sums.sales_dollars > 0 ? sums.markdown_dollars / sums.sales_dollars : 0,
    markdown_pct_ly: sums.sales_dollars_ly > 0 ? sums.markdown_dollars_ly / sums.sales_dollars_ly : 0,
    gross_margin_pct,
    gross_margin_pct_ly,
    maintained_margin_pct,
    maintained_margin_pct_ly,
    sell_through_pct,
    sell_through_pct_ly,
    sell_through_start_inv_pct,
    sell_through_start_inv_pct_ly,
    rtv_dollars: 0,
    rtv_dollars_ly: 0,
    annual_turn_rate: turnSalesSum > 0 ? turnWeightedSum / turnSalesSum : null,
    // Every sub-class under the rolled-up rows, once each.
    leaf_class_names: joinLeafClassNames(Array.from(leafClassNames)),
    cost_of_purchase_pct: hasPurchasesCost && sums.sales_dollars > 0 ? purchasesCostDollars / sums.sales_dollars : null,
    cost_of_purchase_pct_ly: hasPurchasesCostLy && sums.sales_dollars_ly > 0 ? purchasesCostDollarsLy / sums.sales_dollars_ly : null,
    // Recomputed from the summed dollars and units, never averaged across rows.
    avg_selling_price: avgPerUnit(sums.sales_dollars, sums.sales_units),
    avg_selling_price_ly: avgPerUnit(sums.sales_dollars_ly, sums.sales_units_ly),
    avg_purchase_price: avgPerUnit(sums.purchases_cost_dollars, sums.inflow_units),
    avg_purchase_price_ly: avgPerUnit(sums.purchases_cost_dollars_ly, sums.inflow_units_ly),
  };
};

function groupRowsBy(rows: VendorApiRow[], keyFn: (row: VendorApiRow) => string): Map<string, VendorApiRow[]> {
  const map = new Map<string, VendorApiRow[]>();
  rows.forEach((row) => {
    const key = keyFn(row);
    const list = map.get(key);
    if (list) list.push(row); else map.set(key, [row]);
  });
  return map;
}

const mapToVendorRowData = (row: VendorApiRow): VendorRowData => ({
  id: `${row.vendor}-${row.category ?? row.m1_class_name ?? ''}`,
  vendor: row.vendor,
  category: row.category,
  m1_class_name: row.m1_class_name,
  // has_alert has no confirmed trigger condition yet — always false until product defines one.
  has_alert: false,
  ...buildMetricFields(row),
  categories: [],
  classes: [],
});

const mapToCategoryBreakdownItem = (row: VendorApiRow, isCost: boolean): CategoryBreakdownItem => ({
  id: `${row.vendor}-${row.category ?? ''}`,
  category: row.category ?? '',
  otb_dollars: isCost ? row.category_otb_cost : row.category_otb_retail,
  plan_sales: isCost ? row.category_plan_sales_cost : row.category_plan_sales_retail,
  plan_inflow: isCost ? row.category_plan_inflow_cost : row.category_plan_inflow_retail,
  ...buildMetricFields(row),
});

const mapToClassBreakdownItem = (row: VendorApiRow): ClassBreakdownItem => ({
  id: `${row.vendor}-${row.m1_class_code ?? row.m1_class_name ?? ''}`,
  m1_class_name: row.m1_class_name ?? '',
  m1_class_code: row.m1_class_code ?? '',
  ...buildMetricFields(row),
});

const mapToCategoryVendorItem = (row: VendorApiRow): CategoryVendorItem => ({
  id: `${row.category ?? ''}-${row.vendor}`,
  vendor: row.vendor,
  ...buildMetricFields(row),
});

const mapToClassVendorItem = (row: VendorApiRow): ClassVendorItem => ({
  id: `${row.m1_class_code ?? row.m1_class_name ?? ''}-${row.vendor}`,
  vendor: row.vendor,
  ...buildMetricFields(row),
});

const mapToLocationClassVendorItem = (locKey: string, classKey: string, row: VendorApiRow): ClassVendorItem => ({
  id: `${locKey}-${classKey}-${row.vendor}`,
  vendor: row.vendor,
  ...buildMetricFields(row),
});

const fmtCurrency = (val: number | undefined): string => {
  if (val === undefined || isNaN(val)) return '$0';
  const prefix = val < 0 ? '-$' : '$';
  // Rounded to the nearest whole dollar -- BigQuery's NUMERIC/FLOAT64 sums can carry cents-level
  // fractions (e.g. 1420.6789); this report never shows decimals on a dollar figure.
  return `${prefix}${Math.round(Math.abs(val)).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

// A per-unit price keeps its cents ($12.49), unlike the whole-dollar totals; "-" without one.
export const fmtUnitPrice = (val: number | null | undefined): string => {
  if (val == null || !Number.isFinite(val)) return '-';
  const prefix = val < 0 ? '-$' : '$';
  return `${prefix}${Math.abs(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtUnits = (val: number | undefined): string => {
  if (val === undefined || isNaN(val)) return '0';
  // Rounded to a whole unit -- units_sold is FLOAT64 in BigQuery, so a summed value can arrive
  // with a fractional remainder even though a "unit" is conceptually always a whole number.
  return Math.round(val).toLocaleString('en-US', { maximumFractionDigits: 0 });
};

export const calcPctOfTotal = (val: number | undefined, total: number | undefined): number => {
  if (!val || !total || Number(total) <= 0) return 0;
  return (Number(val) / Number(total)) * 100;
};

export const sumPctOfTotals = (rows: MetricFields[]): PctOfTotals =>
  rows.reduce<PctOfTotals>(
    (acc, r) => ({
      sales: acc.sales + n(r.sales_dollars),
      inv: acc.inv + n(r.inventory_oh_dollars),
      salesLy: acc.salesLy + n(r.sales_dollars_ly),
      invLy: acc.invLy + n(r.inventory_oh_dollars_ly),
      inflow: acc.inflow + n(r.inflow_dollars),
      inflowLy: acc.inflowLy + n(r.inflow_dollars_ly),
      onOrder: acc.onOrder + n(r.on_order_dollars),
      onOrderLy: acc.onOrderLy + n(r.on_order_dollars_ly),
    }),
    { sales: 0, inv: 0, salesLy: 0, invLy: 0, inflow: 0, inflowLy: 0, onOrder: 0, onOrderLy: 0 }
  );

const fmtTurn = (val: number | null | undefined): string => {
  if (val == null || isNaN(val)) return '-';
  return `${val.toFixed(1)}x`;
};

const fmtPct = (val: number | null | undefined): string => {
  if (val == null || isNaN(val)) return '-';
  const num = val > 1 || val < -1 ? val : val * 100;
  return `${num.toFixed(1)}%`;
};

// Diff cells show only a sign and text color (green/red) -- no arrow icon. Callers pair `text`
// with `isPositive` to pick the color class.
const fmtDiffCurrency = (thisVal: number, lyVal: number): { text: string; isPositive: boolean } => {
  const diff = thisVal - lyVal;
  const isPositive = diff >= 0;
  const sign = isPositive ? '+' : '-';
  // Rounded to the nearest whole dollar, matching fmtCurrency -- see its comment for why a
  // BigQuery-summed dollar value can carry a cents-level fraction.
  const formatted = `$${Math.round(Math.abs(diff)).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  return { text: `${sign}${formatted}`, isPositive };
};

// Change in a per-unit price, to the cent.
export const fmtDiffUnitPrice = (thisVal: number, lyVal: number): { text: string; isPositive: boolean } => {
  const diff = thisVal - lyVal;
  const isPositive = diff >= 0;
  return { text: `${isPositive ? '+' : '-'}${fmtUnitPrice(Math.abs(diff))}`, isPositive };
};

const fmtDiffUnits = (thisVal: number, lyVal: number): { text: string; isPositive: boolean } => {
  const diff = thisVal - lyVal;
  const isPositive = diff >= 0;
  const sign = isPositive ? '+' : '-';
  return { text: `${sign}${Math.round(Math.abs(diff)).toLocaleString('en-US')}`, isPositive };
};

// Alternate display mode for the $/unit diff columns (toggled by "Show % change"): percent
// change instead of an absolute difference. Mirrors the backend's own sales_change_pct
// convention (VendorPerformanceService::mergeRow) -- LY <= 0 has no meaningful percent change,
// so it renders as "-" rather than a divide-by-zero/infinite value.
const fmtDiffPercentChange = (thisVal: number, lyVal: number): { text: string; isPositive: boolean } => {
  const diff = thisVal - lyVal;
  const isPositive = diff >= 0;
  if (lyVal <= 0) {
    return { text: '-', isPositive };
  }
  const pctChange = (diff / lyVal) * 100;
  const sign = pctChange >= 0 ? '+' : '-';
  return { text: `${sign}${Math.abs(pctChange).toFixed(1)}%`, isPositive };
};

const fmtDiffPctPts = (thisVal: number, lyVal: number): { text: string; isPositive: boolean } => {
  const tPct = thisVal > 1 ? thisVal : thisVal * 100;
  const lPct = lyVal > 1 ? lyVal : lyVal * 100;
  const diff = tPct - lPct;
  const isPositive = diff >= 0;
  const sign = isPositive ? '+' : '-';
  return { text: `${sign}${Math.abs(diff).toFixed(1)}pts`, isPositive };
};

const fmtDiffPct = (thisVal: number, lyVal: number): { text: string; isPositive: boolean } => {
  const tPct = thisVal > 1 ? thisVal : thisVal * 100;
  const lPct = lyVal > 1 ? lyVal : lyVal * 100;
  const diff = tPct - lPct;
  const isPositive = diff >= 0;
  const sign = isPositive ? '+' : '-';
  return { text: `${sign}${Math.abs(diff).toFixed(1)}%`, isPositive };
};

// Maintained margin % badge color, per the tech spec's thresholds. Returns a SELF-CONTAINED
// bg/text/border class string with no font-size/weight -- callers must not also apply another
// class that sets `text-*`/`bg-*`/`border-*` on the same element. (A prior version was combined
// with `metricCellCss`, which carries its own `text-[#344054]`; two same-specificity Tailwind
// arbitrary-value color utilities on one element race for which one's generated CSS rule appears
// later in the stylesheet, and the loser is invisible -- this was reported as "green badge shows
// black text, red badge shows red text": a fixed, but non-obvious, per-color outcome of that
// race, not a random flake. Keeping this function's classes free of anything a caller might also
// apply elsewhere avoids the collision entirely instead of relying on the compiled CSS's rule
// order.)
// Exact text/stroke/bg hex values per the Figma badge spec (node 40679-147339) -- match these
// precisely rather than approximating, and don't reintroduce a solid-fill "critical" variant for
// low values: the design has exactly 3 badge colors, and anything below the amber band uses the
// same light-red treatment as the rest of that band, not a visually distinct 4th style.
const getBadgeClass = (pct: number): string => {
  const val = pct > 1 ? pct / 100 : pct;
  if (val >= 0.50) {
    return 'bg-[#ECFDF3] text-[#067647]'; // Healthy (>=50%)
  } else if (val >= 0.40) {
    return 'bg-[#FFFAEB] text-[#B54708]'; // Watch (40%-<50%)
  } else {
    return 'bg-[#FEF3F2] text-[#B42318]'; // Low (<40%)
  }
};

const metricCellCss = 'text-[#344054] text-[13px] font-semibold leading-[19.5px]';

// Font sizing/weight only, deliberately with NO text color -- for the maintained-margin badge,
// which must supply its own color via getBadgeClass(). Combining metricCellCss's own
// text-[#344054] with getBadgeClass()'s color caused the badge's intended color to
// unpredictably lose to metricCellCss's, per-color-value, in the compiled Tailwind CSS.
const badgeTextCss = 'text-xs font-semibold text-center';

const lastYearCellCss = 'text-[#64748B] text-[13px] font-medium leading-[19.5px]';

// The table uses border-separate so pinned cells keep their borders; row dividers sit on the cells.
const ROW_DIVIDER = 'border-b border-[#E4E4E7]';
const FIRST_COL_SHADOW = 'shadow-[1px_0_0_0_#EAECF0]';

type DiffResult = { text: string; isPositive: boolean };

// Explicit-unit formatters: fmtPct/fmtDiffPctPts guess units by magnitude and misread values
// under 1% or over 100%.
const toPercent = (fraction: number | null | undefined): number | null =>
  fraction == null || isNaN(fraction) ? null : fraction * 100;

export const fmtPercentValue = (percent: number | null | undefined): string =>
  percent == null || isNaN(percent) ? '-' : `${percent.toFixed(1)}%`;

export const fmtDiffPts = (thisPercent: number | null, lyPercent: number | null): DiffResult | null => {
  if (thisPercent == null || lyPercent == null) return null;
  const diff = thisPercent - lyPercent;
  const isPositive = diff >= 0;
  return { text: `${isPositive ? '+' : '-'}${Math.abs(diff).toFixed(1)}pts`, isPositive };
};

export type MetricRowKind = 'current' | 'ly' | 'change';

export interface PctOfTotals {
  sales: number;
  inv: number;
  salesLy: number;
  invLy: number;
  inflow: number;
  inflowLy: number;
  onOrder: number;
  onOrderLy: number;
}

export interface MetricCellContext {
  overallTotals: PctOfTotals;
  parentTotals?: PctOfTotals;
  diffCurrency: (thisVal: number, lyVal: number) => DiffResult;
  diffUnit: (thisVal: number, lyVal: number) => DiffResult;
  // Change in a per-unit price; follows "Show % change" like the others. Defaults to the cent diff.
  diffUnitPrice?: (thisVal: number, lyVal: number) => DiffResult;
  isGroupHeader?: boolean;
  tdClassName?: string;
  tdStyle?: React.CSSProperties;
}

interface MetricColumnRenderer {
  current: (row: MetricFields, ctx: MetricCellContext) => React.ReactNode;
  ly: (row: MetricFields, ctx: MetricCellContext) => React.ReactNode;
  // null renders a neutral "-".
  change: (row: MetricFields, ctx: MetricCellContext) => DiffResult | null;
  cellClassName?: Partial<Record<'current' | 'ly', string>>;
}

type NumericMetricField = {
  [K in keyof MetricFields]: MetricFields[K] extends number ? K : never;
}[keyof MetricFields];

type NullableNumericMetricField = {
  [K in keyof MetricFields]: MetricFields[K] extends number | null | undefined ? K : never;
}[keyof MetricFields];

const dollarsColumn = (cur: NumericMetricField, ly: NumericMetricField): MetricColumnRenderer => ({
  current: (r) => fmtCurrency(r[cur]),
  ly: (r) => fmtCurrency(r[ly]),
  change: (r, ctx) => ctx.diffCurrency(r[cur], r[ly]),
});

const unitsColumn = (cur: NumericMetricField, ly: NumericMetricField): MetricColumnRenderer => ({
  current: (r) => fmtUnits(r[cur]),
  ly: (r) => fmtUnits(r[ly]),
  change: (r, ctx) => ctx.diffUnit(r[cur], r[ly]),
});

const fractionColumn = (
  cur: NullableNumericMetricField,
  ly: NullableNumericMetricField,
  diff: (thisVal: number, lyVal: number) => DiffResult
): MetricColumnRenderer => ({
  current: (r) => fmtPct(r[cur]),
  ly: (r) => fmtPct(r[ly]),
  change: (r) => {
    const curVal = r[cur];
    const lyVal = r[ly];
    if (curVal == null || lyVal == null || isNaN(curVal) || isNaN(lyVal)) return null;
    return diff(curVal, lyVal);
  },
});

// A per-unit price: "-" where a period has no units, and no Change unless both periods have one.
const unitPriceColumn = (cur: NullableNumericMetricField, ly: NullableNumericMetricField): MetricColumnRenderer => ({
  current: (r) => fmtUnitPrice(r[cur]),
  ly: (r) => fmtUnitPrice(r[ly]),
  change: (r, ctx) => {
    const curVal = r[cur];
    const lyVal = r[ly];
    if (curVal == null || lyVal == null) return null;
    return (ctx.diffUnitPrice ?? fmtDiffUnitPrice)(curVal, lyVal);
  },
});

// Inventory and on-order exist only as today's snapshot (there is no prior-year one), so their
// columns have no Last Year or Change value.
const currentOnly = (renderer: MetricColumnRenderer): MetricColumnRenderer => ({
  ...renderer,
  ly: () => '-',
  change: () => null,
});

const pctOfTotalColumn = (
  cur: NumericMetricField,
  ly: NumericMetricField,
  total: 'sales' | 'inv' | 'inflow' | 'onOrder',
  totalLy: 'salesLy' | 'invLy' | 'inflowLy' | 'onOrderLy'
): MetricColumnRenderer => {
  const base = (ctx: MetricCellContext) => ctx.parentTotals ?? ctx.overallTotals;
  const curPct = (r: MetricFields, ctx: MetricCellContext) => calcPctOfTotal(r[cur], base(ctx)[total]);
  const lyPct = (r: MetricFields, ctx: MetricCellContext) => calcPctOfTotal(r[ly], base(ctx)[totalLy]);
  return {
    current: (r, ctx) => fmtPercentValue(curPct(r, ctx)),
    ly: (r, ctx) => fmtPercentValue(lyPct(r, ctx)),
    change: (r, ctx) => fmtDiffPts(curPct(r, ctx), lyPct(r, ctx)),
  };
};

export const METRIC_COLUMN_RENDERERS: Record<MetricColumnKey, MetricColumnRenderer> = {
  sales_dollars: dollarsColumn('sales_dollars', 'sales_dollars_ly'),
  sales_pct_of_total: pctOfTotalColumn('sales_dollars', 'sales_dollars_ly', 'sales', 'salesLy'),
  sales_units: unitsColumn('sales_units', 'sales_units_ly'),
  avg_selling_price: unitPriceColumn('avg_selling_price', 'avg_selling_price_ly'),
  avg_purchase_price: unitPriceColumn('avg_purchase_price', 'avg_purchase_price_ly'),
  inventory_oh_dollars: currentOnly(dollarsColumn('inventory_oh_dollars', 'inventory_oh_dollars_ly')),
  inventory_pct_of_total: currentOnly(pctOfTotalColumn('inventory_oh_dollars', 'inventory_oh_dollars_ly', 'inv', 'invLy')),
  inventory_oh_units: currentOnly(unitsColumn('inventory_oh_units', 'inventory_oh_units_ly')),
  inflow_dollars: dollarsColumn('inflow_dollars', 'inflow_dollars_ly'),
  inflow_pct_of_total: pctOfTotalColumn('inflow_dollars', 'inflow_dollars_ly', 'inflow', 'inflowLy'),
  cost_of_purchase_pct: {
    current: (r) => fmtPercentValue(toPercent(r.cost_of_purchase_pct)),
    ly: (r) => fmtPercentValue(toPercent(r.cost_of_purchase_pct_ly)),
    change: (r) => fmtDiffPts(toPercent(r.cost_of_purchase_pct), toPercent(r.cost_of_purchase_pct_ly)),
  },
  on_order_dollars: currentOnly(dollarsColumn('on_order_dollars', 'on_order_dollars_ly')),
  on_order_pct_of_total: currentOnly(pctOfTotalColumn('on_order_dollars', 'on_order_dollars_ly', 'onOrder', 'onOrderLy')),
  on_order_units: currentOnly(unitsColumn('on_order_units', 'on_order_units_ly')),
  past_due_on_order_dollars: currentOnly(dollarsColumn('past_due_on_order_dollars', 'past_due_on_order_dollars_ly')),
  past_due_on_order_units: currentOnly(unitsColumn('past_due_on_order_units', 'past_due_on_order_units_ly')),
  gross_margin_pct: fractionColumn('gross_margin_pct', 'gross_margin_pct_ly', fmtDiffPctPts),
  maintained_margin_pct: {
    current: (r) => (
      <span className={`inline-block px-2.5 py-0.5 rounded-full ${badgeTextCss} ${getBadgeClass(r.maintained_margin_pct)}`}>
        {fmtPct(r.maintained_margin_pct)}
      </span>
    ),
    ly: (r) => (
      <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#F1F5F9] text-[#64748B]">
        {fmtPct(r.maintained_margin_pct_ly)}
      </span>
    ),
    change: (r) => fmtDiffPctPts(r.maintained_margin_pct, r.maintained_margin_pct_ly),
    cellClassName: { current: 'px-4 py-3 text-right', ly: 'px-4 py-2 text-right' },
  },
  markdown_dollars: dollarsColumn('markdown_dollars', 'markdown_dollars_ly'),
  markdown_pct: fractionColumn('markdown_pct', 'markdown_pct_ly', fmtDiffPctPts),
  sell_through_pct: {
    current: (r) => fmtPercentValue(toPercent(r.sell_through_pct)),
    ly: (r) => fmtPercentValue(toPercent(r.sell_through_pct_ly)),
    change: (r) => fmtDiffPts(toPercent(r.sell_through_pct), toPercent(r.sell_through_pct_ly)),
  },
  sell_through_start_inv_pct: fractionColumn('sell_through_start_inv_pct', 'sell_through_start_inv_pct_ly', fmtDiffPct),
  // Fixed annual figure with no prior-period counterpart.
  annual_turn_rate: {
    current: (r) => fmtTurn(r.annual_turn_rate),
    ly: () => '-',
    change: () => null,
  },
  rtv_dollars: dollarsColumn('rtv_dollars', 'rtv_dollars_ly'),
  // Text, current period only: the sub-classes under the plan class.
  leaf_class_names: {
    current: (r) => r.leaf_class_names || '-',
    ly: () => '-',
    change: () => null,
    cellClassName: { current: 'px-4 py-3 text-left', ly: 'px-4 py-2 text-left' },
  },
};

export const renderMetricCell = (
  col: ColumnToggleConfig,
  row: MetricFields,
  kind: MetricRowKind,
  ctx: MetricCellContext
): React.ReactElement => {
  const renderer = METRIC_COLUMN_RENDERERS[col.key];
  if (kind === 'change') {
    const diff = renderer.change(row, ctx);
    if (!diff) {
      return (
        <td key={col.key} className={`px-4 py-2 text-right ${lastYearCellCss} ${ctx.tdClassName ?? ''}`} style={ctx.tdStyle}>
          -
        </td>
      );
    }
    return (
      <td
        key={col.key}
        className={`px-4 py-2 text-right font-bold text-xs ${diff.isPositive ? 'text-[#16A34A]' : 'text-[#DC2626]'} ${ctx.tdClassName ?? ''}`}
        style={ctx.tdStyle}
      >
        {diff.text}
      </td>
    );
  }
  const defaultClass = ctx.isGroupHeader
    ? (kind === 'current' ? 'px-4 py-2.5 text-right font-bold text-[#101828] text-[13px]' : 'px-4 py-2 text-right font-medium text-[#475467] text-[12px]')
    : (kind === 'current' ? `px-4 py-3 text-right ${metricCellCss}` : `px-4 py-2 text-right ${lastYearCellCss}`);
  const className = ctx.isGroupHeader && kind === 'current'
    ? (col.key === 'maintained_margin_pct' ? 'px-4 py-2.5 text-right font-bold text-[13px]' : 'px-4 py-2.5 text-right font-bold text-[#101828] text-[13px]')
    : (renderer.cellClassName?.[kind] ?? defaultClass);
  return (
    <td key={col.key} className={`${className} ${ctx.tdClassName ?? ''}`} style={ctx.tdStyle}>
      {renderer[kind](row, ctx)}
    </td>
  );
};

// --- CSV export ---

// One metric cell as plain text -- the same value the table shows, so the export matches the
// screen (price basis, % of the parent group, "-" for no data).
export const metricCellText = (
  col: ColumnToggleConfig,
  row: MetricFields,
  kind: MetricRowKind,
  ctx: MetricCellContext
): string => {
  const renderer = METRIC_COLUMN_RENDERERS[col.key];
  if (kind === 'change') return renderer.change(row, ctx)?.text ?? '-';
  // The only renderer that returns markup (a colored badge) rather than text.
  if (col.key === 'maintained_margin_pct') {
    return fmtPct(kind === 'ly' ? row.maintained_margin_pct_ly : row.maintained_margin_pct);
  }
  const value = renderer[kind](row, ctx);
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
};

// Quotes a value when it contains a comma, quote or line break (doubling inner quotes). Text that
// a spreadsheet would run as a formula (=, +, @) is prefixed with an apostrophe.
export const csvCell = (value: string, isText = false): string => {
  const safe = isText && /^[=+@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

export interface VendorPerformanceCsvLeaf {
  // Values for the view's dimension columns, e.g. [category, vendor].
  dims: string[];
  row: MetricFields;
  // The row's parent group totals for the % columns; omit to compare with the report total.
  parentTotals?: PctOfTotals;
}

export const CSV_PERIOD_LABELS: Record<MetricRowKind, string> = {
  current: 'This Period',
  ly: 'Last Year',
  change: 'Change',
};

/**
 * The report as CSV: the view's dimension columns, a Period column when YOY is on, then the
 * visible metric columns in their on-screen order. One row per leaf (three with YOY), then the
 * Total row(s).
 */
export function buildVendorPerformanceCsv({
  dimHeaders,
  leaves,
  subtotal,
  columns,
  headerLabel,
  ctx,
  showYOY,
}: {
  dimHeaders: string[];
  leaves: VendorPerformanceCsvLeaf[];
  subtotal: MetricFields | null;
  columns: ColumnToggleConfig[];
  headerLabel: (col: ColumnToggleConfig) => string;
  ctx: MetricCellContext;
  showYOY: boolean;
}): string {
  const kinds: MetricRowKind[] = showYOY ? ['current', 'ly', 'change'] : ['current'];
  const header = [...dimHeaders, ...(showYOY ? ['Period'] : []), ...columns.map(headerLabel)].map((h) => csvCell(h, true));

  const lines = (dims: string[], row: MetricFields, rowCtx: MetricCellContext): string[] =>
    kinds.map((kind) =>
      [
        ...dims.map((d) => csvCell(d, true)),
        ...(showYOY ? [CSV_PERIOD_LABELS[kind]] : []),
        ...columns.map((col) => csvCell(metricCellText(col, row, kind, rowCtx), col.isText)),
      ].join(',')
    );

  const body = leaves.flatMap((leaf) =>
    lines(leaf.dims, leaf.row, leaf.parentTotals ? { ...ctx, parentTotals: leaf.parentTotals } : ctx)
  );
  const subtotalLines = subtotal
    ? lines(['Total', ...dimHeaders.slice(1).map(() => '')], subtotal, ctx)
    : [];

  return [header.join(','), ...body, ...subtotalLines].join('\r\n');
}

// Drops unknown/duplicate keys and inserts columns added since the order was saved after their
// default predecessor.
export const normalizeColumnOrder = (saved: unknown): MetricColumnKey[] => {
  const known = new Set<string>(DEFAULT_COLUMN_ORDER);
  const result: MetricColumnKey[] = [];
  if (Array.isArray(saved)) {
    saved.forEach((key) => {
      if (typeof key === 'string' && known.has(key) && !result.includes(key as MetricColumnKey)) {
        result.push(key as MetricColumnKey);
      }
    });
  }
  DEFAULT_COLUMN_ORDER.forEach((key, idx) => {
    if (result.includes(key)) return;
    let insertAt = 0;
    for (let p = idx - 1; p >= 0; p--) {
      const at = result.indexOf(DEFAULT_COLUMN_ORDER[p]);
      if (at >= 0) {
        insertAt = at + 1;
        break;
      }
    }
    result.splice(insertAt, 0, key);
  });
  return result;
};

export const moveColumn = (
  order: MetricColumnKey[],
  dragged: MetricColumnKey,
  target: MetricColumnKey,
  side: 'before' | 'after'
): MetricColumnKey[] => {
  if (dragged === target || !order.includes(dragged) || !order.includes(target)) return order;
  const without = order.filter((key) => key !== dragged);
  const targetIdx = without.indexOf(target);
  without.splice(side === 'before' ? targetIdx : targetIdx + 1, 0, dragged);
  return without;
};

// Column keys that column-header sorting can target. The identity column's key varies by view
// (Vendor / Category / Class), everything else is a shared metric name common to every row/group
// shape this report renders (VendorRowData, CategoryVendorItem, ClassVendorItem all share these
// field names, so one comparator works for all of them).
type SortableMetricKey =
  | 'vendor' | 'category' | 'm1_class_name' | 'location_name'
  | 'sales_dollars' | 'sales_units' | 'inventory_oh_dollars' | 'inventory_oh_units'
  | 'on_order_dollars' | 'on_order_units' | 'past_due_on_order_dollars' | 'past_due_on_order_units'
  | 'inflow_dollars' | 'cost_of_purchase_pct' | 'gross_margin_pct'
  | 'maintained_margin_pct' | 'markdown_dollars' | 'markdown_pct' | 'sell_through_pct' | 'sell_through_start_inv_pct' | 'annual_turn_rate' | 'rtv_dollars'
  | 'avg_selling_price' | 'avg_purchase_price' | 'leaf_class_names';

interface SortConfig {
  key: SortableMetricKey;
  direction: 'asc' | 'desc';
}

const compareForSort = (aVal: unknown, bVal: unknown, direction: 'asc' | 'desc'): number => {
  const result =
    typeof aVal === 'string' || typeof bVal === 'string'
      ? String(aVal ?? '').localeCompare(String(bVal ?? ''))
      : (Number(aVal) || 0) - (Number(bVal) || 0);
  return direction === 'asc' ? result : -result;
};

// Generic over any of the row/group shapes this report sorts (VendorRowData, CategoryVendorItem,
// ClassVendorItem, CategoryGroupData, ClassGroupData) -- none of them declare an index signature,
// so the field lookup is cast to Record<string, unknown> inside the function body rather than
// constraining T itself, which would otherwise reject every one of those concrete interfaces at
// the call site.
function sortByConfig<T>(items: T[], config: SortConfig | null): T[] {
  if (!config) return items;
  return [...items].sort((a, b) => {
    const aRow = a as Record<string, unknown>;
    const bRow = b as Record<string, unknown>;
    return compareForSort(aRow[config.key], bRow[config.key], config.direction);
  });
}

// Shared sortable <th> content -- a clickable label that shows a neutral icon until its column is
// the active sort, then swaps to a direction-specific chevron. Used by every column header across
// all 5 views so sorting behaves identically everywhere instead of being reimplemented per-header.
// Looks up a column's definition text from the single source of truth (COLUMN_TOGGLE_LIST) so a
// header's tooltip and its entry in the Columns popover can never drift out of sync.
const getColumnDescription = (key: string): string | undefined =>
  COLUMN_TOGGLE_LIST.find((col) => col.key === key)?.description;

// Shared visual styling for every tooltip bubble in this report -- both the column-header
// tooltips (SortableHeader) and the filter-bar toggle tooltips (InfoTooltip) apply this on top of
// their own positioning classes, so the two can never visually drift apart. Position (top-full vs
// bottom-full, left-0/right-0 vs centered), width, and text-right (numeric-column tooltips only)
// stay per-usage since those are legitimately context-specific.
// whitespace-normal break-words is explicit here, not left to the browser default, because
// SortableHeader's own wrapping <div> sets whitespace-nowrap (for its label + sort-icon row) and
// the tooltip <div> is nested inside it -- white-space is inherited, so without overriding it here
// a long description would inherit nowrap and get cut off against the fixed w-56 width instead of
// wrapping onto a second line.
const TOOLTIP_BUBBLE_CSS =
  'pointer-events-none z-50 rounded-lg bg-[#101828] px-2.5 py-2 text-[11px] font-medium normal-case leading-snug text-white shadow-lg transition-opacity whitespace-normal break-words';

// Clicking the label opens the column's menu (sort, pin, hide) rather than sorting directly.
const SortableHeader: React.FC<{
  label: string;
  sortKey: SortableMetricKey;
  sortConfig: SortConfig | null;
  isMenuOpen: boolean;
  onToggleMenu: () => void;
  menu?: React.ReactNode;
  align?: 'left' | 'right' | 'center';
  description?: string;
  isLastColumn?: boolean;
}> = ({ label, sortKey, sortConfig, isMenuOpen, onToggleMenu, menu, align = 'right', description, isLastColumn = false }) => {
  const isActive = sortConfig?.key === sortKey;
  const sortIcon = isActive
    ? (sortConfig!.direction === 'asc' ? <ChevronUp className="w-3 h-3 shrink-0" /> : <ChevronDown className="w-3 h-3 shrink-0" />)
    : <ArrowUpDown className="w-3 h-3 text-[#94A3B8] shrink-0" />;
  const justify = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';
  const textAlignment = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  // Tooltip visibility is driven by real React state, not a Tailwind group-hover variant -- this
  // project's postcss.config.js runs the Tailwind v4 engine (@tailwindcss/postcss) even though
  // tailwind.config.js/package.json are set up for v3, and that mismatch was confirmed (via the
  // compiled build output) to silently drop specific group-hover combinations, including this
  // one, with no build warning. State-driven visibility can't be affected by that gap.
  const [isHovered, setIsHovered] = useState<boolean>(false);

  // If last column near table edge, align tooltip right; otherwise open to the right (left-0)
  // so it never extends behind the sticky first column or previous columns.
  const tooltipPlacement = isLastColumn ? 'right-0' : 'left-0';

  return (
    <div
      className={`relative flex items-center ${justify} gap-1.5 w-full hover:z-50`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-column-menu
    >
      {description && (
        <div className="relative inline-flex items-center shrink-0">
          <Info className="w-3 h-3 text-[#94A3B8] shrink-0 cursor-help" aria-hidden="true" />
          {isHovered && !isMenuOpen && (
            <div
              role="tooltip"
              className={`${TOOLTIP_BUBBLE_CSS} absolute ${tooltipPlacement} top-full mt-1.5 w-56 text-left opacity-100 z-50`}
            >
              {description}
            </div>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={onToggleMenu}
        aria-haspopup="menu"
        aria-expanded={isMenuOpen}
        className={`flex items-center ${justify} gap-1 hover:text-[#09090B] transition-colors cursor-pointer ${textAlignment} ${isActive || isMenuOpen ? 'text-[#09090B]' : ''}`}
      >
        <span className={`whitespace-normal break-words leading-tight ${textAlignment}`}>{label}</span>
        {sortIcon}
      </button>
      {isMenuOpen && menu}
    </div>
  );
};

// Small info-icon tooltip used next to the YOY Comparison / Show % change toggle labels. Shares
// TOOLTIP_BUBBLE_CSS with SortableHeader's column-header tooltip above so the two stay visually
// identical; only position (bubble sits above, centered, vs. below and edge-aligned) and width
// differ, since those are legitimately specific to each anchor's shape and location on the page.
// Visibility is driven by real React state, not a Tailwind group-hover variant -- same reason as
// SortableHeader's tooltip above: this project's compiled build has been confirmed to silently
// drop specific group-hover combinations, so state-driven visibility is the only reliable option.
const InfoTooltip: React.FC<{ text: string; widthClass?: string; positionClass?: string }> = ({
  text,
  widthClass = 'w-48',
  positionClass = 'bottom-full left-1/2 -translate-x-1/2 mb-1.5',
}) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  return (
    <div
      className="relative flex items-center cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Info className="w-3.5 h-3.5 text-[#98A2B3] hover:text-[#667085]" />
      <div
        className={`${TOOLTIP_BUBBLE_CSS} absolute ${positionClass} ${widthClass} ${isHovered ? 'opacity-100' : 'opacity-0'}`}
      >
        {text}
      </div>
    </div>
  );
};

export const VendorPerformanceReport: React.FC<VendorPerformanceReportProps> = ({ clientId = '' }) => {
  const globalTip = TipContext.useMessage();
  // useMessage() isn't referentially stable, so the fetch effect reads it through a ref instead of a dependency.
  const globalTipRef = useRef(globalTip);
  globalTipRef.current = globalTip;

  const auth = AuthProvider.useAuth() as { user?: { id?: string | number } | null } | null;
  const userId = auth?.user?.id ?? null;
  const [activeView, setActiveView] = useState<VendorView>('total_by_vendor');
  const { widthOf, isResizing, getResizeHandleProps, resetColumnWidths } = useResizableColumns(
    clientId ? getColumnWidthsStorageKey(userId, clientId) : null,
    getDefaultColumnWidths(activeView),
    METRIC_COLUMN_WIDTH
  );
  const selectedViewConfig = useMemo(
    () => VIEW_OPTIONS.find((v) => v.key === activeView),
    [activeView]
  );
  // Price Basis: 'retail' (default) or 'cost' for OTB, On-Order, and Planned figures.
  const [priceBasis, setPriceBasis] = useState<'retail' | 'cost'>('retail');
  // Defaults off: a fresh page load should show a clean, single-period table -- not immediately
  // triple every row into This Period/Last Year/Change before the user has asked for a comparison.
  const [showYOY, setShowYOY] = useState<boolean>(false);
  // Only meaningful when showYOY is on -- switches the CHANGE row's $/unit columns (Sales $,
  // Inventory OH $, On-Order $, Inflow $, Markdown $, RTV $ and their unit counterparts) between
  // an absolute difference and a percent-change value. Percentage-point columns (margins,
  // markdown %, sell-through %) already show a pts diff regardless of this toggle.
  const [showPctChangeDiff, setShowPctChangeDiff] = useState<boolean>(false);
  const [isColumnsPopoverOpen, setIsColumnsPopoverOpen] = useState<boolean>(false);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  // Set from a column header's menu ("Sort high to low" / "Sort low to high").
  const handleSort = (key: SortableMetricKey, direction: ColumnSortDirection) => {
    setSortConfig({ key, direction });
  };

  // Key of the column whose header menu is open ('label' for the sticky first column); one at a time.
  const [activeMenuColumn, setActiveMenuColumn] = useState<string | null>(null);
  const toggleColumnMenu = (key: string) => setActiveMenuColumn((open) => (open === key ? null : key));
  const closeColumnMenu = () => setActiveMenuColumn(null);

  // Custom date range (calendar popover) -- see the Date range filter's "Custom range..." option.
  // `customDateRange` is the committed selection resolveDateRange() actually uses; it always
  // overrides selectedDateRange's preset matching when set, and is cleared whenever a preset is
  // picked instead. `pendingCustomRange`/`showCustomCalendar` are the popover's own in-progress
  // UI state, discarded on Cancel.
  const [customDateRange, setCustomDateRange] = useState<{ from: Date; to: Date } | undefined>(undefined);
  const [showCustomCalendar, setShowCustomCalendar] = useState<boolean>(false);
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');

  const [isSaveModalOpen, setIsSaveModalOpen] = useState<boolean>(false);
  const [saveViewName, setSaveViewName] = useState<string>('');
  const [saveVisibility, setSaveVisibility] = useState<SavedViewVisibility>('only_me');
  const [isSavingView, setIsSavingView] = useState<boolean>(false);

  const {
    views: savedViews,
    saveView,
    deleteView,
    isFavorite: isFavoriteView,
    toggleFavorite: handleToggleFavorite,
  } = useSavedViews<VendorPerformanceViewPayload>({
    reportKey: 'vendor_performance',
    clientId,
    userId,
    storagePrefix: SAVED_VIEWS_STORAGE_PREFIX,
    readLocalViews: () => (clientId ? loadLocalSavedViews(userId, clientId) : []),
  });
  const [isSavedViewsMenuOpen, setIsSavedViewsMenuOpen] = useState<boolean>(false);

  const [activeFilterPopover, setActiveFilterPopover] = useState<string | null>(null);

  const [selectedDateRange, setSelectedDateRange] = useState<string>('Last 3 Months');

  // Location Filter State -- selectedLocations holds `locationCode` values (matching
  // Reporting.HIERARCHY.locationCode, the same code the vendor-performance API's location_codes
  // filter expects), not display names. Options are fetched below via the same
  // /api/clients/{clientId}/dropdown-options endpoint the Simple Dashboard v3 filter bar uses, so
  // this report doesn't need its own separate location/class lookup.
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [excludeLocation, setExcludeLocation] = useState<boolean>(false);
  // Client-side search box inside the Location popover, to narrow locations list.
  const [locationFilterQuery, setLocationFilterQuery] = useState<string>('');

  // Class Filter State -- selectedClasses holds `classCode` values (matching m1_class_code / API's
  // class_codes filter), not display names. See selectedLocations above.
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [excludeClass, setExcludeClass] = useState<boolean>(false);
  // Client-side search box inside the Class popover, to narrow a long class list -- filters
  // classOptions by name only, never touches the API request.
  const [classFilterQuery, setClassFilterQuery] = useState<string>('');

  // Category Filter State -- category already matches the API 1:1 as plain text (HIERARCHY.category),
  // no code/id indirection needed.
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [excludeCategory, setExcludeCategory] = useState<boolean>(false);
  // Client-side search box inside the Category popover, same purpose as classFilterQuery above.
  const [categoryFilterQuery, setCategoryFilterQuery] = useState<string>('');

  // Vendor Filter State -- vendor names exactly as the rows' `vendor` (the API's vendor_ids filter
  // matches d.vendor), so subtotals, footer and % of total all come back scoped to the selection.
  const [selectedVendors, setSelectedVendors] = useState<string[]>([]);
  const [excludeVendor, setExcludeVendor] = useState<boolean>(false);
  const [vendorFilterQuery, setVendorFilterQuery] = useState<string>('');

  // The selected*/exclude* states above are what's applied (and fetched); each popover edits a draft
  // that commits when it closes, so ticking boxes doesn't re-run the report query per tick.
  const locationDraft = usePendingFilterSelection(
    { codes: selectedLocations, exclude: excludeLocation },
    activeFilterPopover === 'location',
    (next) => {
      setSelectedLocations(next.codes);
      setExcludeLocation(next.exclude);
    }
  );
  const classDraft = usePendingFilterSelection(
    { codes: selectedClasses, exclude: excludeClass },
    activeFilterPopover === 'class',
    (next) => {
      setSelectedClasses(next.codes);
      setExcludeClass(next.exclude);
    }
  );
  const categoryDraft = usePendingFilterSelection(
    { codes: selectedCategories, exclude: excludeCategory },
    activeFilterPopover === 'category',
    (next) => {
      setSelectedCategories(next.codes);
      setExcludeCategory(next.exclude);
    }
  );
  const vendorDraft = usePendingFilterSelection(
    { codes: selectedVendors, exclude: excludeVendor },
    activeFilterPopover === 'vendor',
    (next) => {
      setSelectedVendors(next.codes);
      setExcludeVendor(next.exclude);
    }
  );

  // Location/Class/Category dropdown options, fetched per-client (real codes, not a hardcoded
  // list). Same endpoint/shape already used by the Simple Dashboard v3 filter bar
  // (PerformanceDashboardService.getDropdownOptions -> ClientRepository::getDropdownOptions),
  // which already returns a `categories` array alongside locations/classes -- `code` and `name`
  // are the same string for a category (both are the raw HIERARCHY.category value), matching
  // selectedCategories already storing plain category text, not a separate code.
  const [locationOptions, setLocationOptions] = useState<DashboardFilterOption[]>([]);
  const [classOptions, setClassOptions] = useState<DashboardFilterOption[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<DashboardFilterOption[]>([]);
  // The client's full vendor list, loaded once per client -- not built from the rows in view.
  const [vendorOptions, setVendorOptions] = useState<string[]>([]);
  const visibleVendorOptions = vendorOptions.filter((v) => v.toLowerCase().includes(vendorFilterQuery.trim().toLowerCase()));

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    COLUMN_TOGGLE_LIST.forEach((col) => {
      initial[col.key] = col.defaultVisible;
    });
    return initial;
  });

  const [columnOrder, setColumnOrder] = useState<MetricColumnKey[]>(DEFAULT_COLUMN_ORDER);
  // Columns pinned from a header menu sit at the front of columnOrder, in the order they were pinned.
  const { pinnedColumns, isPinned, setPinnedColumns, clearPinnedColumns } = usePinnedColumns(
    clientId ? getPinnedColumnsStorageKey(userId, clientId) : null,
    DEFAULT_COLUMN_ORDER
  );
  const [draggedColumn, setDraggedColumn] = useState<MetricColumnKey | null>(null);
  const [dropTarget, setDropTarget] = useState<{ key: MetricColumnKey; side: 'before' | 'after' } | null>(null);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const { scrollRef, theadRef, registerGroupBody, updateScrollState, stickyTop, headShadow, footShadow, groupShadow } = useStickyTableRows();

  const [minSales, setMinSales] = useState<string>('none');

  // On-Order Span -- how far into the future an open PO's expected delivery can fall and still
  // count toward On-Order $/Units.
  const [onOrderSpan, setOnOrderSpan] = useState<string>('3_months');
  const [onOrderStartDate, setOnOrderStartDate] = useState<string>('');
  const [onOrderEndDate, setOnOrderEndDate] = useState<string>('');
  const [showCustomOnOrderSpan, setShowCustomOnOrderSpan] = useState<boolean>(false);
  const [draftOnOrderStart, setDraftOnOrderStart] = useState<string>('');
  const [draftOnOrderEnd, setDraftOnOrderEnd] = useState<string>('');

  // apiRows holds the flat rows for whichever groupBy the current activeView needs; they're
  // reshaped client-side below into whatever local shape each view's JSX expects.
  const [apiRows, setApiRows] = useState<VendorApiRow[]>([]);
  const [apiTotals, setApiTotals] = useState<Partial<VendorApiRow> | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Expand / collapse states for the Location > Class > Vendor 3-level view.
  // Stored as collapsed-state sets: empty object means everything starts expanded by default.
  const [collapsedLocations, setCollapsedLocations] = useState<Record<string, boolean>>({});
  const [collapsedLocationClasses, setCollapsedLocationClasses] = useState<Record<string, boolean>>({});

  const toggleLocation = (locId: string) => {
    setCollapsedLocations((prev) => ({ ...prev, [locId]: !prev[locId] }));
  };

  const toggleLocationClass = (classId: string) => {
    setCollapsedLocationClasses((prev) => ({ ...prev, [classId]: !prev[classId] }));
  };

  const toggleColumnVisibility = (key: string) => {
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    setColumnOrder(clientId ? loadColumnOrder(userId, clientId) : DEFAULT_COLUMN_ORDER);
  }, [userId, clientId]);

  const handleSaveView = async () => {
    if (isSavingView) return;
    const name = saveViewName.trim() || 'Untitled view';
    const payload: VendorPerformanceViewPayload = {
      view: activeView,
      filters: {
        selectedDateRange,
        selectedLocations,
        selectedClasses,
        selectedCategories,
        selectedVendors,
        excludeVendor,
        minSales,
        showYOY,
      },
      visibleColumns,
      sortConfig,
      onOrderSpan,
      onOrderStartDate,
      onOrderEndDate,
      priceBasis,
      showPctChangeDiff,
      columnOrder,
    };

    setIsSavingView(true);
    try {
      await saveView({ name, visibility: saveVisibility, payload });
      setIsSaveModalOpen(false);
      setSaveViewName('');
      setSaveVisibility('only_me');
      if (globalTip) globalTip.showSuccess(`View "${name}" saved`);
    } catch (error) {
      console.error('Failed to save vendor performance view', error);
      if (globalTip) globalTip.show('Could not save this view. Please try again.');
    } finally {
      setIsSavingView(false);
    }
  };

  const handleLoadSavedView = (savedView: VendorPerformanceViewPayload) => {
    setActiveView(savedView.view);
    setSelectedDateRange(savedView.filters.selectedDateRange);
    // The actual custom-range Date objects are never saved with a view (only the formatted
    // label is) -- clear any currently-active one so resolveDateRange falls back to
    // re-matching the loaded label against the preset switch, instead of silently keeping
    // whatever custom range happened to be active in the browser at load time.
    setCustomDateRange(undefined);
    setSelectedLocations(savedView.filters.selectedLocations);
    setSelectedClasses(savedView.filters.selectedClasses);
    setSelectedCategories(savedView.filters.selectedCategories);
    // Views saved before the Vendor filter existed carry neither field -- load them as "all vendors".
    setSelectedVendors(savedView.filters.selectedVendors ?? []);
    setExcludeVendor(savedView.filters.excludeVendor ?? false);
    setMinSales(savedView.filters.minSales);
    setShowYOY(savedView.filters.showYOY);
    setShowPctChangeDiff(savedView.showPctChangeDiff ?? false);
    // Defaults first, so columns added after the view was saved aren't hidden.
    const defaults: Record<string, boolean> = {};
    COLUMN_TOGGLE_LIST.forEach((col) => {
      defaults[col.key] = col.defaultVisible;
    });
    setVisibleColumns({ ...defaults, ...savedView.visibleColumns });
    setSortConfig(savedView.sortConfig ?? null);
    setOnOrderSpan(savedView.onOrderSpan ?? '3_months');
    setOnOrderStartDate(savedView.onOrderStartDate ?? '');
    setOnOrderEndDate(savedView.onOrderEndDate ?? '');
    setShowCustomOnOrderSpan(false);
    setPriceBasis(savedView.priceBasis ?? 'retail');
    if (savedView.columnOrder) {
      // The saved order defines the layout; pins from the current session would no longer hold.
      updateColumnOrder(normalizeColumnOrder(savedView.columnOrder));
      clearPinnedColumns();
    }
    setIsSavedViewsMenuOpen(false);
  };

  const handleDeleteSavedView = async (savedView: SavedViewRecord<VendorPerformanceViewPayload>) => {
    try {
      await deleteView(savedView.id);
    } catch (error) {
      console.error('Failed to delete vendor performance view', error);
      if (globalTip) globalTip.show(`Could not delete "${savedView.name}". Please try again.`);
    }
  };

  // Favorites float to the top of the Saved Views menu; ties keep their original save order.
  const sortedSavedViews = useMemo(
    () => [...savedViews].sort((a, b) => (isFavoriteView(b.id) ? 1 : 0) - (isFavoriteView(a.id) ? 1 : 0)),
    [savedViews, isFavoriteView]
  );

  // Populate Location/Class/Category filter dropdowns with real, client-scoped values -- reuses
  // the same /api/clients/{clientId}/dropdown-options endpoint the Simple Dashboard v3 filter bar
  // already uses (PerformanceDashboardService.getDropdownOptions), instead of a report-specific
  // lookup. `code` here is `locationCode`/`classCode` from Reporting.HIERARCHY for locations and
  // classes -- exactly what the vendor-performance API's location_codes/class_codes filters
  // expect. For categories, `code` and `name` are both the same raw `HIERARCHY.category` string
  // (see GetDropdownOptions.sql), already sorted by categorySortOrder server-side.
  useEffect(() => {
    if (!clientId) {
      setLocationOptions([]);
      setClassOptions([]);
      setCategoryOptions([]);
      setVendorOptions([]);
      return;
    }

    let cancelled = false;
    PerformanceDashboardService.getDropdownOptions({
      clientId,
      epochMonth: EpochMonthService.getTodayEpochMonth(),
    }).then((data: { locations?: DashboardFilterOption[]; classes?: DashboardFilterOption[]; categories?: DashboardFilterOption[] } | undefined) => {
      if (cancelled) return;
      setLocationOptions(Array.isArray(data?.locations) ? data!.locations! : []);
      setClassOptions(Array.isArray(data?.classes) ? data!.classes! : []);
      setCategoryOptions(Array.isArray(data?.categories) ? data!.categories! : []);
    }).catch((error: unknown) => {
      if (!cancelled) console.error('Failed to fetch vendor performance filter options', error);
    });

    ReportService.getVendorPerformanceVendorOptions(clientId).then((vendors) => {
      if (!cancelled) setVendorOptions(vendors);
    });

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // Close open popovers and menus when clicking anywhere outside
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-popover-container]')) {
        setActiveFilterPopover(null);
        setIsColumnsPopoverOpen(false);
        setIsSavedViewsMenuOpen(false);
      }
      if (!target.closest('[data-column-menu]')) {
        setActiveMenuColumn(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      const { from, to } = resolveDateRange(selectedDateRange, customDateRange);
      const groupBy = VIEW_TO_GROUP_BY[activeView];
      const categoryIds = selectedCategories.length > 0 ? selectedCategories : undefined;
      const locationCodes = selectedLocations.length > 0 ? selectedLocations : undefined;
      const classCodes = selectedClasses.length > 0 ? selectedClasses : undefined;
      const vendorIds = selectedVendors.length > 0 ? selectedVendors : undefined;
      const minSalesValue = minSales === 'none' ? null : Number(minSales);

      const response: VendorApiResponse | undefined = await ReportService.getVendorPerformanceReportData({
        clientId,
        from,
        to,
        groupBy,
        categoryIds,
        locationCodes,
        classCodes,
        vendorIds,
        excludeLocations: excludeLocation,
        excludeClasses: excludeClass,
        excludeCategories: excludeCategory,
        excludeVendors: excludeVendor,
        minSales: minSalesValue,
        includeLy: showYOY,
        onOrderSpan,
        onOrderStartDate: onOrderSpan === 'custom' ? onOrderStartDate : undefined,
        onOrderEndDate: onOrderSpan === 'custom' ? onOrderEndDate : undefined,
      });

      if (cancelled) return;

      if (response && Array.isArray(response.rows)) {
        setApiRows(response.rows);
        setApiTotals(response.totals ?? null);
      } else {
        setApiRows([]);
        setApiTotals(null);
        globalTipRef.current?.show('Failed to fetch vendor performance data');
      }
      setLoading(false);
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [clientId, activeView, selectedDateRange, customDateRange, selectedCategories, selectedLocations, selectedClasses, selectedVendors, excludeLocation, excludeClass, excludeCategory, excludeVendor, minSales, showYOY, onOrderSpan, onOrderStartDate, onOrderEndDate]);

  // The fetched rows with On-Order converted to the selected price basis -- every grouping,
  // subtotal and pill below reads these, never apiRows directly.
  const basisRows = useMemo(
    () => apiRows.map((row) => toPriceBasisRow(row, priceBasis)),
    [apiRows, priceBasis]
  );

  // Reshape the fetched flat rows into the vendor-top-level shape used by Total by Vendor,
  // Category by Vendor (vendor row + nested category breakdown) and Vendor by Class
  // (vendor row + nested class breakdown).
  const vendorRows: VendorRowData[] = useMemo(() => {
    const isCost = priceBasis === 'cost';
    if (activeView === 'total_by_vendor') {
      return basisRows.map((row) => mapToVendorRowData(row));
    }
    if (activeView === 'category_by_vendor') {
      const grouped = groupRowsBy(basisRows, (r) => r.vendor);
      return Array.from(grouped.entries()).map(([vendor, rows]) => {
        const distinctCategories = new Map<string, VendorApiRow>();
        rows.forEach((r) => {
          const cat = (r.category ?? '').trim();
          if (cat && !distinctCategories.has(cat)) {
            distinctCategories.set(cat, r);
          }
        });

        let otbDollars = 0;
        let planSales = 0;
        let planInflow = 0;
        let hasOtbData = false;

        distinctCategories.forEach((r) => {
          const otb = isCost ? r.category_otb_cost : r.category_otb_retail;
          const ps = isCost ? r.category_plan_sales_cost : r.category_plan_sales_retail;
          const pi = isCost ? r.category_plan_inflow_cost : r.category_plan_inflow_retail;
          if (otb != null || ps != null || pi != null) {
            hasOtbData = true;
            otbDollars += Number(otb || 0);
            planSales += Number(ps || 0);
            planInflow += Number(pi || 0);
          }
        });

        return {
          id: vendor,
          vendor,
          has_alert: false, // no confirmed trigger condition yet
          otb_dollars: hasOtbData ? otbDollars : null,
          plan_sales: hasOtbData ? planSales : null,
          plan_inflow: hasOtbData ? planInflow : null,
          ...sumVendorMetrics(rows),
          categories: rows.map((r) => mapToCategoryBreakdownItem(r, isCost)),
          classes: [],
        };
      });
    }
    if (activeView === 'vendor_by_class') {
      const grouped = groupRowsBy(basisRows, (r) => r.vendor);
      return Array.from(grouped.entries()).map(([vendor, rows]) => ({
        id: vendor,
        vendor,
        has_alert: false, // no confirmed trigger condition yet
        ...sumVendorMetrics(rows),
        categories: [],
        classes: rows.map(mapToClassBreakdownItem),
      }));
    }
    return [];
  }, [activeView, basisRows, priceBasis]);

  // Vendor by Category: group flat rows by category into top-level groups containing vendors.
  const categoryGroups: CategoryGroupData[] = useMemo(() => {
    if (activeView !== 'vendor_by_category') return [];
    const isCost = priceBasis === 'cost';
    const grouped = groupRowsBy(basisRows, (r) => r.category ?? '');
    return Array.from(grouped.entries()).map(([category, rows]) => {
      const firstRow = rows[0];
      const otbDollars = isCost ? firstRow?.category_otb_cost : firstRow?.category_otb_retail;
      const planSales = isCost ? firstRow?.category_plan_sales_cost : firstRow?.category_plan_sales_retail;
      const planInflow = isCost ? firstRow?.category_plan_inflow_cost : firstRow?.category_plan_inflow_retail;
      const planOnOrder = isCost ? firstRow?.category_on_order_cost : firstRow?.category_on_order_retail;

      return {
        id: category,
        category,
        vendors: rows.map(mapToCategoryVendorItem),
        otb_dollars: otbDollars != null ? otbDollars : null,
        plan_sales: planSales != null ? planSales : null,
        plan_inflow: planInflow != null ? planInflow : null,
        plan_on_order: planOnOrder ?? null,
      };
    });
  }, [activeView, basisRows, priceBasis]);

  // Class by Vendor: group flat rows by class into top-level groups containing vendors.
  const classGroups: ClassGroupData[] = useMemo(() => {
    if (activeView !== 'class_by_vendor') return [];
    // By the plan class code, as the server groups (the name only stands in for a row with no code).
    const grouped = groupRowsBy(basisRows, (r) => r.m1_class_code || r.m1_class_name || '');
    return Array.from(grouped.entries()).map(([classKey, rows]) => {
      const firstRow = rows[0];
      const className = firstRow?.m1_class_name ?? classKey;
      const isCost = priceBasis === 'cost';
      const otbDollars = isCost ? firstRow?.class_otb_cost : firstRow?.class_otb_retail;
      const planSales = isCost ? firstRow?.class_plan_sales_cost : firstRow?.class_plan_sales_retail;
      const planInflow = isCost ? firstRow?.class_plan_inflow_cost : firstRow?.class_plan_inflow_retail;
      const planOnOrder = isCost ? firstRow?.class_on_order_cost : firstRow?.class_on_order_retail;

      return {
        id: classKey,
        m1_class_name: className,
        m1_class_code: firstRow?.m1_class_code ?? '',
        vendors: rows.map(mapToClassVendorItem),
        otb_dollars: otbDollars != null ? otbDollars : null,
        plan_sales: planSales != null ? planSales : null,
        plan_inflow: planInflow != null ? planInflow : null,
        plan_on_order: planOnOrder ?? null,
      };
    });
  }, [activeView, basisRows, priceBasis]);

  // Filter Data (search box, applied on top of the real fetched/reshaped data). Matches vendor
  // name OR the row's category/class -- no tag field exists anywhere in this report's data
  // (VendorApiRow has none), so tag search isn't offered; a real tag search needs a real tag
  // data source added upstream first.
  const filteredData = useMemo(() => {
    const searched = vendorRows.filter((item) => {
      if (searchQuery.trim() === '') return true;
      const query = searchQuery.toLowerCase();
      return (
        item.vendor.toLowerCase().includes(query) ||
        !!item.category?.toLowerCase().includes(query) ||
        !!item.m1_class_name?.toLowerCase().includes(query)
      );
    });
    return sortByConfig(searched, sortConfig);
  }, [vendorRows, searchQuery, sortConfig]);

  // Filter Vendor by Category Data. Sorting by the identity column ("Category") reorders the
  // groups themselves; sorting by any metric column reorders the vendors within each group.
  const filteredVendorByCategoryData = useMemo(() => {
    const searched = categoryGroups.map((group) => {
      if (searchQuery.trim() === '') return group;
      const query = searchQuery.toLowerCase();
      const matchedVendors = group.vendors.filter(
        (v) =>
          v.vendor.toLowerCase().includes(query) ||
          group.category.toLowerCase().includes(query)
      );
      return { ...group, vendors: matchedVendors };
    }).filter((group) => group.vendors.length > 0);

    const withVendorsSorted = sortConfig && sortConfig.key !== 'category'
      ? searched.map((group) => ({ ...group, vendors: sortByConfig(group.vendors, sortConfig) }))
      : searched;

    return sortConfig?.key === 'category'
      ? sortByConfig(withVendorsSorted, sortConfig)
      : withVendorsSorted;
  }, [categoryGroups, searchQuery, sortConfig]);

  // Filter Class by Vendor Data. Same rule as above: identity column ("Class") sorts the groups,
  // any metric column sorts the vendors within each group.
  const filteredClassByVendorData = useMemo(() => {
    const searched = classGroups.map((group) => {
      if (searchQuery.trim() === '') return group;
      const query = searchQuery.toLowerCase();
      const matchedVendors = group.vendors.filter(
        (v) =>
          v.vendor.toLowerCase().includes(query) ||
          group.m1_class_name.toLowerCase().includes(query)
      );
      return { ...group, vendors: matchedVendors };
    }).filter((group) => group.vendors.length > 0);

    const withVendorsSorted = sortConfig && sortConfig.key !== 'm1_class_name'
      ? searched.map((group) => ({ ...group, vendors: sortByConfig(group.vendors, sortConfig) }))
      : searched;

    return sortConfig?.key === 'm1_class_name'
      ? sortByConfig(withVendorsSorted, sortConfig)
      : withVendorsSorted;
  }, [classGroups, searchQuery, sortConfig]);

  // Location > Class > Vendor: group flat rows by location, then by class into groups containing vendors.
  const locationGroups: LocationClassVendorGroup[] = useMemo(() => {
    if (activeView !== 'location_class_vendor') return [];
    const isCost = priceBasis === 'cost';

    const byLocation = groupRowsBy(basisRows, (r) => r.location_code || r.location_name || 'Unknown');

    return Array.from(byLocation.entries()).map(([locKey, locRows]) => {
      const locName = locRows.find((r) => r.location_name)?.location_name || locRows[0]?.location_name || locKey;
      const locCode = locRows.find((r) => r.location_code)?.location_code || locKey;

      const locOtbRow = locRows.find((r) => r.location_otb_retail != null || r.location_otb_cost != null) ?? locRows[0];
      const locOtbDollars = isCost ? locOtbRow?.location_otb_cost : locOtbRow?.location_otb_retail;
      const locPlanSales = isCost ? locOtbRow?.location_plan_sales_cost : locOtbRow?.location_plan_sales_retail;
      const locPlanInflow = isCost ? locOtbRow?.location_plan_inflow_cost : locOtbRow?.location_plan_inflow_retail;
      const locPlanOnOrder = isCost ? locOtbRow?.location_on_order_cost : locOtbRow?.location_on_order_retail;

      const byClass = groupRowsBy(locRows, (r) => r.m1_class_code || r.m1_class_name || 'Unassigned');

      const classes: ClassGroupData[] = Array.from(byClass.entries()).map(([classKey, classRows]) => {
        const className = classRows.find((r) => r.m1_class_name)?.m1_class_name || classKey;
        const classOtbRow = classRows.find((r) => r.class_otb_retail != null || r.class_otb_cost != null) ?? classRows[0];
        const classOtbDollars = isCost ? classOtbRow?.class_otb_cost : classOtbRow?.class_otb_retail;
        const classPlanSales = isCost ? classOtbRow?.class_plan_sales_cost : classOtbRow?.class_plan_sales_retail;
        const classPlanInflow = isCost ? classOtbRow?.class_plan_inflow_cost : classOtbRow?.class_plan_inflow_retail;
        const classPlanOnOrder = isCost ? classOtbRow?.class_on_order_cost : classOtbRow?.class_on_order_retail;
        const classCode = classRows.find((r) => r.m1_class_code)?.m1_class_code ?? classOtbRow?.m1_class_code ?? '';

        return {
          id: `${locKey}_${classKey}`,
          m1_class_name: className,
          m1_class_code: classCode,
          vendors: classRows.map((row) => mapToLocationClassVendorItem(locKey, classKey, row)),
          otb_dollars: classOtbDollars != null ? classOtbDollars : null,
          plan_sales: classPlanSales != null ? classPlanSales : null,
          plan_inflow: classPlanInflow != null ? classPlanInflow : null,
          plan_on_order: classPlanOnOrder ?? null,
        };
      });

      return {
        id: locKey,
        location_code: locCode,
        location_name: locName,
        otb_dollars: locOtbDollars != null ? locOtbDollars : null,
        plan_sales: locPlanSales != null ? locPlanSales : null,
        plan_inflow: locPlanInflow != null ? locPlanInflow : null,
        plan_on_order: locPlanOnOrder ?? null,
        classes,
      };
    });
  }, [activeView, basisRows, priceBasis]);

  // Filter Location > Class > Vendor Data
  const filteredLocationClassVendorData = useMemo(() => {
    if (activeView !== 'location_class_vendor') return [];

    const query = searchQuery.trim().toLowerCase();

    const searched = locationGroups
      .map((loc) => {
        const matchedClasses = loc.classes
          .map((cls) => {
            if (!query) return cls;
            const matchedVendors = cls.vendors.filter(
              (v) =>
                v.vendor.toLowerCase().includes(query) ||
                cls.m1_class_name.toLowerCase().includes(query) ||
                loc.location_name.toLowerCase().includes(query)
            );
            return { ...cls, vendors: matchedVendors };
          })
          .filter(
            (cls) =>
              query === '' ||
              cls.vendors.length > 0 ||
              cls.m1_class_name.toLowerCase().includes(query)
          );

        return { ...loc, classes: matchedClasses };
      })
      .filter(
        (loc) =>
          query === '' ||
          loc.classes.some((c) => c.vendors.length > 0) ||
          loc.location_name.toLowerCase().includes(query)
      );

    if (!sortConfig) return searched;

    if (sortConfig.key === 'location_name') {
      return sortByConfig(searched, sortConfig);
    }

    return searched.map((loc) => {
      let sortedClasses = loc.classes;
      if (sortConfig.key === 'm1_class_name') {
        sortedClasses = sortByConfig(loc.classes, sortConfig);
      } else {
        sortedClasses = loc.classes.map((cls) => ({
          ...cls,
          vendors: sortByConfig(cls.vendors, sortConfig),
        }));
      }
      return { ...loc, classes: sortedClasses };
    });
  }, [locationGroups, activeView, searchQuery, sortConfig]);

  const overallTotals = useMemo(() => {
    let sales = 0;
    let inv = 0;
    let salesLy = 0;
    let invLy = 0;
    let inflow = 0;
    let inflowLy = 0;
    let onOrder = 0;
    let onOrderLy = 0;

    if (apiTotals?.sales_dollars != null && Number(apiTotals.sales_dollars) > 0) {
      sales = Number(apiTotals.sales_dollars);
      inv = Number(apiTotals.inventory_oh_dollars) || 0;
      salesLy = Number(apiTotals.sales_dollars_ly) || 0;
      invLy = Number(apiTotals.inventory_oh_dollars_ly) || 0;
      inflow = Number(apiTotals.inflow_dollars) || 0;
      inflowLy = Number(apiTotals.inflow_dollars_ly) || 0;
      onOrder = Number(apiTotals.on_order_dollars) || 0;
      onOrderLy = Number(apiTotals.on_order_dollars_ly) || 0;
    } else {
      for (const r of basisRows) {
        sales += Number(r.sales_dollars) || 0;
        inv += Number(r.inventory_oh_dollars) || 0;
        salesLy += Number(r.sales_dollars_ly) || 0;
        invLy += Number(r.inventory_oh_dollars_ly) || 0;
        inflow += Number(r.inflow_dollars) || 0;
        inflowLy += Number(r.inflow_dollars_ly) || 0;
        onOrder += Number(r.on_order_dollars) || 0;
        onOrderLy += Number(r.on_order_dollars_ly) || 0;
      }
    }

    // apiTotals carries On-Order at retail only; at cost the total is the sum of the converted rows.
    if (priceBasis === 'cost') {
      onOrder = basisRows.reduce((sum, r) => sum + n(r.on_order_dollars), 0);
      onOrderLy = basisRows.reduce((sum, r) => sum + n(r.on_order_dollars_ly), 0);
    }

    return { sales, inv, salesLy, invLy, inflow, inflowLy, onOrder, onOrderLy };
  }, [basisRows, apiTotals, priceBasis]);

  // Built from the unsearched groups so a search doesn't change a row's share of its group.
  const parentGroupTotals = useMemo(() => {
    const totals = new Map<string, PctOfTotals>();
    categoryGroups.forEach((group) => totals.set(group.id, sumPctOfTotals(group.vendors)));
    classGroups.forEach((group) => totals.set(group.id, sumPctOfTotals(group.vendors)));
    locationGroups.forEach((loc) => {
      totals.set(loc.id, sumPctOfTotals(loc.classes.flatMap((c) => c.vendors)));
      loc.classes.forEach((group) => totals.set(group.id, sumPctOfTotals(group.vendors)));
    });
    vendorRows.forEach((vendor) => totals.set(vendor.id, sumPctOfTotals([...vendor.categories, ...vendor.classes])));
    return totals;
  }, [categoryGroups, classGroups, locationGroups, vendorRows]);

  // The active view's leaf rows as listed (search and sort applied, collapsed groups included).
  const displayedLeaves = useMemo(() => {
    let dimHeaders: string[];
    let leaves: VendorPerformanceCsvLeaf[];
    switch (activeView) {
      case 'vendor_by_category':
        dimHeaders = ['Category', 'Vendor'];
        leaves = filteredVendorByCategoryData.flatMap((group) =>
          group.vendors.map((v) => ({ dims: [group.category, v.vendor], row: v, parentTotals: parentGroupTotals.get(group.id) }))
        );
        break;
      case 'class_by_vendor':
        dimHeaders = ['Class #', 'Class Name', 'Vendor'];
        leaves = filteredClassByVendorData.flatMap((group) =>
          group.vendors.map((v) => ({
            dims: [group.m1_class_code ?? '', group.m1_class_name, v.vendor],
            row: v,
            parentTotals: parentGroupTotals.get(group.id),
          }))
        );
        break;
      case 'vendor_by_class':
        dimHeaders = ['Vendor', 'Class #', 'Class Name'];
        leaves = filteredData.flatMap((vendor) =>
          vendor.classes.map((cls) => ({
            dims: [vendor.vendor, cls.m1_class_code ?? '', cls.m1_class_name],
            row: cls,
            parentTotals: parentGroupTotals.get(vendor.id),
          }))
        );
        break;
      case 'category_by_vendor':
        dimHeaders = ['Vendor', 'Category'];
        leaves = filteredData.flatMap((vendor) =>
          vendor.categories.map((cat) => ({ dims: [vendor.vendor, cat.category], row: cat, parentTotals: parentGroupTotals.get(vendor.id) }))
        );
        break;
      case 'location_class_vendor':
        dimHeaders = ['Location', 'Class #', 'Class Name', 'Vendor'];
        leaves = filteredLocationClassVendorData.flatMap((loc) =>
          loc.classes.flatMap((cls) =>
            cls.vendors.map((v) => ({
              dims: [loc.location_name, cls.m1_class_code ?? '', cls.m1_class_name, v.vendor],
              row: v,
              parentTotals: parentGroupTotals.get(cls.id),
            }))
          )
        );
        break;
      case 'total_by_vendor':
      default:
        dimHeaders = ['Vendor'];
        leaves = filteredData.map((row) => ({ dims: [row.vendor], row }));
    }
    return { dimHeaders, leaves };
  }, [activeView, filteredData, filteredVendorByCategoryData, filteredClassByVendorData, filteredLocationClassVendorData, parentGroupTotals]);

  const subtotalMetricRow = useMemo<MetricFields>(() => {
    // While searching, the Total adds up the rows on screen, as group rows do.
    if (searchQuery.trim() !== '') return sumVendorMetrics(displayedLeaves.leaves.map((leaf) => leaf.row));
    if (apiTotals && (Number(apiTotals.sales_dollars) > 0 || Number(apiTotals.inventory_oh_dollars) > 0)) {
      const rowSums = sumVendorMetrics(basisRows);
      const apiBase = buildMetricFields(apiTotals as VendorApiRow);
      // apiTotals carries On-Order at retail only; at cost the footer uses the converted row sums.
      const base = priceBasis === 'cost'
        ? {
          ...apiBase,
          on_order_dollars: rowSums.on_order_dollars,
          on_order_dollars_ly: rowSums.on_order_dollars_ly,
          past_due_on_order_dollars: rowSums.past_due_on_order_dollars,
          past_due_on_order_dollars_ly: rowSums.past_due_on_order_dollars_ly,
        }
        : apiBase;
      // Fall back to row-weighted percentages when the API totals omit them.
      if (!base.gross_margin_pct && basisRows.length > 0) {
        return {
          ...base,
          gross_margin_pct: rowSums.gross_margin_pct || base.gross_margin_pct,
          gross_margin_pct_ly: rowSums.gross_margin_pct_ly || base.gross_margin_pct_ly,
          maintained_margin_pct: rowSums.maintained_margin_pct || base.maintained_margin_pct,
          maintained_margin_pct_ly: rowSums.maintained_margin_pct_ly || base.maintained_margin_pct_ly,
          sell_through_pct: base.sell_through_pct ?? rowSums.sell_through_pct,
          sell_through_pct_ly: base.sell_through_pct_ly ?? rowSums.sell_through_pct_ly,
          sell_through_start_inv_pct: base.sell_through_start_inv_pct || rowSums.sell_through_start_inv_pct,
          sell_through_start_inv_pct_ly: base.sell_through_start_inv_pct_ly || rowSums.sell_through_start_inv_pct_ly,
        };
      }
      return base;
    }
    return sumVendorMetrics(basisRows);
  }, [apiTotals, basisRows, priceBasis, searchQuery, displayedLeaves]);

  useEffect(() => {
    updateScrollState();
  }, [updateScrollState, loading, displayedLeaves, collapsedLocations, collapsedLocationClasses, showYOY]);

  const totalLocationClassVendorVendorsCount = useMemo(() => {
    return filteredLocationClassVendorData.reduce(
      (acc, loc) => acc + loc.classes.reduce((cAcc, cls) => cAcc + cls.vendors.length, 0),
      0
    );
  }, [filteredLocationClassVendorData]);

  const totalLocationClassesCount = useMemo(() => {
    return filteredLocationClassVendorData.reduce((acc, loc) => acc + loc.classes.length, 0);
  }, [filteredLocationClassVendorData]);

  const totalVendorRowsCount = useMemo(() => {
    return filteredVendorByCategoryData.reduce((acc, curr) => acc + curr.vendors.length, 0);
  }, [filteredVendorByCategoryData]);

  const totalClassRowsCount = useMemo(() => {
    return filteredClassByVendorData.reduce((acc, curr) => acc + curr.vendors.length, 0);
  }, [filteredClassByVendorData]);

  const totalCategoriesCount = useMemo(() => {
    return filteredData.reduce((acc, curr) => acc + curr.categories.length, 0);
  }, [filteredData]);

  const totalClassesCount = useMemo(() => {
    return filteredData.reduce((acc, curr) => acc + curr.classes.length, 0);
  }, [filteredData]);

  const filterBtnStyle =
    'h-[34px] min-w-[120px] bg-white border border-[#D0D5DD] rounded-[9px] px-[14px] text-[14px] font-semibold text-[#344054] text-left leading-[21px] flex items-center justify-between gap-1.5 shadow-2xs hover:bg-[#F9FAFB] transition-colors cursor-pointer';

  const vendorNameCss =
    'text-[#101828] font-sans text-[14px] font-bold leading-[21px]';

  const subRowLabelCss =
    'text-[#64748B] font-sans text-[12px] font-bold tracking-wide uppercase pl-12';


  // Change-row $/unit formatters follow the "Show % change" toggle.
  const diffCurrency = showPctChangeDiff ? fmtDiffPercentChange : fmtDiffCurrency;
  const diffUnit = showPctChangeDiff ? fmtDiffPercentChange : fmtDiffUnits;
  const diffUnitPrice = showPctChangeDiff ? fmtDiffPercentChange : fmtDiffUnitPrice;

  const updateColumnOrder = (next: MetricColumnKey[]) => {
    setColumnOrder(next);
    persistColumnOrder(userId, clientId, next);
  };

  const resetColumnOrder = () => {
    setColumnOrder(DEFAULT_COLUMN_ORDER);
    persistColumnOrder(userId, clientId, null);
    clearPinnedColumns();
  };

  // Pinning moves the column to just after the other pinned columns; unpinning returns it to its
  // default slot (after the columns still pinned).
  const togglePinnedColumn = (key: MetricColumnKey) => {
    if (isPinned(key)) {
      const remaining = pinnedColumns.filter((k) => k !== key);
      setPinnedColumns(remaining);
      updateColumnOrder(unpinColumn(columnOrder, key, remaining, DEFAULT_COLUMN_ORDER));
    } else {
      updateColumnOrder(pinColumn(columnOrder, key, pinnedColumns));
      setPinnedColumns([...pinnedColumns, key]);
    }
  };

  const hideColumn = (key: MetricColumnKey) => setVisibleColumns((prev) => ({ ...prev, [key]: false }));

  // Used by table headers (axis 'x') and the Columns picker (axis 'y'). Pinned columns can't be
  // dragged or dropped on, so a drag can never move a column ahead of them.
  const getColumnDragProps = (key: MetricColumnKey, axis: 'x' | 'y') => isPinned(key) ? { draggable: false } : ({
    draggable: true,
    onDragStart: (e: React.DragEvent<HTMLElement>) => {
      setDraggedColumn(key);
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move';
        // Firefox won't start a drag without data set.
        e.dataTransfer.setData('text/plain', key);
      }
    },
    onDragOver: (e: React.DragEvent<HTMLElement>) => {
      if (!draggedColumn) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      const rect = e.currentTarget.getBoundingClientRect();
      const side: 'before' | 'after' = axis === 'x'
        ? (e.clientX < rect.left + rect.width / 2 ? 'before' : 'after')
        : (e.clientY < rect.top + rect.height / 2 ? 'before' : 'after');
      if (dropTarget?.key !== key || dropTarget.side !== side) setDropTarget({ key, side });
    },
    onDrop: (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      if (draggedColumn) {
        const side = dropTarget?.key === key ? dropTarget.side : 'before';
        updateColumnOrder(moveColumn(columnOrder, draggedColumn, key, side));
      }
      setDraggedColumn(null);
      setDropTarget(null);
    },
    onDragEnd: () => {
      setDraggedColumn(null);
      setDropTarget(null);
    },
  });

  const columnDragClass = (key: MetricColumnKey, axis: 'x' | 'y'): string => {
    if (draggedColumn === key) return 'opacity-50';
    if (!draggedColumn || dropTarget?.key !== key) return '';
    if (axis === 'x') {
      return dropTarget.side === 'before' ? 'shadow-[inset_2px_0_0_0_#7F56D9]' : 'shadow-[inset_-2px_0_0_0_#7F56D9]';
    }
    return dropTarget.side === 'before' ? 'shadow-[inset_0_2px_0_0_#7F56D9]' : 'shadow-[inset_0_-2px_0_0_#7F56D9]';
  };

  const orderedColumns = columnOrder
    .map((key) => COLUMN_CONFIG_BY_KEY.get(key))
    .filter((col): col is ColumnToggleConfig => col !== undefined);
  const visibleOrderedColumns = orderedColumns.filter((col) => visibleColumns[col.key]);

  // Sticky first column: what it's called and what it sorts by in the active view.
  const labelColumnHeader =
    activeView === 'vendor_by_category'
      ? 'Category'
      : activeView === 'class_by_vendor'
        ? 'Class'
        : activeView === 'location_class_vendor'
          ? 'Location / Class / Vendor'
          : 'Vendor';
  const labelColumnSortKey: SortableMetricKey =
    activeView === 'vendor_by_category'
      ? 'category'
      : activeView === 'class_by_vendor'
        ? 'm1_class_name'
        : activeView === 'location_class_vendor'
          ? 'location_name'
          : 'vendor';

  const cellContext: MetricCellContext = { overallTotals, diffCurrency, diffUnit, diffUnitPrice };

  const renderMetricCells = (
    row: MetricFields,
    kind: MetricRowKind,
    parentTotals?: PctOfTotals,
    isGroupHeader?: boolean,
    tdClassName: string = ROW_DIVIDER,
    tdStyle?: React.CSSProperties
  ) =>
    visibleOrderedColumns.map((col) =>
      renderMetricCell(col, row, kind, {
        ...(parentTotals ? { ...cellContext, parentTotals } : cellContext),
        isGroupHeader,
        tdClassName,
        tdStyle,
      })
    );

  // A top-level group row pins under the header; its cells carry the background so it stays opaque.
  const pinnedGroupRow = (key: string, bg: string, divider = ROW_DIVIDER, edgeShadow = FIRST_COL_SHADOW) => {
    const shadow = groupShadow(key);
    return {
      labelClass: `sticky left-0 ${STICKY_LAYER.pinnedRowFirstColumn} ${bg} ${shadow || edgeShadow}`,
      cellClass: `${divider} sticky ${STICKY_LAYER.pinnedRowCell} ${bg} ${shadow}`,
      style: stickyTop,
    };
  };

  const footerCellClass = (isFirstRow: boolean) =>
    isFirstRow ? `bg-[#F8F9FA] border-t-2 border-t-[#D0D5DD] ${footShadow}` : 'bg-[#F8F9FA] border-t border-[#E4E4E7]';

  // Column header text, shared by the table and the CSV export.
  const metricHeaderLabel = (col: ColumnToggleConfig): string =>
    priceBasisLabel(col.key, priceBasis) ?? col.headerLabel ?? col.label;

  // Exports what the active view lists (search and sort applied, collapsed groups included): one
  // row per leaf with its group columns, the same metric columns and values as the table, and the
  // Total row.
  const handleExportCSV = () => {
    const { dimHeaders, leaves } = displayedLeaves;
    const csv = buildVendorPerformanceCsv({
      dimHeaders,
      leaves,
      subtotal: apiRows.length > 0 ? subtotalMetricRow : null,
      columns: visibleOrderedColumns,
      headerLabel: metricHeaderLabel,
      ctx: cellContext,
      showYOY,
    });

    downloadCsv(csv, `vendor-performance-${formatDate(new Date(), 'yyyy-MM-dd')}-${activeView}.csv`);
  };

  return (
    <div className="flex flex-col gap-4 p-6 bg-[#FAFAFC] min-h-screen text-[#09090B] font-sans antialiased relative">
      {/* Header Row -- breadcrumbs removed; only relevant when this report is embedded in the
          full Reports page, not standalone here. */}
      <div className="flex flex-wrap items-center justify-between gap-4 mt-0.5">
        <div className="flex items-start gap-3">
          {/* Soft Purple Building Icon Box */}
          <div className="w-9 h-9 rounded-xl bg-[#F5F0FF] border border-[#E9D7FE] flex items-center justify-center text-[#7C3AED] shadow-2xs shrink-0">
            <Building2 className="w-5 h-5 text-[#7C3AED]" />
          </div>
          <div>
            <h1 className="text-[22px] font-bold text-[#09090B] tracking-tight leading-tight">
              Vendor Performance Analysis
            </h1>
            <p className="text-[13px] text-[#71717A] mt-1 max-w-xl leading-normal">
              Sales, inventory on hand, gross margin %, and on-order by vendor — with year-over-year comparison.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setIsSaveModalOpen(true)}
            className="h-9 px-3.5 bg-white border border-[#E4E4E7] rounded-[9px] text-[14px] font-semibold text-[#344054] hover:bg-[#F4F4F5] shadow-2xs flex items-center gap-1.5 transition-colors leading-[21px]"
          >
            <Bookmark className="w-4 h-4 text-[#667085]" /> Save view
          </button>
          <button
            type="button"
            onClick={handleExportCSV}
            disabled={loading}
            className="h-9 px-3.5 bg-white border border-[#E4E4E7] rounded-[9px] text-[14px] font-semibold text-[#344054] hover:bg-[#F4F4F5] shadow-2xs flex items-center gap-1.5 transition-colors leading-[21px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4 text-[#667085]" /> Export CSV
          </button>
          <div className="relative" data-popover-container>
            <button
              type="button"
              onClick={() => setIsSavedViewsMenuOpen((open) => !open)}
              className="h-9 w-9 bg-white border border-[#E4E4E7] rounded-[9px] text-xs text-[#667085] hover:text-[#09090B] hover:bg-[#F4F4F5] shadow-2xs flex items-center justify-center transition-colors"
              title="Saved views"
            >
              <Star className="w-4 h-4 text-[#667085]" />
            </button>

            {isSavedViewsMenuOpen && (
              <div className="absolute right-0 top-11 z-[60] w-72 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-3 flex flex-col gap-1 animate-in fade-in zoom-in-95 duration-100">
                <div className="text-[13px] font-bold text-[#101828] px-1.5 pb-1">Saved views</div>
                {savedViews.length === 0 ? (
                  <div className="text-[12px] text-[#94A3B8] px-1.5 py-2">
                    No saved views yet — use "Save view" to save your current filters.
                  </div>
                ) : (
                  <div className="flex flex-col gap-0.5 max-h-64 overflow-y-auto">
                    {sortedSavedViews.map((savedView) => {
                      const favorite = isFavoriteView(savedView.id);
                      return (
                        <div
                          key={savedView.id}
                          className="flex items-center justify-between gap-2 px-1.5 py-1.5 rounded-lg hover:bg-[#F9FAFB] group"
                        >
                          <button
                            type="button"
                            onClick={() => handleToggleFavorite(savedView.id)}
                            className="shrink-0 text-[#D0D5DD] hover:text-[#F79009] transition-colors"
                            title={favorite ? 'Remove from favorites' : 'Mark as favorite'}
                          >
                            <Star className={`w-3.5 h-3.5 ${favorite ? 'fill-[#F79009] text-[#F79009]' : ''}`} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleLoadSavedView(savedView.payload)}
                            className="flex-1 min-w-0 text-left flex flex-col"
                            title={savedView.name}
                          >
                            <span className="text-[13px] font-semibold text-[#344054] truncate">{savedView.name}</span>
                            {savedView.visibility === 'shared' && (
                              <span className="text-[11px] text-[#667085] flex items-center gap-1 truncate">
                                <Users className="w-3 h-3 shrink-0" />
                                {savedView.canEdit || !savedView.ownerName ? 'Shared' : `Shared by ${savedView.ownerName}`}
                              </span>
                            )}
                          </button>
                          {savedView.canEdit && (
                            <button
                              type="button"
                              onClick={() => handleDeleteSavedView(savedView)}
                              className="text-[#98A2B3] hover:text-[#D92D20] text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity px-1"
                              title="Delete this saved view"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 3. Filter Bar (Row 1) with Interactive Popovers */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-white p-4 px-5 border border-[#E4E4E7] rounded-[16px] shadow-2xs mt-1 relative z-40">
        <div className="flex flex-wrap items-center gap-3.5">
          {/* Date range Filter Popover */}
          <div className="flex flex-col gap-1 relative" data-popover-container>
            <span className="text-[12px] font-medium text-[#71717A]">Date range</span>
            <button
              type="button"
              onClick={() => {
                const opening = activeFilterPopover !== 'date';
                setActiveFilterPopover(opening ? 'date' : null);
                if (opening) setShowCustomCalendar(false);
              }}
              className={filterBtnStyle}
            >
              <span>{selectedDateRange}</span>
              <ChevronDown className="w-3.5 h-3.5 text-[#667085]" />
            </button>

            {/* Date Range Dropdown Popover */}
            {activeFilterPopover === 'date' && (
              <div className="absolute left-0 top-16 z-50 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-3 animate-in fade-in zoom-in-95 duration-100">
                {showCustomCalendar ? (
                  <div className="flex flex-col gap-3.5 w-72">
                    <div className="flex items-center justify-between border-b border-[#EAECF0] pb-2">
                      <span className="text-[13px] font-bold text-[#101828]">Custom date range</span>
                      <button
                        type="button"
                        onClick={() => setShowCustomCalendar(false)}
                        className="text-xs text-[#667085] hover:text-[#101828]"
                      >
                        ✕
                      </button>
                    </div>

                    {/* From / To Date Inputs */}
                    <div className="flex flex-col gap-2.5">
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-[#64748B]">Start date</label>
                        <input
                          type="date"
                          value={customStartDate}
                          max={customEndDate || undefined}
                          onChange={(e) => setCustomStartDate(e.target.value)}
                          className="h-9 w-full px-3 border border-[#E4E4E7] rounded-[9px] text-[13px] font-semibold text-[#344054] bg-white focus:outline-none focus:ring-2 focus:ring-[#7F56D9] shadow-2xs"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-[#64748B]">End date</label>
                        <input
                          type="date"
                          value={customEndDate}
                          min={customStartDate || undefined}
                          max={formatDate(subDays(new Date(), 1), 'yyyy-MM-dd')}
                          onChange={(e) => {
                            const yesterdayIso = formatDate(subDays(new Date(), 1), 'yyyy-MM-dd');
                            const val = e.target.value;
                            setCustomEndDate(val && val > yesterdayIso ? yesterdayIso : val);
                          }}
                          className="h-9 w-full px-3 border border-[#E4E4E7] rounded-[9px] text-[13px] font-semibold text-[#344054] bg-white focus:outline-none focus:ring-2 focus:ring-[#7F56D9] shadow-2xs"
                        />
                      </div>
                    </div>

                    {/* Quick Shortcuts */}
                    <div className="flex flex-wrap gap-1.5 pt-1 border-t border-[#EAECF0]">
                      {[
                        { label: 'Last 30D', days: 30 },
                        { label: 'Last 60D', days: 60 },
                        { label: 'Last 90D', days: 90 },
                        { label: 'Last 180D', days: 180 },
                      ].map((preset) => (
                        <button
                          key={preset.label}
                          type="button"
                          onClick={() => {
                            const today = new Date();
                            const yesterday = subDays(today, 1);
                            setCustomEndDate(formatDate(yesterday, 'yyyy-MM-dd'));
                            setCustomStartDate(formatDate(subDays(yesterday, preset.days - 1), 'yyyy-MM-dd'));
                          }}
                          className="px-2 py-1 bg-[#F4F4F5] hover:bg-[#E4E4E7] text-[#344054] rounded-[6px] text-[11px] font-semibold transition-colors"
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-between border-t border-[#EAECF0] pt-2.5">
                      <button
                        type="button"
                        onClick={() => {
                          setShowCustomCalendar(false);
                          setActiveFilterPopover(null);
                        }}
                        className="h-8 px-3 text-[13px] font-semibold text-[#667085] hover:text-[#101828] rounded-[9px] transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled={!customStartDate || !customEndDate}
                        onClick={() => {
                          if (!customStartDate || !customEndDate) return;
                          const fromDate = new Date(customStartDate + 'T00:00:00');
                          const toDate = new Date(customEndDate + 'T23:59:59');
                          setCustomDateRange({ from: fromDate, to: toDate });
                          setSelectedDateRange(
                            `${formatDate(fromDate, 'MMM d')} - ${formatDate(toDate, 'MMM d, yyyy')}`
                          );
                          setShowCustomCalendar(false);
                          setActiveFilterPopover(null);
                        }}
                        className="h-8 px-4 text-[13px] font-bold text-white bg-[#7F56D9] hover:bg-[#6941C6] rounded-[9px] transition-colors shadow-xs disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="w-52 flex flex-col gap-0.5">
                    {[
                      'Month to date',
                      'Last 1 Month',
                      'Last 2 Months',
                      'Last 3 Months',
                      'Last 6 Months',
                      'Last 12 Months',
                      'Year to date',
                      'Custom range...',
                    ].map((item) => (
                      <button
                        key={item}
                        type="button"
                        onClick={() => {
                          if (item === 'Custom range...') {
                            const today = new Date();
                            if (customDateRange) {
                              setCustomStartDate(formatDate(customDateRange.from, 'yyyy-MM-dd'));
                              setCustomEndDate(formatDate(customDateRange.to, 'yyyy-MM-dd'));
                            } else {
                              const yesterday = subDays(today, 1);
                              setCustomStartDate(formatDate(subDays(yesterday, 89), 'yyyy-MM-dd'));
                              setCustomEndDate(formatDate(yesterday, 'yyyy-MM-dd'));
                            }
                            setShowCustomCalendar(true);
                            return;
                          }
                          setCustomDateRange(undefined);
                          setSelectedDateRange(item);
                          setActiveFilterPopover(null);
                        }}
                        className={`w-full px-3 py-2 text-[14px] font-semibold rounded-[9px] flex items-center justify-between text-left transition-colors leading-[21px] ${selectedDateRange === item
                          ? 'bg-[#F4F0FF] text-[#7F56D9]'
                          : 'text-[#344054] hover:bg-[#F4F4F5]'
                          }`}
                      >
                        <span>{item}</span>
                        {selectedDateRange === item && <Check className="w-3.5 h-3.5 text-[#7F56D9]" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Location Filter Popover */}
          <div className="flex flex-col gap-1 relative" data-popover-container>
            <span className="text-[12px] font-medium text-[#71717A]">Location</span>
            <button
              type="button"
              onClick={() => setActiveFilterPopover(activeFilterPopover === 'location' ? null : 'location')}
              className={filterBtnStyle}
            >
              <span>
                {selectedLocations.length === 0
                  ? 'All locations'
                  : selectedLocations.length === 1
                    ? (locationOptions.find((o) => o.code === selectedLocations[0])?.name ?? selectedLocations[0])
                    : `${selectedLocations.length} locations`}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-[#667085]" />
            </button>

            {/* Location Checkbox Popover */}
            {activeFilterPopover === 'location' && (
              <div className="absolute left-0 top-16 z-50 w-64 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-4 flex flex-col gap-3 animate-in fade-in zoom-in-95 duration-100">
                <div className="text-[14px] font-bold text-[#101828]">Location</div>
                <input
                  type="text"
                  value={locationFilterQuery}
                  onChange={(e) => setLocationFilterQuery(e.target.value)}
                  placeholder="Search locations..."
                  className="h-8 w-full px-2.5 border border-[#E4E4E7] rounded-[8px] text-[13px] font-medium text-[#344054] bg-white focus:outline-none focus:ring-2 focus:ring-[#7F56D9] shadow-2xs"
                />
                <div className="flex flex-col gap-2.5 max-h-52 overflow-y-auto pr-1">
                  {locationOptions.filter((o) => {
                    const q = locationFilterQuery.trim().toLowerCase();
                    return o.name.toLowerCase().includes(q) || (o.code && o.code.toLowerCase().includes(q));
                  }).length === 0 && (
                      <span className="text-[13px] text-[#94A3B8]">No locations found</span>
                    )}
                  {locationOptions.filter((o) => {
                    const q = locationFilterQuery.trim().toLowerCase();
                    return o.name.toLowerCase().includes(q) || (o.code && o.code.toLowerCase().includes(q));
                  }).map((loc) => {
                    const isChecked = locationDraft.pending.codes.includes(loc.code);
                    return (
                      <label key={loc.code} className="flex items-start gap-2.5 cursor-pointer text-[14px] font-semibold text-[#344054] leading-[21px]">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => locationDraft.toggleCode(loc.code)}
                          className="w-4 h-4 mt-0.5 accent-[#7F56D9] rounded border-[#D0D5DD] shrink-0"
                        />
                        <span className="min-w-0 whitespace-normal break-words">{loc.name}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="border-t border-[#EAECF0] pt-2.5 flex items-center gap-2 text-[12px] font-medium text-[#344054]">
                  <input
                    type="checkbox"
                    checked={locationDraft.pending.exclude}
                    onChange={(e) => locationDraft.setExclude(e.target.checked)}
                    className="w-3.5 h-3.5 accent-[#7F56D9] rounded"
                  />
                  <span>Exclude selected instead</span>
                </div>
                <div className="flex items-center justify-between border-t border-[#EAECF0] pt-3">
                  <button
                    type="button"
                    onClick={() => {
                      locationDraft.clearCodes();
                      setLocationFilterQuery('');
                    }}
                    className="text-[13px] font-semibold text-[#667085] hover:text-[#101828]"
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveFilterPopover(null)}
                    className="px-4 py-1.5 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold text-[13px] rounded-[9px] shadow-xs"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Class Filter Popover */}
          <div className="flex flex-col gap-1 relative" data-popover-container>
            <span className="text-[12px] font-medium text-[#71717A]">Class</span>
            <button
              type="button"
              onClick={() => setActiveFilterPopover(activeFilterPopover === 'class' ? null : 'class')}
              className={filterBtnStyle}
            >
              <span>
                {selectedClasses.length === 0
                  ? 'All classes'
                  : selectedClasses.length === 1
                    ? (() => {
                      const found = classOptions.find((o) => o.code === selectedClasses[0]);
                      return found ? (found.code && found.code !== found.name ? `[${found.code}] ${found.name}` : found.name) : selectedClasses[0];
                    })()
                    : `${selectedClasses.length} selected`}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-[#667085]" />
            </button>

            {/* Class Checkbox Popover */}
            {activeFilterPopover === 'class' && (
              <div className="absolute left-0 top-16 z-50 w-64 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-4 flex flex-col gap-3 animate-in fade-in zoom-in-95 duration-100">
                <div className="text-[14px] font-bold text-[#101828]">Class</div>
                <input
                  type="text"
                  value={classFilterQuery}
                  onChange={(e) => setClassFilterQuery(e.target.value)}
                  placeholder="Search classes..."
                  className="h-8 w-full px-2.5 border border-[#E4E4E7] rounded-[8px] text-[13px] font-medium text-[#344054] bg-white focus:outline-none focus:ring-2 focus:ring-[#7F56D9] shadow-2xs"
                />
                <div className="flex flex-col gap-2.5 max-h-56 overflow-y-auto pr-1">
                  {classOptions.filter((o) => {
                    const q = classFilterQuery.trim().toLowerCase();
                    return o.name.toLowerCase().includes(q) || (o.code && o.code.toLowerCase().includes(q));
                  }).length === 0 && (
                      <span className="text-[13px] text-[#94A3B8]">No classes found</span>
                    )}
                  {classOptions.filter((o) => {
                    const q = classFilterQuery.trim().toLowerCase();
                    return o.name.toLowerCase().includes(q) || (o.code && o.code.toLowerCase().includes(q));
                  }).map((cls) => {
                    const isChecked = classDraft.pending.codes.includes(cls.code);
                    return (
                      <label key={cls.code} className="flex items-start gap-2.5 cursor-pointer text-[14px] font-semibold text-[#344054] leading-[21px]">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => classDraft.toggleCode(cls.code)}
                          className="w-4 h-4 mt-0.5 accent-[#7F56D9] rounded border-[#D0D5DD] shrink-0"
                        />
                        <span className="min-w-0 flex items-center gap-1.5 whitespace-normal break-words">
                          {cls.code && (
                            <span className="px-1.5 py-0.5 rounded-[5px] bg-[#F2F4F7] text-[11px] font-mono font-semibold text-[#475467] shrink-0">
                              {cls.code}
                            </span>
                          )}
                          <span>{cls.name}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
                <div className="border-t border-[#EAECF0] pt-2.5 flex items-center gap-2 text-[12px] font-medium text-[#344054]">
                  <input
                    type="checkbox"
                    checked={classDraft.pending.exclude}
                    onChange={(e) => classDraft.setExclude(e.target.checked)}
                    className="w-3.5 h-3.5 accent-[#7F56D9] rounded"
                  />
                  <span>Exclude selected instead</span>
                </div>
                <div className="flex items-center justify-between border-t border-[#EAECF0] pt-3">
                  <button
                    type="button"
                    onClick={() => {
                      classDraft.clearCodes();
                      setClassFilterQuery('');
                    }}
                    className="text-[13px] font-semibold text-[#667085] hover:text-[#101828]"
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveFilterPopover(null)}
                    className="px-4 py-1.5 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold text-[13px] rounded-[9px] shadow-xs"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Category Filter Popover */}
          <div className="flex flex-col gap-1 relative" data-popover-container>
            <span className="text-[12px] font-medium text-[#71717A]">Category</span>
            <button
              type="button"
              onClick={() => setActiveFilterPopover(activeFilterPopover === 'category' ? null : 'category')}
              className={filterBtnStyle}
            >
              <span>
                {selectedCategories.length === 0
                  ? 'All categories'
                  : selectedCategories.length === 1
                    ? selectedCategories[0]
                    : `${selectedCategories.length} selected`}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-[#667085]" />
            </button>

            {/* Category Checkbox Popover */}
            {activeFilterPopover === 'category' && (
              <div className="absolute left-0 top-16 z-50 w-64 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-4 flex flex-col gap-3 animate-in fade-in zoom-in-95 duration-100">
                <div className="text-[14px] font-bold text-[#101828]">Category</div>
                <input
                  type="text"
                  value={categoryFilterQuery}
                  onChange={(e) => setCategoryFilterQuery(e.target.value)}
                  placeholder="Search categories..."
                  className="h-8 w-full px-2.5 border border-[#E4E4E7] rounded-[8px] text-[13px] font-medium text-[#344054] bg-white focus:outline-none focus:ring-2 focus:ring-[#7F56D9] shadow-2xs"
                />
                <div className="flex flex-col gap-2.5 max-h-56 overflow-y-auto pr-1">
                  {categoryOptions.filter((o) => o.name.toLowerCase().includes(categoryFilterQuery.trim().toLowerCase())).length === 0 && (
                    <span className="text-[13px] text-[#94A3B8]">No categories found</span>
                  )}
                  {categoryOptions.filter((o) => o.name.toLowerCase().includes(categoryFilterQuery.trim().toLowerCase())).map((option) => {
                    const cat = option.name;
                    const isChecked = categoryDraft.pending.codes.includes(cat);
                    return (
                      <label key={option.code} className="flex items-start gap-2.5 cursor-pointer text-[14px] font-semibold text-[#344054] leading-[21px]">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => categoryDraft.toggleCode(cat)}
                          className="w-4 h-4 mt-0.5 accent-[#7F56D9] rounded border-[#D0D5DD] shrink-0"
                        />
                        <span className="min-w-0 whitespace-normal break-words">{cat}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="border-t border-[#EAECF0] pt-2.5 flex items-center gap-2 text-[12px] font-medium text-[#344054]">
                  <input
                    type="checkbox"
                    checked={categoryDraft.pending.exclude}
                    onChange={(e) => categoryDraft.setExclude(e.target.checked)}
                    className="w-3.5 h-3.5 accent-[#7F56D9] rounded"
                  />
                  <span>Exclude selected instead</span>
                </div>
                <div className="flex items-center justify-between border-t border-[#EAECF0] pt-3">
                  <button
                    type="button"
                    onClick={() => {
                      categoryDraft.clearCodes();
                      setCategoryFilterQuery('');
                    }}
                    className="text-[13px] font-semibold text-[#667085] hover:text-[#101828]"
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveFilterPopover(null)}
                    className="px-4 py-1.5 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold text-[13px] rounded-[9px] shadow-xs"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Vendor Filter Popover */}
          <div className="flex flex-col gap-1 relative" data-popover-container>
            <span className="text-[12px] font-medium text-[#71717A]">Vendor</span>
            <button
              type="button"
              onClick={() => setActiveFilterPopover(activeFilterPopover === 'vendor' ? null : 'vendor')}
              className={filterBtnStyle}
            >
              <span>
                {selectedVendors.length === 0
                  ? 'All vendors'
                  : selectedVendors.length === 1
                    ? selectedVendors[0]
                    : `${selectedVendors.length} vendors`}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-[#667085]" />
            </button>

            {/* Vendor Checkbox Popover */}
            {activeFilterPopover === 'vendor' && (
              <div className="absolute left-0 top-16 z-50 w-64 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-4 flex flex-col gap-3 animate-in fade-in zoom-in-95 duration-100">
                <div className="text-[14px] font-bold text-[#101828]">Vendor</div>
                <input
                  type="text"
                  value={vendorFilterQuery}
                  onChange={(e) => setVendorFilterQuery(e.target.value)}
                  placeholder="Search vendors..."
                  className="h-8 w-full px-2.5 border border-[#E4E4E7] rounded-[8px] text-[13px] font-medium text-[#344054] bg-white focus:outline-none focus:ring-2 focus:ring-[#7F56D9] shadow-2xs"
                />
                <div className="flex items-center justify-between text-[12px] font-semibold">
                  <button
                    type="button"
                    onClick={() => vendorDraft.addCodes(visibleVendorOptions)}
                    disabled={visibleVendorOptions.length === 0}
                    className="text-[#7F56D9] hover:text-[#6941C6] disabled:text-[#98A2B3] disabled:cursor-not-allowed"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={() => vendorDraft.clearCodes()}
                    disabled={vendorDraft.pending.codes.length === 0}
                    className="text-[#667085] hover:text-[#101828] disabled:text-[#98A2B3] disabled:cursor-not-allowed"
                  >
                    Clear
                  </button>
                </div>
                <div className="flex flex-col gap-2.5 max-h-56 overflow-y-auto pr-1">
                  {visibleVendorOptions.length === 0 && (
                    <span className="text-[13px] text-[#94A3B8]">No vendors found</span>
                  )}
                  {visibleVendorOptions.map((vendor) => (
                    <label key={vendor} className="flex items-start gap-2.5 cursor-pointer text-[14px] font-semibold text-[#344054] leading-[21px]">
                      <input
                        type="checkbox"
                        checked={vendorDraft.pending.codes.includes(vendor)}
                        onChange={() => vendorDraft.toggleCode(vendor)}
                        className="w-4 h-4 mt-0.5 accent-[#7F56D9] rounded border-[#D0D5DD] shrink-0"
                      />
                      <span className="min-w-0 whitespace-normal break-words">{vendor}</span>
                    </label>
                  ))}
                </div>
                <div className="border-t border-[#EAECF0] pt-2.5 flex items-center gap-2 text-[12px] font-medium text-[#344054]">
                  <input
                    type="checkbox"
                    checked={vendorDraft.pending.exclude}
                    onChange={(e) => vendorDraft.setExclude(e.target.checked)}
                    className="w-3.5 h-3.5 accent-[#7F56D9] rounded"
                  />
                  <span>Exclude selected instead</span>
                </div>
                <div className="flex items-center justify-between border-t border-[#EAECF0] pt-3">
                  <button
                    type="button"
                    onClick={() => {
                      vendorDraft.clearCodes();
                      setVendorFilterQuery('');
                    }}
                    className="text-[13px] font-semibold text-[#667085] hover:text-[#101828]"
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveFilterPopover(null)}
                    className="px-4 py-1.5 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold text-[13px] rounded-[9px] shadow-xs"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Min. Sales Direct Inline Input Filter */}
          <div className="flex flex-col gap-1">
            <span className="text-[12px] font-medium text-[#71717A]">Min. sales</span>
            <div className="relative flex items-center">
              <input
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                value={minSales === 'none' ? '' : minSales}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || val === undefined) {
                    setMinSales('none');
                  } else {
                    const parsed = Number(val);
                    setMinSales(Number.isFinite(parsed) && parsed >= 0 ? String(parsed) : 'none');
                  }
                }}
                placeholder="e.g. 500"
                className="h-[34px] w-28 px-3 border border-[#D0D5DD] rounded-[9px] text-[13px] font-semibold text-[#344054] bg-white focus:outline-none focus:ring-2 focus:ring-[#7F56D9] shadow-2xs placeholder-[#98A2B3]"
              />
            </div>
          </div>

          {/* On-Order & OTB Span Dropdown Filter */}
          <div className="flex flex-col gap-1 relative" data-popover-container>
            <div className="flex items-center">
              <span className="text-[12px] font-medium text-[#71717A]">On-order & OTB span</span>
              <InfoTooltip text={`${ON_ORDER_OTB_SPAN_NOTE} ${ON_ORDER_NO_DATE_NOTE}`} />
            </div>
            <button
              type="button"
              onClick={() => {
                if (activeFilterPopover === 'on_order_span') {
                  setActiveFilterPopover(null);
                } else {
                  setShowCustomOnOrderSpan(false);
                  setActiveFilterPopover('on_order_span');
                }
              }}
              className={filterBtnStyle}
            >
              {(() => {
                const info = getSpanDateRangeInfo(onOrderSpan, undefined, onOrderStartDate, onOrderEndDate);
                return (
                  <span className="flex items-center gap-1.5 truncate">
                    <span>{info.label}</span>
                    <span className="text-[11px] font-normal text-[#667085]">({info.dateRange})</span>
                  </span>
                );
              })()}
              <ChevronDown className="w-3.5 h-3.5 text-[#667085] shrink-0" />
            </button>

            {activeFilterPopover === 'on_order_span' && (
              <div className="absolute left-0 top-16 z-50 w-72 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-2.5 flex flex-col gap-1 animate-in fade-in zoom-in-95 duration-100">
                {showCustomOnOrderSpan ? (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between border-b border-[#EAECF0] pb-2">
                      <span className="text-[13px] font-bold text-[#101828]">Custom span date range</span>
                      <button
                        type="button"
                        onClick={() => setShowCustomOnOrderSpan(false)}
                        className="text-xs text-[#667085] hover:text-[#101828]"
                      >
                        ✕
                      </button>
                    </div>

                    <div className="flex flex-col gap-2.5">
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-[#64748B]">Start date</label>
                        <input
                          type="date"
                          value={draftOnOrderStart}
                          max={draftOnOrderEnd || undefined}
                          onChange={(e) => setDraftOnOrderStart(e.target.value)}
                          className="h-8 w-full px-2.5 border border-[#E4E4E7] rounded-[8px] text-[12px] font-semibold text-[#344054] bg-white focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-[#64748B]">End date</label>
                        <input
                          type="date"
                          value={draftOnOrderEnd}
                          min={draftOnOrderStart || undefined}
                          onChange={(e) => setDraftOnOrderEnd(e.target.value)}
                          className="h-8 w-full px-2.5 border border-[#E4E4E7] rounded-[8px] text-[12px] font-semibold text-[#344054] bg-white focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between border-t border-[#EAECF0] pt-2">
                      <button
                        type="button"
                        onClick={() => setShowCustomOnOrderSpan(false)}
                        className="h-7 px-2.5 text-[12px] font-semibold text-[#667085] hover:text-[#101828] rounded-[7px] transition-colors"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        disabled={!draftOnOrderStart || !draftOnOrderEnd}
                        onClick={() => {
                          if (!draftOnOrderStart || !draftOnOrderEnd) return;
                          setOnOrderStartDate(draftOnOrderStart);
                          setOnOrderEndDate(draftOnOrderEnd);
                          setOnOrderSpan('custom');
                          setShowCustomOnOrderSpan(false);
                          setActiveFilterPopover(null);
                        }}
                        className="h-7 px-3 text-[12px] font-bold text-white bg-[#7F56D9] hover:bg-[#6941C6] rounded-[7px] transition-colors shadow-xs disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {ON_ORDER_SPAN_OPTIONS.map((val) => {
                      const info = getSpanDateRangeInfo(val);
                      const isSelected = onOrderSpan === val;
                      return (
                        <button
                          key={val}
                          type="button"
                          onClick={() => {
                            setOnOrderSpan(val);
                            setActiveFilterPopover(null);
                          }}
                          className={`w-full px-3 py-2 text-[13px] rounded-[9px] flex items-center justify-between text-left transition-colors cursor-pointer leading-[20px] ${isSelected
                            ? 'bg-[#F4F0FF] text-[#7C3AED]'
                            : 'text-[#344054] hover:bg-[#F4F4F5]'
                            }`}
                        >
                          <div className="flex flex-col">
                            <span className="font-semibold">{info.label}</span>
                            <span className={`text-[11px] font-normal ${isSelected ? 'text-[#8B5CF6]' : 'text-[#667085]'}`}>
                              {info.dateRange}
                            </span>
                          </div>
                          {isSelected && <Check className="w-3.5 h-3.5 text-[#7C3AED] shrink-0" />}
                        </button>
                      );
                    })}

                    {/* Custom Date Range Option */}
                    <button
                      type="button"
                      onClick={() => {
                        const today = new Date();
                        const todayIso = formatDate(today, 'yyyy-MM-dd');
                        const defaultEnd = formatDate(new Date(today.getFullYear(), today.getMonth() + 3, 0), 'yyyy-MM-dd');
                        setDraftOnOrderStart(onOrderStartDate || todayIso);
                        setDraftOnOrderEnd(onOrderEndDate || defaultEnd);
                        setShowCustomOnOrderSpan(true);
                      }}
                      className={`w-full px-3 py-2 text-[13px] rounded-[9px] flex items-center justify-between text-left transition-colors cursor-pointer leading-[20px] border-t border-[#F2F4F7] mt-0.5 ${onOrderSpan === 'custom'
                        ? 'bg-[#F4F0FF] text-[#7C3AED]'
                        : 'text-[#344054] hover:bg-[#F4F4F5]'
                        }`}
                    >
                      <div className="flex flex-col">
                        <span className="font-semibold">Custom date range...</span>
                        <span className={`text-[11px] font-normal ${onOrderSpan === 'custom' ? 'text-[#8B5CF6]' : 'text-[#667085]'}`}>
                          {onOrderSpan === 'custom' && onOrderStartDate && onOrderEndDate
                            ? getSpanDateRangeInfo('custom', undefined, onOrderStartDate, onOrderEndDate).dateRange
                            : 'Set custom start & end date'}
                        </span>
                      </div>
                      {onOrderSpan === 'custom' && <Check className="w-3.5 h-3.5 text-[#7C3AED] shrink-0" />}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Retail vs Cost Basis Control (Moved to Row 1 next to On-order & OTB span) */}
          <div className="flex flex-col gap-1 justify-end">
            <span className="text-[12px] font-medium text-transparent select-none" aria-hidden="true">&nbsp;</span>
            <div className="inline-flex items-center bg-[#F2F4F7] p-[3px] rounded-[8px] h-[34px] border border-[#E4E4E7]/60">
              <button
                type="button"
                onClick={() => setPriceBasis('retail')}
                className={`px-3 py-1 text-[13px] font-semibold rounded-[6px] transition-all cursor-pointer h-7 flex items-center justify-center whitespace-nowrap ${priceBasis === 'retail'
                  ? 'bg-white text-[#101828] shadow-2xs font-bold'
                  : 'text-[#667085] hover:text-[#101828]'
                  }`}
              >
                Retail
              </button>
              <button
                type="button"
                onClick={() => setPriceBasis('cost')}
                className={`px-3 py-1 text-[13px] font-semibold rounded-[6px] transition-all cursor-pointer h-7 flex items-center justify-center whitespace-nowrap ${priceBasis === 'cost'
                  ? 'bg-white text-[#101828] shadow-2xs font-bold'
                  : 'text-[#667085] hover:text-[#101828]'
                  }`}
              >
                Cost
              </button>
            </div>
          </div>
        </div>

        {/* Right side toggles: YOY Comparison & Show % change */}
        <div className="flex items-center gap-5">
          {/* YOY comparison Switch */}
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-semibold text-[#344054] leading-[20px]">YOY Comparison</span>
            <InfoTooltip text="Compare current period against same period last year" />
            <button
              type="button"
              onClick={() => setShowYOY(!showYOY)}
              className={`w-9 h-5 flex items-center rounded-full p-0.5 transition-colors cursor-pointer ${showYOY ? 'bg-[#7C3AED]' : 'bg-[#E4E4E7]'
                }`}
            >
              <div
                className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform ${showYOY ? 'translate-x-4' : 'translate-x-0'
                  }`}
              />
            </button>
          </div>

          {/* Show % Change Switch */}
          {showYOY && (
            <div className="flex items-center gap-2 animate-in fade-in duration-150">
              <span className="text-[13px] font-semibold text-[#344054] leading-[20px]">Show % change</span>
              <InfoTooltip text="Display change row values as percentage change instead of dollar/unit difference" />
              <button
                type="button"
                onClick={() => setShowPctChangeDiff(!showPctChangeDiff)}
                className={`w-9 h-5 flex items-center rounded-full p-0.5 transition-colors cursor-pointer ${showPctChangeDiff ? 'bg-[#7C3AED]' : 'bg-[#E4E4E7]'
                  }`}
              >
                <div
                  className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform ${showPctChangeDiff ? 'translate-x-4' : 'translate-x-0'
                    }`}
                />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 4. Toolbar (Row 2) — Streamlined 1-Line Side by Side Layout */}
      <div className="flex flex-wrap items-center justify-between gap-4 mt-1 relative z-20">
        {/* Left: Hierarchy Dropdown */}
        <div className="flex items-center gap-3">
          <div className="relative flex flex-col" data-popover-container>
            <div className="flex items-center gap-1.5 mb-1">
              <span className="text-[12px] font-medium text-[#71717A]">Hierarchy</span>
              <InfoTooltip
                text="Choose how rows are grouped — by vendor, class, category, or location, in any combination. Groupings that include class also show Open-to-Buy."
                widthClass="w-72"
                positionClass="top-full left-0 mt-2 z-[110]"
              />
            </div>

            <div className="relative">
              <button
                type="button"
                onClick={() => setActiveFilterPopover(activeFilterPopover === 'hierarchy' ? null : 'hierarchy')}
                className={`h-[34px] w-[320px] px-3.5 rounded-[8px] text-[14px] font-semibold text-left leading-[21px] flex items-center justify-between gap-3 transition-all cursor-pointer ${activeFilterPopover === 'hierarchy'
                  ? 'border border-[#7F56D9] bg-white shadow-[0_0_0_3px_rgba(127,86,217,0.15)]'
                  : 'border border-[#D0D5DD] bg-white shadow-2xs hover:bg-[#F9FAFB]'
                  }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-[#101828] font-bold">
                    {selectedViewConfig?.label ?? 'Select Hierarchy'}
                  </span>
                  {selectedViewConfig?.hasOtb && (
                    <span className="px-1.5 py-0.5 rounded-[5px] bg-[#F9F5FF] text-[#6941C6] border border-[#E9D7FE] text-[10px] font-bold">
                      OTB
                    </span>
                  )}
                </div>
                {activeFilterPopover === 'hierarchy' ? (
                  <ChevronUp className="w-3.5 h-3.5 text-[#7F56D9]" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5 text-[#667085]" />
                )}
              </button>

              {activeFilterPopover === 'hierarchy' && (
                <div className="absolute left-0 top-[calc(100%+6px)] z-[100] w-[320px] bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-2 flex flex-col gap-0.5 animate-in fade-in zoom-in-95 duration-100">
                  {/* Section 1: WITH OPEN-TO-BUY */}
                  <div className="px-3 py-1.5 text-[11px] font-bold text-[#98A2B3] uppercase tracking-wider">
                    WITH OPEN-TO-BUY
                  </div>
                  {WITH_OTB_VIEW_OPTIONS.map((opt) => {
                    const isSelected = activeView === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => {
                          setActiveView(opt.key);
                          setActiveFilterPopover(null);
                        }}
                        className={`w-full px-3 py-2 text-[13px] font-semibold rounded-[9px] flex items-center justify-between text-left transition-colors leading-[20px] ${isSelected
                          ? 'bg-[#F9F5FF] text-[#6941C6]'
                          : 'text-[#344054] hover:bg-[#F4F4F5]'
                          }`}
                      >
                        <span className="truncate">{opt.label}</span>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="px-1.5 py-0.5 rounded-[5px] bg-[#F9F5FF] text-[#6941C6] border border-[#E9D7FE] text-[10px] font-bold">
                            OTB
                          </span>
                          {isSelected && <Check className="w-3.5 h-3.5 text-[#6941C6]" />}
                        </div>
                      </button>
                    );
                  })}

                  {/* Section 2: WITHOUT OPEN-TO-BUY */}
                  <div className="px-3 py-1.5 pt-2 text-[11px] font-bold text-[#98A2B3] uppercase tracking-wider">
                    WITHOUT OPEN-TO-BUY
                  </div>
                  {WITHOUT_OTB_VIEW_OPTIONS.map((opt) => {
                    const isSelected = activeView === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => {
                          setActiveView(opt.key);
                          setActiveFilterPopover(null);
                        }}
                        className={`w-full px-3 py-2 text-[13px] font-semibold rounded-[9px] flex items-center justify-between text-left transition-colors leading-[20px] ${isSelected
                          ? 'bg-[#F9F5FF] text-[#6941C6]'
                          : 'text-[#344054] hover:bg-[#F4F4F5]'
                          }`}
                      >
                        <span className="truncate">{opt.label}</span>
                        {isSelected && <Check className="w-3.5 h-3.5 text-[#6941C6]" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: Search + Columns Popover + Reset */}
        <div className="flex items-center gap-2.5 relative">
          {/* Vendor/Category Search Box */}
          <div className="flex items-center bg-white border border-[#E4E4E7] rounded-[9px] px-3.5 h-9 shadow-2xs">
            <Search className="w-4 h-4 text-[#667085] shrink-0" />
            <input
              type="text"
              placeholder="Search by vendor or category"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64 h-full px-2 text-[14px] font-semibold text-[#344054] bg-transparent focus:outline-none placeholder-[#98A2B3] leading-[21px]"
            />
          </div>

          {/* Columns Button with Popover Trigger */}
          <div className="relative" data-popover-container>
            <button
              type="button"
              onClick={() => setIsColumnsPopoverOpen(!isColumnsPopoverOpen)}
              className={`h-9 px-3.5 border rounded-[9px] text-[14px] font-semibold transition-colors leading-[21px] flex items-center gap-2 whitespace-nowrap shadow-2xs ${isColumnsPopoverOpen
                ? 'bg-[#F4F0FF] border-[#7F56D9] text-[#7F56D9]'
                : 'bg-white border-[#E4E4E7] text-[#344054] hover:bg-[#F4F4F5]'
                }`}
            >
              <SlidersHorizontal className="w-4 h-4" /> Columns
            </button>

            {/* Column Toggle Popover Menu */}
            {isColumnsPopoverOpen && (
              <div className="absolute right-0 top-11 z-[100] w-80 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-4 flex flex-col gap-3.5 animate-in fade-in zoom-in-95 duration-100">
                <div className="text-xs font-bold uppercase tracking-wider text-[#101828] border-b border-[#EAECF0] pb-2 flex items-center justify-between">
                  <span>VISIBLE COLUMNS</span>
                  <button
                    type="button"
                    onClick={() => setIsColumnsPopoverOpen(false)}
                    className="text-xs text-[#667085] hover:text-[#101828]"
                  >
                    ✕
                  </button>
                </div>

                <div className="flex flex-col gap-3.5 max-h-[380px] overflow-y-auto pr-1">
                  {orderedColumns.map((col) => {
                    const isChecked = visibleColumns[col.key];
                    return (
                      <div
                        key={col.key}
                        {...getColumnDragProps(col.key, 'y')}
                        onClick={() => toggleColumnVisibility(col.key)}
                        className={`flex items-start gap-2 cursor-pointer group rounded-[6px] ${columnDragClass(col.key, 'y')}`}
                      >
                        {isPinned(col.key) ? (
                          <Pin className="w-3.5 h-3.5 mt-0.5 text-[#7C3AED] shrink-0" aria-label="Pinned" />
                        ) : (
                          <GripVertical className="w-3.5 h-3.5 mt-0.5 text-[#98A2B3] cursor-grab shrink-0" aria-hidden="true" />
                        )}
                        <div
                          className={`w-4 h-4 rounded mt-0.5 flex items-center justify-center transition-colors shrink-0 ${isChecked
                            ? 'bg-[#7F56D9] text-white'
                            : 'bg-white border border-[#D0D5DD] group-hover:border-[#7F56D9]'
                            }`}
                        >
                          {isChecked && <Check className="w-3 h-3 stroke-[3]" />}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[13px] font-bold text-[#101828] leading-tight">
                            {priceBasisLabel(col.key, priceBasis) ?? col.label}
                          </span>
                          <span className="text-[11px] text-[#667085] mt-0.5 leading-snug">
                            {col.description}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="border-t border-[#EAECF0] pt-2 text-[11px] text-[#98A2B3] flex items-start justify-between gap-3">
                  <span>Drag column headers, or the items above, to reorder. Drag a header's right edge to resize. Saved for you on this browser.</span>
                  <div className="shrink-0 flex flex-col items-end gap-1">
                    <button
                      type="button"
                      onClick={resetColumnOrder}
                      className="font-semibold text-[#7F56D9] hover:text-[#6941C6]"
                    >
                      Reset order
                    </button>
                    <button
                      type="button"
                      onClick={resetColumnWidths}
                      className="font-semibold text-[#7F56D9] hover:text-[#6941C6]"
                    >
                      Reset widths
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Reset Button */}
          <button
            type="button"
            onClick={() => {
              setSearchQuery('');
              setSelectedDateRange('Last 3 Months');
              setCustomDateRange(undefined);
              setSelectedLocations([]);
              setSelectedClasses([]);
              setSelectedCategories([]);
              setSelectedVendors([]);
              setExcludeVendor(false);
              setVendorFilterQuery('');
              setMinSales('none');
              setOnOrderSpan('3_months');
              setOnOrderStartDate('');
              setOnOrderEndDate('');
              setShowCustomOnOrderSpan(false);
              setIsColumnsPopoverOpen(false);
              setActiveFilterPopover(null);
              const initial: Record<string, boolean> = {};
              COLUMN_TOGGLE_LIST.forEach((col) => {
                initial[col.key] = col.defaultVisible;
              });
              setVisibleColumns(initial);
              resetColumnOrder();
            }}
            title="Reset Filters"
            className="h-9 w-9 flex items-center justify-center bg-white border border-[#E4E4E7] rounded-[9px] text-xs text-[#667085] hover:text-[#09090B] hover:bg-[#F4F4F5] shadow-2xs transition-colors shrink-0"
          >
            <RotateCcw className="w-4 h-4 text-[#667085]" />
          </button>
        </div>
      </div>

      {/* Row Count Display */}
      <div className="text-[12px] font-medium text-[#A1A1AA] tracking-tight mt-0.5">
        {activeView === 'vendor_by_category'
          ? showYOY
            ? `${filteredVendorByCategoryData.length} categories · ${totalVendorRowsCount} vendor rows · ${totalVendorRowsCount * 3} rows shown (This Period / Last Year / Change)`
            : `${filteredVendorByCategoryData.length} categories · ${totalVendorRowsCount} vendor rows · ${totalVendorRowsCount} rows shown`
          : activeView === 'class_by_vendor'
            ? showYOY
              ? `${filteredClassByVendorData.length} classes · ${totalClassRowsCount} vendor rows · ${totalClassRowsCount * 3} rows shown (This Period / Last Year / Change)`
              : `${filteredClassByVendorData.length} classes · ${totalClassRowsCount} vendor rows · ${totalClassRowsCount} rows shown`
            : activeView === 'location_class_vendor'
              ? showYOY
                ? `${filteredLocationClassVendorData.length} locations · ${totalLocationClassesCount} classes · ${totalLocationClassVendorVendorsCount} vendor rows · ${totalLocationClassVendorVendorsCount * 3} rows shown (This Period / Last Year / Change)`
                : `${filteredLocationClassVendorData.length} locations · ${totalLocationClassesCount} classes · ${totalLocationClassVendorVendorsCount} vendor rows · ${totalLocationClassVendorVendorsCount} rows shown`
              : activeView === 'category_by_vendor'
                ? showYOY
                  ? `${filteredData.length} vendors · ${totalCategoriesCount} categories · ${totalCategoriesCount * 3} rows shown (This Period / Last Year / Change)`
                  : `${filteredData.length} vendors · ${totalCategoriesCount} categories · ${totalCategoriesCount} rows shown`
                : activeView === 'vendor_by_class'
                  ? showYOY
                    ? `${filteredData.length} vendors · ${totalClassesCount} classes · ${totalClassesCount * 3} rows shown (This Period / Last Year / Change)`
                    : `${filteredData.length} vendors · ${totalClassesCount} classes · ${totalClassesCount} rows shown`
                  : showYOY
                    ? `${filteredData.length} vendors · ${filteredData.length * 3} rows shown (This Period / Last Year / Change)`
                    : `${filteredData.length} vendors · ${filteredData.length} rows shown`}
      </div>

      {/* 5. Matrix Table */}
      <div className="bg-white border border-[#E4E4E7] rounded-2xl shadow-xs overflow-hidden mt-0.5 relative z-10">
        <div ref={scrollRef} onScroll={updateScrollState} className="overflow-x-auto max-h-[calc(100vh-220px)] overflow-y-auto">
          <table className="w-full text-left text-[13px] border-separate border-spacing-0">
            <thead ref={theadRef} className={`sticky top-0 ${STICKY_LAYER.header} bg-[#F9F9FB]`}>
              <tr className="bg-[#F9F9FB] text-[#64748B] font-semibold select-none">
                {/* Sticky First Column */}
                <th
                  style={{ width: widthOf(LABEL_COLUMN_KEY), minWidth: widthOf(LABEL_COLUMN_KEY) }}
                  className={`sticky left-0 top-0 bg-[#F9F9FB] ${activeMenuColumn === LABEL_COLUMN_KEY ? STICKY_LAYER.menuOpen : STICKY_LAYER.headerFirstColumn} hover:z-50 px-4 py-2.5 border-r border-b border-[#E4E4E7] ${headShadow || FIRST_COL_SHADOW}`}
                >
                  <SortableHeader
                    label={labelColumnHeader}
                    sortKey={labelColumnSortKey}
                    sortConfig={sortConfig}
                    isMenuOpen={activeMenuColumn === LABEL_COLUMN_KEY}
                    onToggleMenu={() => toggleColumnMenu(LABEL_COLUMN_KEY)}
                    // The sticky label column can only be sorted -- no pin or hide.
                    menu={
                      <ColumnHeaderMenu
                        title={labelColumnHeader}
                        description={FIRST_COLUMN_DESCRIPTIONS[activeView]}
                        sortType="text"
                        activeDirection={sortConfig?.key === labelColumnSortKey ? sortConfig.direction : null}
                        onSort={(direction) => handleSort(labelColumnSortKey, direction)}
                        onClose={closeColumnMenu}
                      />
                    }
                    align="left"
                    description={FIRST_COLUMN_DESCRIPTIONS[activeView]}
                  />
                  <span {...getResizeHandleProps(LABEL_COLUMN_KEY, 'first')} className={COLUMN_RESIZE_HANDLE_CLASS} />
                </th>

                {visibleOrderedColumns.map((col, idx) => {
                  const headerLabel = metricHeaderLabel(col);
                  const sortKey = col.sortKey ?? (col.key as SortableMetricKey);
                  const isMenuOpen = activeMenuColumn === col.key;
                  const isLastColumn = idx >= visibleOrderedColumns.length - 2;
                  const pinned = isPinned(col.key);
                  return (
                    <th
                      key={col.key}
                      {...getColumnDragProps(col.key, 'x')}
                      draggable={!isResizing && !pinned && !isMenuOpen}
                      style={{ width: widthOf(col.key), minWidth: widthOf(col.key) }}
                      className={`px-4 py-2.5 sticky top-0 bg-[#F9F9FB] ${isMenuOpen ? STICKY_LAYER.menuOpen : STICKY_LAYER.header} hover:z-50 border-b border-[#E4E4E7] ${headShadow} text-right ${pinned ? '' : 'cursor-grab'} ${columnDragClass(col.key, 'x')}`}
                    >
                      <SortableHeader
                        label={headerLabel}
                        sortKey={sortKey}
                        sortConfig={sortConfig}
                        isMenuOpen={isMenuOpen}
                        onToggleMenu={() => toggleColumnMenu(col.key)}
                        menu={
                          <ColumnHeaderMenu
                            title={headerLabel}
                            description={getColumnDescription(col.key)}
                            sortType="numeric"
                            activeDirection={sortConfig?.key === sortKey ? sortConfig.direction : null}
                            onSort={(direction) => handleSort(sortKey, direction)}
                            pin={{ isPinned: pinned, onToggle: () => togglePinnedColumn(col.key) }}
                            onHide={() => hideColumn(col.key)}
                            onClose={closeColumnMenu}
                            align={isLastColumn ? 'right' : 'left'}
                          />
                        }
                        align="right"
                        description={getColumnDescription(col.key)}
                        isLastColumn={isLastColumn}
                      />
                      <span {...getResizeHandleProps(col.key, col.headerLabel ?? col.label)} className={COLUMN_RESIZE_HANDLE_CLASS} />
                    </th>
                  );
                })}
              </tr>
            </thead>
              {loading ? (
                <tbody>
                  <tr>
                    <td colSpan={100} className="px-4 py-10 text-center text-[13px] font-medium text-[#71717A]">
                      Loading vendor performance...
                    </td>
                  </tr>
                </tbody>
              ) : (
                activeView === 'vendor_by_category'
                  ? filteredVendorByCategoryData.length === 0
                  : activeView === 'class_by_vendor'
                    ? filteredClassByVendorData.length === 0
                    : activeView === 'location_class_vendor'
                      ? filteredLocationClassVendorData.length === 0
                      : filteredData.length === 0
              ) ? (
                <tbody>
                  <tr>
                    <td colSpan={100} className="px-4 py-10 text-center text-[13px] font-medium text-[#71717A]">
                      No vendors match the current filters.
                    </td>
                  </tr>
                </tbody>
              ) : (
                <>
                  {/* VENDOR BY CATEGORY VIEW */}
                  {activeView === 'vendor_by_category' ? (
                    filteredVendorByCategoryData.map((catGroup) => {
                      const catSubtotal = sumVendorMetrics(catGroup.vendors);
                      const pin = pinnedGroupRow(catGroup.id, 'bg-[#F8F9FA]');

                      return (
                        <tbody key={catGroup.id} ref={registerGroupBody(catGroup.id)}>
                          {/* Category Group Header Row */}
                          <tr className="bg-[#F8F9FA]">
                            <td
                              className={`${pin.labelClass} px-4 py-2.5 border-r border-b border-[#E4E4E7] whitespace-nowrap`}
                              style={pin.style}
                            >
                              <div className="flex flex-col gap-1.5">
                                <div className="flex items-center gap-2">
                                  <span className="text-[14px] font-bold text-[#101828]">{catGroup.category}</span>
                                  <span className="font-normal text-[12px] text-[#667085]">
                                    {catGroup.vendors.length} vendors
                                  </span>
                                </div>
                                <OtbSummaryPills
                                  planSales={catGroup.plan_sales}
                                  planInflow={catGroup.plan_inflow}
                                  onOrder={catGroup.plan_on_order}
                                  otb={catGroup.otb_dollars}
                                  priceBasis={priceBasis}
                                />
                              </div>
                            </td>
                            {renderMetricCells(catSubtotal, 'current', overallTotals, true, pin.cellClass, pin.style)}
                          </tr>
                          {/* Vendor Sub-Rows under Category */}
                          {catGroup.vendors.map((vendorItem) => {
                            return (
                              <React.Fragment key={vendorItem.id}>
                                {/* ROW 1: THIS PERIOD */}
                                <tr className="group hover:bg-[#F4F4F5] transition-colors bg-white">
                                  <td className="sticky left-0 bg-white group-hover:bg-[#F4F4F5] z-10 px-4 py-3 border-r border-b border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0] pl-8">
                                    <span className="text-[#101828] text-[13px] font-semibold">{vendorItem.vendor}</span>
                                  </td>
                                  {renderMetricCells(vendorItem, 'current', parentGroupTotals.get(catGroup.id))}
                                </tr>

                                {/* YOY SUB-ROWS IF YOY IS ON */}
                                {showYOY && (
                                  <>
                                    {/* ROW 2: LAST YEAR */}
                                    <tr className="group hover:bg-[#F4F4F5] transition-colors bg-white">
                                      <td className="sticky left-0 bg-white group-hover:bg-[#F4F4F5] z-10 px-4 py-2 border-r border-b border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0]">
                                        <span className={subRowLabelCss}>LAST YEAR</span>
                                      </td>
                                      {renderMetricCells(vendorItem, 'ly', parentGroupTotals.get(catGroup.id))}
                                    </tr>

                                    {/* ROW 3: CHANGE */}
                                    <tr className="group hover:bg-[#F4F4F5] transition-colors bg-white">
                                      <td className="sticky left-0 bg-white group-hover:bg-[#F4F4F5] z-10 px-4 py-2 border-r border-b border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0]">
                                        <span className={subRowLabelCss}>CHANGE</span>
                                      </td>
                                      {renderMetricCells(vendorItem, 'change', parentGroupTotals.get(catGroup.id))}
                                    </tr>
                                  </>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      );
                    })
                  ) : activeView === 'class_by_vendor' ? (
                    /* CLASS BY VENDOR VIEW */
                    filteredClassByVendorData.map((classGroup) => {
                      const classSubtotal = sumVendorMetrics(classGroup.vendors);
                      const pin = pinnedGroupRow(classGroup.id, 'bg-[#F8F9FA]');

                      return (
                        <tbody key={classGroup.id} ref={registerGroupBody(classGroup.id)}>
                          {/* Class Group Header Row */}
                          <tr className="bg-[#F8F9FA]">
                            <td
                              className={`${pin.labelClass} px-4 py-2.5 border-r border-b border-[#E4E4E7] whitespace-nowrap`}
                              style={pin.style}
                            >
                              <div className="flex flex-col gap-1.5">
                                <div className="flex items-center gap-2">
                                  {classGroup.m1_class_code && (
                                    <span className="px-1.5 py-0.5 rounded-[5px] bg-[#F2F4F7] text-[11px] font-mono font-semibold text-[#475467] shrink-0">
                                      {classGroup.m1_class_code}
                                    </span>
                                  )}
                                  <span className="text-[14px] font-bold text-[#101828]">{classGroup.m1_class_name}</span>
                                  <span className="font-normal text-[12px] text-[#667085]">
                                    {classGroup.vendors.length} vendors
                                  </span>
                                </div>
                                <OtbSummaryPills
                                  planSales={classGroup.plan_sales}
                                  planInflow={classGroup.plan_inflow}
                                  onOrder={classGroup.plan_on_order}
                                  otb={classGroup.otb_dollars}
                                  priceBasis={priceBasis}
                                />
                              </div>
                            </td>
                            {renderMetricCells(classSubtotal, 'current', overallTotals, true, pin.cellClass, pin.style)}
                          </tr>

                          {/* Vendor Sub-Rows under Class */}

                          {classGroup.vendors.map((vendorItem) => {



                            return (

                              <React.Fragment key={vendorItem.id}>

                                {/* ROW 1: THIS PERIOD */}

                                <tr className="group hover:bg-[#F4F4F5] transition-colors bg-white">

                                  <td className="sticky left-0 bg-white group-hover:bg-[#F4F4F5] z-10 px-4 py-3 border-r border-b border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0] pl-8">

                                    <span className="text-[#101828] text-[13px] font-semibold">{vendorItem.vendor}</span>

                                  </td>

                                  {renderMetricCells(vendorItem, 'current', parentGroupTotals.get(classGroup.id))}

                                </tr>



                                {/* YOY SUB-ROWS IF YOY IS ON */}

                                {showYOY && (

                                  <>

                                    {/* ROW 2: LAST YEAR */}

                                    <tr className="group hover:bg-[#F4F4F5] transition-colors bg-white">

                                      <td className="sticky left-0 bg-white group-hover:bg-[#F4F4F5] z-10 px-4 py-2 border-r border-b border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0]">

                                        <span className={subRowLabelCss}>LAST YEAR</span>

                                      </td>

                                      {renderMetricCells(vendorItem, 'ly', parentGroupTotals.get(classGroup.id))}

                                    </tr>



                                    {/* ROW 3: CHANGE */}

                                    <tr className="group hover:bg-[#F4F4F5] transition-colors bg-white">

                                      <td className="sticky left-0 bg-white group-hover:bg-[#F4F4F5] z-10 px-4 py-2 border-r border-b border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0]">

                                        <span className={subRowLabelCss}>CHANGE</span>

                                      </td>

                                      {renderMetricCells(vendorItem, 'change', parentGroupTotals.get(classGroup.id))}

                                    </tr>

                                  </>

                                )}

                              </React.Fragment>

                            );

                          })}

                        </tbody>
                      );
                    })
                  ) : activeView === 'location_class_vendor' ? (
                    /* LOCATION > CLASS > VENDOR VIEW */
                    filteredLocationClassVendorData.map((locGroup) => {
                      const isLocCollapsed = !!collapsedLocations[locGroup.id];
                      const locVendors = locGroup.classes.flatMap((c) => c.vendors);
                      const locSubtotal = sumVendorMetrics(locVendors);
                      const pin = pinnedGroupRow(
                        locGroup.id,
                        'bg-[#EEF2F6] group-hover:bg-[#E2E8F0] transition-colors',
                        'border-b border-[#D0D5DD]',
                        'shadow-[1px_0_0_0_#D0D5DD]'
                      );

                      return (
                        <tbody key={locGroup.id} ref={registerGroupBody(locGroup.id)}>
                          {/* LEVEL 1: LOCATION GROUP HEADER ROW */}
                          <tr
                            className="group bg-[#EEF2F6] hover:bg-[#E2E8F0] transition-colors cursor-pointer select-none"
                            onClick={() => toggleLocation(locGroup.id)}
                          >
                            <td
                              className={`${pin.labelClass} px-4 py-2.5 border-r border-b border-[#D0D5DD] whitespace-nowrap`}
                              style={pin.style}
                            >
                              <div className="flex flex-col gap-1.5">
                                <div className="flex items-center gap-2 shrink-0">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleLocation(locGroup.id);
                                    }}
                                    className="p-1 rounded hover:bg-[#CBD5E1] text-[#475467] transition-colors"
                                    title={isLocCollapsed ? 'Expand Location' : 'Collapse Location'}
                                  >
                                    {isLocCollapsed ? (
                                      <ChevronRight className="w-4 h-4" />
                                    ) : (
                                      <ChevronDown className="w-4 h-4" />
                                    )}
                                  </button>
                                  {locGroup.location_code && (
                                    <span className="px-1.5 py-0.5 rounded-[5px] bg-[#E2E8F0] text-[11px] font-mono font-semibold text-[#334155] shrink-0">
                                      {locGroup.location_code}
                                    </span>
                                  )}
                                  <span className="text-[15px] font-bold text-[#0F172A]">{locGroup.location_name}</span>
                                  <span className="font-normal text-[13px] text-[#64748B]">
                                    {locGroup.classes.length} classes
                                  </span>
                                </div>

                                <OtbSummaryPills
                                  planSales={locGroup.plan_sales}
                                  planInflow={locGroup.plan_inflow}
                                  onOrder={locGroup.plan_on_order}
                                  otb={locGroup.otb_dollars}
                                  priceBasis={priceBasis}
                                  className="pl-6"
                                />
                              </div>
                            </td>
                            {renderMetricCells(locSubtotal, 'current', overallTotals, true, pin.cellClass, pin.style)}
                          </tr>

                          {/* LEVEL 2: CLASSES UNDER LOCATION (Rendered only if location is expanded) */}
                          {!isLocCollapsed &&
                            locGroup.classes.map((classGroup) => {
                              const isClassCollapsed = !!collapsedLocationClasses[classGroup.id];
                              const classSubtotal = sumVendorMetrics(classGroup.vendors);


                              return (
                                <React.Fragment key={classGroup.id}>
                                  {/* Class Sub-Header Row */}
                                  <tr
                                    className="group bg-[#F8F9FA] hover:bg-[#F1F3F5] transition-colors cursor-pointer select-none"
                                    onClick={() => toggleLocationClass(classGroup.id)}
                                  >
                                    <td
                                      className="sticky left-0 z-10 px-4 py-2 bg-[#F8F9FA] group-hover:bg-[#F1F3F5] transition-colors border-r border-b border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0] pl-8 whitespace-nowrap"
                                    >
                                      <div className="flex flex-col gap-1.5">
                                        <div className="flex items-center gap-2 shrink-0">
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleLocationClass(classGroup.id);
                                            }}
                                            className="p-1 rounded hover:bg-[#E4E4E7] text-[#667085] transition-colors"
                                            title={isClassCollapsed ? 'Expand Class' : 'Collapse Class'}
                                          >
                                            {isClassCollapsed ? (
                                              <ChevronRight className="w-3.5 h-3.5" />
                                            ) : (
                                              <ChevronDown className="w-3.5 h-3.5" />
                                            )}
                                          </button>
                                          {classGroup.m1_class_code && (
                                            <span className="px-1.5 py-0.5 rounded-[5px] bg-[#F2F4F7] text-[11px] font-mono font-semibold text-[#475467] shrink-0">
                                              {classGroup.m1_class_code}
                                            </span>
                                          )}
                                          <span className="text-[13px] font-bold text-[#101828]">{classGroup.m1_class_name}</span>
                                          <span className="font-normal text-[12px] text-[#667085]">
                                            {classGroup.vendors.length} vendors
                                          </span>
                                        </div>

                                        <OtbSummaryPills
                                          planSales={classGroup.plan_sales}
                                          planInflow={classGroup.plan_inflow}
                                          onOrder={classGroup.plan_on_order}
                                          otb={classGroup.otb_dollars}
                                          priceBasis={priceBasis}
                                          className="pl-[22px]"
                                        />
                                      </div>
                                    </td>
                                    {renderMetricCells(classSubtotal, 'current', parentGroupTotals.get(locGroup.id), true)}
                                  </tr>

                                  {/* LEVEL 3: VENDOR DATA ROWS (Rendered only if class is expanded) */}
                                  {!isClassCollapsed &&
                                    classGroup.vendors.map((vendorItem) => {

                                      return (
                                        <React.Fragment key={vendorItem.id}>
                                          {/* VENDOR ROW 1: THIS PERIOD */}
                                          <tr className="group hover:bg-[#F4F4F5] transition-colors bg-white">
                                            <td className="sticky left-0 bg-white group-hover:bg-[#F4F4F5] z-10 px-4 py-3 border-r border-b border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0] pl-14">
                                              <span className="text-[#101828] text-[13px] font-semibold">{vendorItem.vendor}</span>
                                            </td>

                                            {renderMetricCells(vendorItem, 'current', parentGroupTotals.get(classGroup.id))}
                                          </tr>

                                          {/* YOY SUB-ROWS IF YOY IS ON */}
                                          {showYOY && (
                                            <>
                                              {/* ROW 2: LAST YEAR */}
                                              <tr className="group hover:bg-[#F4F4F5] transition-colors bg-white">
                                                <td className="sticky left-0 bg-white group-hover:bg-[#F4F4F5] z-10 px-4 py-2 border-r border-b border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0] pl-14">
                                                  <span className={subRowLabelCss}>LAST YEAR</span>
                                                </td>
                                                {renderMetricCells(vendorItem, 'ly', parentGroupTotals.get(classGroup.id))}
                                              </tr>

                                              {/* ROW 3: CHANGE */}
                                              <tr className="group hover:bg-[#F4F4F5] transition-colors bg-white">
                                                <td className="sticky left-0 bg-white group-hover:bg-[#F4F4F5] z-10 px-4 py-2 border-r border-b border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0] pl-14">
                                                  <span className={subRowLabelCss}>CHANGE</span>
                                                </td>
                                                {renderMetricCells(vendorItem, 'change', parentGroupTotals.get(classGroup.id))}
                                              </tr>
                                            </>
                                          )}
                                        </React.Fragment>
                                      );
                                    })}
                                </React.Fragment>
                              );
                            })}
                        </tbody>
                      );
                    })
                  ) : activeView === 'category_by_vendor' ? (
                    /* CATEGORY BY VENDOR VIEW */
                    filteredData.map((vendorGroup) => {
                      const vendorSubtotal = sumVendorMetrics(vendorGroup.categories);
                      const pin = pinnedGroupRow(vendorGroup.id, 'bg-[#F8F9FA]');
                      return (
                        <tbody key={vendorGroup.id} ref={registerGroupBody(vendorGroup.id)}>
                          {/* Group Header Row for Vendor */}
                          <tr className="bg-[#F8F9FA]">
                            <td
                              className={`${pin.labelClass} px-4 py-2.5 font-bold text-[#101828] text-[14px] border-r border-b border-[#E4E4E7] whitespace-nowrap`}
                              style={pin.style}
                            >
                              <div className="flex items-center gap-2">
                                <span>{vendorGroup.vendor}</span>
                                <span className="font-normal text-[12px] text-[#667085]">
                                  {vendorGroup.categories.length} categories
                                </span>
                              </div>
                            </td>
                            {renderMetricCells(vendorSubtotal, 'current', overallTotals, true, pin.cellClass, pin.style)}
                          </tr>

                          {/* Category Rows (with YOY support if active) */}
                          {vendorGroup.categories.map((cat) => {

                            return (
                              <React.Fragment key={cat.id}>
                                {/* CATEGORY ROW 1: THIS PERIOD */}
                                <tr className="group hover:bg-[#F4F4F5] transition-colors bg-white">
                                  <td className="sticky left-0 bg-white group-hover:bg-[#F4F4F5] z-10 px-4 py-3 border-r border-b border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0] pl-8">
                                    <span className="text-[#344054] text-[13px] font-semibold">{cat.category}</span>
                                  </td>
                                  {renderMetricCells(cat, 'current', parentGroupTotals.get(vendorGroup.id))}
                                </tr>

                                {/* CATEGORY YOY SUB-ROWS IF YOY IS ON */}
                                {showYOY && (
                                  <>
                                    {/* CATEGORY ROW 2: LAST YEAR */}
                                    <tr className="group hover:bg-[#F4F4F5] transition-colors bg-white">
                                      <td className="sticky left-0 bg-white group-hover:bg-[#F4F4F5] z-10 px-4 py-2 border-r border-b border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0]">
                                        <span className={subRowLabelCss}>LAST YEAR</span>
                                      </td>
                                      {renderMetricCells(cat, 'ly', parentGroupTotals.get(vendorGroup.id))}
                                    </tr>

                                    {/* CATEGORY ROW 3: CHANGE */}
                                    <tr className="group hover:bg-[#F4F4F5] transition-colors bg-white">
                                      <td className="sticky left-0 bg-white group-hover:bg-[#F4F4F5] z-10 px-4 py-2 border-r border-b border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0]">
                                        <span className={subRowLabelCss}>CHANGE</span>
                                      </td>
                                      {renderMetricCells(cat, 'change', parentGroupTotals.get(vendorGroup.id))}
                                    </tr>
                                  </>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      );
                    })
                  ) : activeView === 'vendor_by_class' ? (
                    /* VENDOR BY CLASS VIEW */
                    filteredData.map((vendorGroup) => {
                      const vendorSubtotal = sumVendorMetrics(vendorGroup.classes);
                      const pin = pinnedGroupRow(vendorGroup.id, 'bg-[#F8F9FA]');
                      return (
                        <tbody key={vendorGroup.id} ref={registerGroupBody(vendorGroup.id)}>
                          {/* Group Header Row for Vendor */}
                          <tr className="bg-[#F8F9FA]">
                            <td
                              className={`${pin.labelClass} px-4 py-2.5 font-bold text-[#101828] text-[14px] border-r border-b border-[#E4E4E7] whitespace-nowrap`}
                              style={pin.style}
                            >
                              <div className="flex items-center gap-2">
                                <span>{vendorGroup.vendor}</span>
                                <span className="font-normal text-[12px] text-[#667085]">
                                  {vendorGroup.classes.length} classes
                                </span>
                              </div>
                            </td>
                            {renderMetricCells(vendorSubtotal, 'current', overallTotals, true, pin.cellClass, pin.style)}
                          </tr>

                          {/* Class Rows (with YOY support if active) */}
                          {vendorGroup.classes.map((cls) => {

                            return (
                              <React.Fragment key={cls.id}>
                                {/* CLASS ROW 1: THIS PERIOD */}
                                <tr className="group hover:bg-[#F4F4F5] transition-colors bg-white">
                                  <td className="sticky left-0 bg-white group-hover:bg-[#F4F4F5] z-10 px-4 py-3 border-r border-b border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0] pl-8">
                                    <span className="text-[#344054] text-[13px] font-semibold flex items-center gap-2">
                                      {cls.m1_class_code && (
                                        <span className="px-1.5 py-0.5 rounded-[5px] bg-[#F2F4F7] text-[11px] font-mono font-semibold text-[#475467] shrink-0">
                                          {cls.m1_class_code}
                                        </span>
                                      )}
                                      <span>{cls.m1_class_name}</span>
                                    </span>
                                  </td>
                                  {renderMetricCells(cls, 'current', parentGroupTotals.get(vendorGroup.id))}
                                </tr>

                                {/* CLASS YOY SUB-ROWS IF YOY IS ON */}
                                {showYOY && (
                                  <>
                                    {/* CLASS ROW 2: LAST YEAR */}
                                    <tr className="group hover:bg-[#F4F4F5] transition-colors bg-white">
                                      <td className="sticky left-0 bg-white group-hover:bg-[#F4F4F5] z-10 px-4 py-2 border-r border-b border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0]">
                                        <span className={subRowLabelCss}>LAST YEAR</span>
                                      </td>
                                      {renderMetricCells(cls, 'ly', parentGroupTotals.get(vendorGroup.id))}
                                    </tr>

                                    {/* CLASS ROW 3: CHANGE */}
                                    <tr className="group hover:bg-[#F4F4F5] transition-colors bg-white">
                                      <td className="sticky left-0 bg-white group-hover:bg-[#F4F4F5] z-10 px-4 py-2 border-r border-b border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0]">
                                        <span className={subRowLabelCss}>CHANGE</span>
                                      </td>
                                      {renderMetricCells(cls, 'change', parentGroupTotals.get(vendorGroup.id))}
                                    </tr>
                                  </>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      );
                    })
                  ) : (
                    /* TOTAL BY VENDOR VIEW */
                    <tbody>
                      {/* Top Summary Row for Total by Vendor */}
                      {apiTotals?.total_otb_retail != null && (
                        <tr className="bg-[#F8F9FA]" data-testid="vendor-performance-total-store-plan-row">
                          <td
                            className={`sticky left-0 ${STICKY_LAYER.pinnedRowFirstColumn} px-4 py-2.5 font-bold text-[#101828] text-[13px] bg-[#F8F9FA] border-r border-b border-[#E4E4E7] ${headShadow || FIRST_COL_SHADOW} whitespace-nowrap`}
                            style={stickyTop}
                          >
                            <span className="font-bold text-[#6D28D9]">Total Store Plan & OTB</span>
                          </td>
                          <td
                            colSpan={100}
                            className={`sticky ${STICKY_LAYER.pinnedRowCell} px-4 py-2.5 bg-[#F8F9FA] ${ROW_DIVIDER} ${headShadow}`}
                            style={stickyTop}
                          >
                            <OtbSummaryPills
                              layout="inline"
                              otbLabel="Total OTB"
                              priceBasis={priceBasis}
                              planSales={priceBasis === 'cost' ? apiTotals.total_plan_sales_cost : apiTotals.total_plan_sales_retail}
                              planInflow={priceBasis === 'cost' ? apiTotals.total_plan_inflow_cost : apiTotals.total_plan_inflow_retail}
                              onOrder={priceBasis === 'cost' ? apiTotals.total_on_order_cost : apiTotals.total_on_order_retail}
                              otb={priceBasis === 'cost' ? apiTotals.total_otb_cost : apiTotals.total_otb_retail}
                            />
                          </td>
                        </tr>
                      )}

                      {filteredData.map((row, idx) => {
                        const isEvenGroup = idx % 2 === 1;
                        const bgGroupClass = isEvenGroup ? 'bg-[#F7F8FA]' : 'bg-white';


                        return (
                          <React.Fragment key={row.id}>
                            {/* ROW 1: THIS PERIOD / MAIN VENDOR ROW */}
                            <tr className={`transition-colors group ${bgGroupClass} hover:bg-[#F4F4F5]`}>
                              {/* Sticky Vendor Column */}
                              <td className={`sticky left-0 z-10 px-4 py-3 border-r border-b border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0] ${bgGroupClass} group-hover:bg-[#F4F4F5]`}>
                                {/* No expand/collapse affordance here -- Total by Vendor is a flat,
                              one-row-per-vendor list with no nested breakdown to reveal. */}
                                <div className="flex items-center gap-2">
                                  <span className={vendorNameCss}>{row.vendor}</span>
                                  {row.has_alert && (
                                    <Flag className="w-3.5 h-3.5 text-[#F97215]" />
                                  )}
                                </div>
                              </td>

                              {renderMetricCells(row, 'current')}
                            </tr>

                            {/* YOY SUB-ROWS IF YOY IS ON */}
                            {showYOY && (
                              <>
                                {/* ROW 2: LAST YEAR */}
                                <tr className={`transition-colors group ${bgGroupClass} hover:bg-[#F4F4F5]`}>
                                  <td className={`sticky left-0 z-10 px-4 py-2 border-r border-b border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0] ${bgGroupClass} group-hover:bg-[#F4F4F5]`}>
                                    <span className={subRowLabelCss}>LAST YEAR</span>
                                  </td>
                                  {renderMetricCells(row, 'ly')}
                                </tr>

                                {/* ROW 3: CHANGE */}
                                <tr className={`transition-colors group ${bgGroupClass} hover:bg-[#F4F4F5]`}>
                                  <td className={`sticky left-0 z-10 px-4 py-2 border-r border-b border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0] ${bgGroupClass} group-hover:bg-[#F4F4F5]`}>
                                    <span className={subRowLabelCss}>CHANGE</span>
                                  </td>
                                  {renderMetricCells(row, 'change')}
                                </tr>
                              </>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  )}
                </>
              )}

            {apiRows.length > 0 && (
              <tfoot className={`sticky bottom-0 ${STICKY_LAYER.footer}`}>
                {/* ROW 1: TOTAL / THIS PERIOD */}
                <tr className="bg-[#F8F9FA] font-bold" data-testid="vendor-performance-total-row">
                  <td
                    className={`sticky left-0 ${STICKY_LAYER.footerFirstColumn} px-4 py-3 bg-[#F8F9FA] border-r border-t-2 border-[#E4E4E7] border-t-[#D0D5DD] ${footShadow || FIRST_COL_SHADOW} whitespace-nowrap text-[13px] font-bold text-[#101828]`}
                  >
                    Total
                  </td>
                  {renderMetricCells(subtotalMetricRow, 'current', undefined, undefined, footerCellClass(true))}
                </tr>

                {/* YOY SUB-ROWS IF YOY IS ON */}
                {showYOY && (
                  <>
                    {/* ROW 2: LAST YEAR */}
                    <tr className="bg-[#F8F9FA] font-medium">
                      <td className={`sticky left-0 ${STICKY_LAYER.footerFirstColumn} px-4 py-2 bg-[#F8F9FA] border-r border-t border-[#E4E4E7] ${FIRST_COL_SHADOW} whitespace-nowrap`}>
                        <span className={subRowLabelCss}>LAST YEAR</span>
                      </td>
                      {renderMetricCells(subtotalMetricRow, 'ly', undefined, undefined, footerCellClass(false))}
                    </tr>

                    {/* ROW 3: CHANGE */}
                    <tr className="bg-[#F8F9FA] font-bold">
                      <td className={`sticky left-0 ${STICKY_LAYER.footerFirstColumn} px-4 py-2 bg-[#F8F9FA] border-r border-t border-[#E4E4E7] ${FIRST_COL_SHADOW} whitespace-nowrap`}>
                        <span className={subRowLabelCss}>CHANGE</span>
                      </td>
                      {renderMetricCells(subtotalMetricRow, 'change', undefined, undefined, footerCellClass(false))}
                    </tr>
                  </>
                )}
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {activeView !== 'total_by_vendor' && (
        <p className="text-[12px] text-[#71717A] leading-[18px] -mt-2 px-1">
          Group rows show a real subtotal in every column. Percentage columns compare each row with its
          parent group ({GROUP_PARENT_RELATIONS[activeView]}). Margins, markdown and sell-through on
          group rows are calculated from the underlying totals, not averaged.
        </p>
      )}

      {/* 6. Bottom Information Note */}
      <div className="flex items-start gap-2 text-xs text-[#71717A] bg-white p-3.5 border border-[#E4E4E7] rounded-xl shadow-2xs mt-1">
        <Info className="w-4 h-4 text-[#7C3AED] shrink-0 mt-0.5" />
        <span>
          With YOY on, each vendor (and each category, when expanded) shows 3 rows: This Period, Last Year, and Change. Gross Margin % (after markdowns) is always shown as a pill — green (50%+), amber (40–50%), red (below 40%).
        </span>
      </div>

      {/* Save View Modal */}
      {/*
        Rendered via a portal straight into document.body, not inline here. `position: fixed` only
        centers on the true browser viewport when there is no ancestor between it and <body> that
        sets a `transform`/`filter`/`perspective`/`will-change: transform` (any of those creates a
        new containing block, and a fixed child then centers on THAT ancestor's box instead of the
        viewport -- indistinguishable from "the modal is centered on the page/content instead of
        the screen" once the ancestor is taller than the viewport). Bumping z-index alone (still
        needed, and kept below) fixed the modal rendering behind the MUI AppBar/Drawer, but did not
        fully fix the centering complaint, so this closes the remaining gap the safe, standard way:
        a portal makes the modal a direct child of <body>, where no such ancestor can exist, so
        centering is correct unconditionally -- regardless of scroll position, screen size, or
        whatever wraps this component today or in the future.
      */}
      {isSaveModalOpen && typeof document !== 'undefined' && createPortal(
        /* z-[10000]: the MUI app shell's fixed AppBar/Drawer sit at z-index ~1100-1300, well
           above Tailwind's default z-50 -- without a higher z-index here this modal renders
           behind the header, so only the portion not covered by it is visible, making it look
           like it opened lower on the page instead of centered on the true viewport. Matches the
           z-[10000]/z-[9999] convention BaseReport.component.tsx/ReportModal.component.tsx
           already use for the same reason. */
        <div
          data-testid="save-view-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsSaveModalOpen(false);
          }}
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-[#101828]/50 backdrop-blur-[2px] animate-in fade-in duration-150"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="save-view-title"
            className="bg-white rounded-2xl p-6 w-[520px] max-w-[calc(100vw-32px)] shadow-2xl border border-slate-100 flex flex-col gap-3 animate-in zoom-in-95 duration-150"
          >
            {/* Header with Title & Close Icon */}
            <div className="flex items-center justify-between">
              <h3 id="save-view-title" className="text-[18px] font-bold text-[#101828]">Save this view</h3>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setIsSaveModalOpen(false)}
                className="text-[#98A2B3] hover:text-[#101828] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Description Subtitle */}
            <p className="text-[14px] text-[#667085] leading-relaxed font-normal">
              Saves your current filters, grouping, sort, and visible columns to your account.
            </p>

            {/* View Name Input */}
            <div className="flex flex-col gap-2">
              <label htmlFor="save-view-name" className="text-[14px] font-medium text-[#344054]">View name</label>
              <input
                id="save-view-name"
                type="text"
                placeholder="e.g. Vendors below margin threshold"
                value={saveViewName}
                maxLength={SAVED_VIEW_NAME_MAX_LENGTH}
                onChange={(e) => setSaveViewName(e.target.value)}
                className="h-11 w-full border border-[#D0D5DD] rounded-[10px] px-3.5 text-[14px] font-medium text-[#101828] placeholder-[#98A2B3] focus:outline-none focus:ring-2 focus:ring-[#7C3AED] shadow-2xs"
              />
            </div>

            {/* Visibility Segmented Pills */}
            <div className="flex flex-col gap-2">
              <span className="text-[14px] font-medium text-[#344054]">Visibility</span>
              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  aria-pressed={saveVisibility === 'only_me'}
                  onClick={() => setSaveVisibility('only_me')}
                  className={`flex-1 h-11 rounded-[10px] text-[14px] font-semibold flex items-center justify-center gap-2 transition-colors ${saveVisibility === 'only_me'
                    ? 'bg-[#7C3AED] text-white shadow-2xs'
                    : 'bg-white border border-[#D0D5DD] text-[#344054] hover:bg-[#F9FAFB]'
                    }`}
                >
                  <Lock className="w-4 h-4" /> Only me
                </button>
                <button
                  type="button"
                  aria-pressed={saveVisibility === 'shared'}
                  onClick={() => setSaveVisibility('shared')}
                  className={`flex-1 h-11 rounded-[10px] text-[14px] font-semibold flex items-center justify-center gap-2 transition-colors ${saveVisibility === 'shared'
                    ? 'bg-[#7C3AED] text-white shadow-2xs'
                    : 'bg-white border border-[#D0D5DD] text-[#344054] hover:bg-[#F9FAFB]'
                    }`}
                >
                  <Users className="w-4 h-4" /> Shared
                </button>
              </div>
              {saveVisibility === 'shared' && (
                <p className="text-[12px] text-[#667085]">
                  Everyone with access to this client can see and apply this view. Only you can delete it.
                </p>
              )}
            </div>

            {/* Footer Action Buttons */}
            <div className="flex items-center justify-end gap-3 mt-1">
              <button
                type="button"
                onClick={() => setIsSaveModalOpen(false)}
                className="h-10 px-4 bg-white border border-[#D0D5DD] rounded-[10px] text-[14px] font-semibold text-[#344054] hover:bg-[#F9FAFB] shadow-2xs transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveView}
                disabled={isSavingView}
                className="h-10 px-4 bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-[14px] font-semibold rounded-[10px] shadow-2xs transition-colors flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Check className="w-4 h-4" /> Save view
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default VendorPerformanceReport;